import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from "react";
import { trialWindowOpen, trialEndLabel } from "@shared/pro-pricing";
import { MEMBER_WORKSHOP_PERCENT_OFF } from "@shared/member-discount";
import { PLUS_NAME } from "@shared/ack";
import { useQuery } from "@tanstack/react-query";
import { useRoute, useLocation, Link } from "wouter";
import { getEventRedirect } from "@shared/event-redirects";
import { useDrawnButtons } from "@/lib/ds-drawn-buttons";
import {
  SalesArtifactGallery,
} from "@/components/sections/sales/sales-sections";
import { DsIcon } from "@/components/sections/ds-icons";
import { DS_COWORK_SERIES } from "@shared/premium-css-routes";
import { ensurePremiumCssForSlug } from "@/lib/premium-css";
import { KIDS_COURSE_WORKLOAD, KIDS_COURSE_SCHEDULE } from "@shared/kids-camp-meta";
import { motion, useScroll, useTransform } from "framer-motion";
import { Navbar } from "@/components/navbar";
import { getKidsDetail, preloadKidsDetail } from "@/lib/kids-detail-loader";
import { ReminderPopup, type ReminderPopupSession } from "@/components/ui/reminder-popup";
import { resolveReminderTarget } from "@shared/reminder-target";
import { useExitIntent } from "@/hooks/use-exit-intent";
import { AdventureLogo } from "@/components/adventure-logo";
import { DotMatrixAccent } from "@/components/primitives";
import { usePageMeta, breadcrumbSchema, faqSchema, WORKSHOP_SERIES_SEO } from "@/lib/seo";
import { toWebpUrl } from "@/lib/utils";
import {
  Check, X,
  ChevronLeft, ChevronRight, ArrowLeft,
  Globe2, FileSpreadsheet, Mail, BarChart3,
  Bot, Presentation, Users,
  Calculator,
} from "lucide-react";
import {
  WorkshopTicketBlock,
  RevealSection,
  SiteEyebrow,
  formatDate,
  formatTime,
  formatPrice,
  type CouponStatus,
} from "@/components/sections/workshop-ticket-block";
import { Button } from "@/components/ui/button";
import Marquee from "@/components/ui/marquee";
import { WorkshopHero } from "@/components/sections/workshop-hero";
import { CoworkHero } from "@/components/sections/cowork-hero";
import { CoworkBuildAccordion, type CoworkBuildItem } from "@/components/sections/cowork-build-accordion";
import { ClaudeFlowDemo } from "@/components/sections/beginners/claude-flow-demo";
import { BeginnersFlow } from "@/components/sections/beginners/beginners-flow";
import { SampleWorkSection } from "@/components/sections/ai-video-workshop/SampleWorkSection";
import { DaveInstructorSection } from "@/components/sections/ai-video-workshop/DaveInstructorSection";
import { StickyBuildScroller } from "@/components/sections/ai-video-workshop/StickyBuildScroller";
import {
  AttendeesReceive,
  type AttendeesReceiveVariant,
  type AttendeesArt,
  type ReceiveItem,
} from "@/components/sections/attendees-receive";
import { AskAiAboutUs } from "@/components/sections/ask-ai-about-us";
import { SalesChatSection } from "@/components/sections/sales/sales-chat"; // parked 2026-09-17, see beforeFinalCta on the Sales config
void SalesChatSection;
import { DrawnArrow } from "@/components/ui/drawn-arrow";
import {
  OnPromptStudioValueStack,
  OnPromptStudioSection,
} from "@/components/sections/onprompt/onprompt-sections";
import { PersonaCarousel, COWORK_PERSONAS, type Persona } from "@/components/sections/persona-carousel";
import { TestimonialsMarquee } from "@/components/sections/design-workshop/TestimonialsMarquee";
// Design-only proof sections — injected after the build accordion on the
// Claude for Design page via PremiumLayoutConfig.extraAfterBuild.
import { SamplesShowcaseSection } from "@/components/sections/design-workshop/SamplesShowcaseSection";
import { BeforeAfterSection } from "@/components/sections/design-workshop/BeforeAfterSection";
import { getScarcityConfig } from "@/lib/scarcity-config";
import { requestCheckout } from "@/lib/open-checkout";
import { useSeatClocks } from "@/components/scarcity/useSeatClock";
import { deriveTimelineTiles } from "@/components/scarcity/deriveTimelineTiles";
import { HairlineTimeline, type HairlineStop } from "@/components/scarcity/HairlineTimeline";
import { WavyMark } from "@/components/ui/wavy-mark";
import { useClients } from "@/hooks/use-clients";
import type { EventWithSeries, SeriesSessionSummary } from "@shared/schema";
import {
  type WorkshopReview,
  WORKSHOP_REVIEWS,
  getPublishedWorkshopReviews,
  REVIEWS_ENABLED_SERIES,
  REVIEWS_ENABLED_SLUGS,
  buildReviewsJsonLd,
} from "@shared/workshop-reviews";
import { getWorkshopContent, type WorkshopAgendaItem } from "@shared/workshop-content";

const DARK_BG = "var(--charcoal)";

const NAV_HEIGHT = 56;
const NAV_HEIGHT_SM = 72;

function EventHeader() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-cream border-b border-charcoal/10">
      <div className="container-content flex items-center justify-between h-[56px] sm:h-[72px]">
        <a href="https://www.adventureppc.com" className="scale-[0.8] sm:scale-100 origin-left block">
          <AdventureLogo />
        </a>
        <a href="https://adventureppc.com" target="_blank" rel="noopener noreferrer" className="h-[34px] sm:h-[44px] px-3 sm:px-6 bg-indigo text-white rounded-[4px] font-mono text-[10px] sm:text-[13px] uppercase tracking-wider flex items-center hover:bg-indigo/90 transition-colors whitespace-nowrap" data-testid="button-work-with-adventure">
          Work with AdVenture
        </a>
      </div>
    </header>
  );
}

// formatDate / formatTime / formatPrice and the RevealSection scroll-reveal
// wrapper now live in client/src/components/sections/workshop-ticket-block.tsx
// (shared with the design workshop page) and are imported above.

/** A single label/value fact in the sticky bar's info cluster. Mono label
 *  stacked over a cream value — scannable, two text levels only (cream full +
 *  cream-muted), no inline color. */
/** Desktop sticky reserve bar — appears once the hero scrolls away. Charcoal
 *  ground for premium, high-contrast read. Left: workshop name. Middle: the
 *  conversion-critical facts (date + extra dates, format, price, seat urgency)
 *  derived upstream so they mirror the ticketing panel exactly. Right: a
 *  var-driven terra CTA that scrolls to #tickets. Desktop only — mobile uses
 *  the persistent bottom reserve bar in workshop-ticket-block.tsx. */
function JumpNav({
  heroRef,
  dateLabel,
  moreDates = 0,
  priceLabel,
  priceFrom = false,
  seatsLabel,
  formatLabel,
  soldOut = false,
  variant = "facts",
  title,
  nextDateLong,
}: {
  /** The hero block this bar replaces. Observed HERE rather than by the page. */
  heroRef: React.RefObject<HTMLDivElement | null>;
  dateLabel?: string;
  moreDates?: number;
  priceLabel?: string | null;
  priceFrom?: boolean;
  seatsLabel?: string | null;
  formatLabel?: string;
  /** No open (non-sold-out) upcoming session — render a "Sold out" label
   *  instead of date/price facts. */
  soldOut?: boolean;
  /** Left-hand treatment. "facts" is what every page has always rendered;
   *  "name-date" is the workshop title plus a date badge (Sales). */
  variant?: "facts" | "name-date";
  /** "name-date" only: the event's own title, e.g. "The Claude for Sales Teams
   *  Workshop". Read off the event row, never written here, so a retitled
   *  workshop cannot leave a stale name riding the scroll. */
  title?: string;
  /** "name-date" only: the next open date as "September 15" — long month, no
   *  year, formatted in that session's own timezone. Same `nextSession` the
   *  facts line derives from, so the two can never disagree. */
  nextDateLong?: string;
}) {
  // Jump-nav "Reserve your seat": OPENS THE CHECKOUT drawer/sheet — the
  // on-page ticket section was sunset 2026-07-02. When nothing is bookable
  // (sold out) requestCheckout() returns false and the click falls through
  // to the #tickets anchor, which lands on the rendered sold-out section.
  const openCheckoutFromJumpnav = (e: React.MouseEvent) => {
    if (requestCheckout()) {
      e.preventDefault();
      return;
    }
    // Fall through: native anchor navigation scrolls to #tickets.
  };

  /* 2026-09-09 — what was wrong here:
     1. `visible` was page-level state in EventDetailPage, flipped by a hero
        IntersectionObserver, so every hero crossing re-rendered the whole
        4,300-line page component.
     2. AnimatePresence unmounted the bar on the way out. The drawn CTA outline
        keeps its "already drawn" memory on the node, so a remounted bar redrew
        its frame from scratch on every reappearance.
     The bar stays mounted now and CSS toggles it off `data-visible` (see
     `.ds-jumpnav[data-visible]` at the foot of cowork-ds.css); the observer
     lives here, so nothing above this component re-renders on a crossing. */
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0 },
    );
    io.observe(hero);
    return () => io.disconnect();
  }, [heroRef]);

  return (
    <div
      data-visible={visible ? "true" : "false"}
      data-variant={variant}
      aria-hidden={!visible}
      className="ds-jumpnav fixed top-[56px] sm:top-[72px] left-0 right-0 z-40 bg-charcoal border-b border-cream/12 hidden sm:block"
    >
      <div className="container-content">
        <div className="flex items-center justify-between gap-6 py-3">
          {/* NAME + DATE (Sales): the workshop's own title, then the next open
              date in a badge. Nothing else — the price lives in the drawer the
              CTA opens, and the format is the whole page's subject by now. When
              nothing is bookable the badge says so rather than naming a date
              the checkout will refuse. */}
          {variant === "name-date" ? (
            <div className="flex items-baseline gap-3 min-w-0">
              {title && <span className="jn-title truncate">{title}</span>}
              {soldOut ? (
                <span className="jn-badge">Sold out</span>
              ) : (
                nextDateLong && <span className="jn-badge">{nextDateLong}</span>
              )}
            </div>
          ) : (
            /* Facts — one pipe-separated uppercase line. When every date is
               sold out, the facts would advertise a date/price the drawer
               won't sell — show a plain "Sold out" label instead. */
            <div className="flex items-center gap-2.5 shrink-0 font-mono text-[12px] uppercase tracking-wide text-cream/85 whitespace-nowrap">
              {soldOut ? (
                <span style={{ color: "var(--terra-lt)" }}>Sold out</span>
              ) : (
                <>
                  {dateLabel && (
                    <span>
                      {dateLabel}
                      {moreDates > 0 && <span className="text-cream/55"> +{moreDates} more</span>}
                    </span>
                  )}
                  {formatLabel && (
                    <>
                      <span className="text-cream/25" aria-hidden="true">|</span>
                      <span>{formatLabel}</span>
                    </>
                  )}
                  {priceLabel && (
                    <>
                      <span className="text-cream/25" aria-hidden="true">|</span>
                      <span>{priceFrom ? `From ${priceLabel}` : priceLabel}</span>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Seat urgency (handwritten) + arrow, then the CTA */}
          <div className="flex items-center gap-3 shrink-0">
            {!soldOut && seatsLabel && (
              <span
                className="flex items-center gap-1 whitespace-nowrap"
                style={{ fontFamily: "var(--font-hand)", fontWeight: 700, color: "var(--terra-lt)", fontSize: 25, lineHeight: 1 }}
              >
                {seatsLabel}
                <svg width="34" height="16" viewBox="0 0 34 16" fill="none" aria-hidden="true">
                  <path d="M2 8 Q 17 5 29 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M23 4 L 30 9 L 23 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
            {/* One interactive element, not an <a> wrapping a <button>:
                a button inside a link is invalid HTML (interactive content
                inside an <a>), gives keyboard users two stops for one control,
                and leaves assistive tech to guess which role wins. The drawn
                treatment selects on .btn-cta, not on the tag, so the ink is
                unchanged. Same shape as the hero CTA above. */}
            <a
              href="#tickets"
              onClick={openCheckoutFromJumpnav}
              className="btn btn-cta btn-lg shrink-0"
              data-ds-draw-key="jumpnav"
              data-testid="jumpnav-reserve"
            >
              Reserve your seat
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ title, dark = false, id }: { title: React.ReactNode; dark?: boolean; id?: string }) {
  return (
    <div className="mb-8 max-w-[var(--max-w-heading)]">
      {/* `id` is what a surrounding <section aria-labelledby> points at, so the
          section takes its name from the heading a sighted reader sees rather
          than from a duplicated aria-label that can drift from the copy. */}
      <h2 id={id} className={`type-h2 ${dark ? "text-cream" : "text-charcoal"}`}>{title}</h2>
    </div>
  );
}


/**
 * A page section that carries an accessible name.
 *
 * `RevealSection` (workshop-ticket-block.tsx) is now a plain passthrough to
 * `<section id style className>`, and its prop list has no way to name the
 * element. An unnamed <section> is a generic box: it does not appear in a
 * screen reader's landmark list, so this page's eight major blocks were one
 * undifferentiated run of content with no way to jump between them. Naming
 * each one from the <h2> a sighted reader already sees turns them into
 * regions, and keeps the name from drifting away from the visible copy the
 * way a hand-written aria-label does.
 *
 * Output is otherwise byte-identical to RevealSection's, so the structural
 * selectors in cowork-ds.css (`#faq > div > div:last-child > div > button`
 * and friends) still match.
 */
function LabelledSection({
  id,
  labelledBy,
  className = "",
  style,
  children,
}: {
  id?: string;
  labelledBy: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={labelledBy} style={style} className={className}>
      {children}
    </section>
  );
}


function TrainingEyebrow({ label }: { label?: string }) {
  /* Shared by all four premium pages, so the default stays put — Design is the
     only page that overrides it (premium.learnEyebrow). */
  if (label === "") return null;
  return <SiteEyebrow label={label ?? "Live, hands-on training"} />;
}

function ReviewsEyebrow() {
  return <SiteEyebrow label="Workshop reviews" />;
}

/** Pulsing green dot + date-count eyebrow — cowork hero only. Copy adapts to
 *  how many sessions are actually open so it never overclaims. */
function CoworkPulsingEyebrow({ openCount = 2 }: { openCount?: number }) {
  const label =
    openCount >= 2 ? "TWO NEW DATES JUST ADDED"
    : openCount === 1 ? "FINAL DATE THIS MONTH — SEATS OPEN"
    : "NEW DATES COMING SOON";
  return (
    <div className="flex items-center justify-center gap-2.5">
      <span
        className="w-2.5 h-2.5 rounded-full animate-pulse flex-shrink-0"
        style={{ backgroundColor: "var(--forest)" }}
        aria-hidden="true"
      />
      <span
        className="type-eyebrow text-charcoal"
        style={{ marginBottom: 0 }}
      >
        {label}
      </span>
    </div>
  );
}

function SectionDivider() {
  return (
    <div
      aria-hidden="true"
      style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
    >
      <div className="h-px bg-charcoal" />
    </div>
  );
}

function DoubleBorderLine({ dark = false, className = "" }: { dark?: boolean; className?: string }) {
  const color = dark ? "bg-cream/20" : "bg-charcoal/8";
  return (
    <div className={`flex flex-col gap-[3px] ${className}`} aria-hidden="true">
      <div className={`h-px ${color}`} />
      <div className={`h-px ${color}`} />
    </div>
  );
}

function EdgeMark({ dark = false, position = "top-left" }: { dark?: boolean; position?: string }) {
  const color = dark ? "border-cream/30" : "border-charcoal/10";
  const posMap: Record<string, string> = {
    "top-left": "top-2 left-2", "top-right": "top-2 right-2",
    "bottom-left": "bottom-2 left-2", "bottom-right": "bottom-2 right-2",
  };
  return (
    <div className={`absolute ${posMap[position] || posMap["top-left"]} w-3 h-3 pointer-events-none`} aria-hidden="true">
      <div className={`absolute ${position?.includes("top") ? "top-0" : "bottom-0"} ${position?.includes("left") ? "left-0" : "right-0"} w-full h-px border-t ${color}`} />
      <div className={`absolute ${position?.includes("top") ? "top-0" : "bottom-0"} ${position?.includes("left") ? "left-0" : "right-0"} h-full w-px border-l ${color}`} />
    </div>
  );
}

function LinkedInIcon({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );
}

function GridIcon({ variant, className = "" }: { variant: number; className?: string }) {
  const color = "hsla(60, 3%, 8%, 0.15)";
  const accent = "hsla(246, 42%, 40%, 0.6)";
  const icons = [
    <svg viewBox="0 0 48 48" className={className}><rect x="4" y="4" width="16" height="16" stroke={color} strokeWidth="1.5" fill="none" rx="2" /><rect x="28" y="4" width="16" height="16" stroke={color} strokeWidth="1.5" fill="none" rx="2" /><rect x="4" y="28" width="16" height="16" stroke={color} strokeWidth="1.5" fill="none" rx="2" /><rect x="28" y="28" width="16" height="16" stroke={accent} strokeWidth="1.5" fill="none" rx="2" /><line x1="12" y1="24" x2="12" y2="28" stroke={color} strokeWidth="1" /><line x1="36" y1="24" x2="36" y2="28" stroke={accent} strokeWidth="1" /></svg>,
    <svg viewBox="0 0 48 48" className={className}><circle cx="24" cy="24" r="18" stroke={color} strokeWidth="1.5" fill="none" /><circle cx="24" cy="24" r="10" stroke={accent} strokeWidth="1.5" fill="none" /><circle cx="24" cy="24" r="3" fill={accent} /></svg>,
    <svg viewBox="0 0 48 48" className={className}><path d="M4 38 L24 6 L44 38 Z" stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round" /><path d="M14 32 L24 14 L34 32 Z" stroke={accent} strokeWidth="1.5" fill="none" strokeLinejoin="round" /></svg>,
    <svg viewBox="0 0 48 48" className={className}><rect x="6" y="6" width="36" height="36" stroke={color} strokeWidth="1.5" fill="none" rx="2" /><line x1="6" y1="18" x2="42" y2="18" stroke={color} strokeWidth="1" /><line x1="6" y1="30" x2="42" y2="30" stroke={color} strokeWidth="1" /><line x1="18" y1="6" x2="18" y2="42" stroke={color} strokeWidth="1" /><line x1="30" y1="6" x2="30" y2="42" stroke={color} strokeWidth="1" /><rect x="18" y="18" width="12" height="12" fill={accent} opacity="0.15" /><circle cx="24" cy="24" r="2" fill={accent} /></svg>,
    <svg viewBox="0 0 48 48" className={className}><path d="M24 4 L24 44" stroke={color} strokeWidth="1.5" /><path d="M4 24 L44 24" stroke={color} strokeWidth="1.5" /><circle cx="24" cy="12" r="4" stroke={accent} strokeWidth="1.5" fill="none" /><circle cx="24" cy="36" r="4" stroke={accent} strokeWidth="1.5" fill="none" /></svg>,
    <svg viewBox="0 0 48 48" className={className}><rect x="4" y="12" width="40" height="24" stroke={color} strokeWidth="1.5" fill="none" rx="2" /><line x1="4" y1="20" x2="44" y2="20" stroke={color} strokeWidth="1" /><rect x="8" y="24" width="14" height="8" stroke={accent} strokeWidth="1" fill="none" rx="1" /></svg>,
  ];
  return icons[variant % icons.length] || icons[0];
}

function BentoIcon({ variant, className = "w-10 h-10" }: { variant: number; className?: string }) {
  const c = "hsla(246, 42%, 40%, 0.8)";
  const d = "hsla(60, 3%, 8%, 0.35)";
  const icons = [
    <svg viewBox="0 0 32 32" className={className}><rect x="3" y="6" width="26" height="20" rx="2" stroke={d} strokeWidth="1.8" fill="none"/><path d="M3 10h26" stroke={d} strokeWidth="1.5"/><path d="M8 15h6M8 18h4" stroke={c} strokeWidth="1.8" strokeLinecap="round"/><rect x="18" y="13" width="8" height="8" rx="1" stroke={c} strokeWidth="1.5" fill="none"/><path d="M19 17l2 2 4-4" stroke={c} strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" className={className}><circle cx="16" cy="12" r="5" stroke={c} strokeWidth="1.8" fill="none"/><path d="M16 17v3M12 22h8" stroke={d} strokeWidth="1.8" strokeLinecap="round"/><rect x="6" y="22" width="20" height="6" rx="2" stroke={d} strokeWidth="1.8" fill="none"/><circle cx="16" cy="12" r="2" fill={c}/></svg>,
    <svg viewBox="0 0 32 32" className={className}><rect x="4" y="4" width="24" height="24" rx="2" stroke={d} strokeWidth="1.8" fill="none"/><rect x="4" y="4" width="24" height="8" rx="2" stroke={c} strokeWidth="1.8" fill="none"/><path d="M8 16h16M8 20h12M8 24h8" stroke={d} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" className={className}><circle cx="16" cy="16" r="12" stroke={d} strokeWidth="1.8" fill="none"/><path d="M10 20l4-8 4 5 4-9" stroke={c} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 22h16" stroke={d} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" className={className}><circle cx="16" cy="10" r="4" stroke={c} strokeWidth="1.8" fill="none"/><path d="M10 26V18a6 6 0 0112 0v8" stroke={d} strokeWidth="1.8" fill="none"/><path d="M13 22h6" stroke={c} strokeWidth="1.5" strokeLinecap="round"/><circle cx="16" cy="10" r="1.5" fill={c}/></svg>,
    <svg viewBox="0 0 32 32" className={className}><rect x="4" y="3" width="24" height="26" rx="2" stroke={d} strokeWidth="1.8" fill="none"/><path d="M4 9h24" stroke={d} strokeWidth="1.5"/><path d="M8 14h10M8 18h14M8 22h8" stroke={c} strokeWidth="1.5" strokeLinecap="round"/><rect x="20" y="20" width="6" height="6" rx="1" stroke={c} strokeWidth="1.5" fill="none"/></svg>,
    <svg viewBox="0 0 32 32" className={className}><rect x="4" y="8" width="10" height="8" rx="1.5" stroke={d} strokeWidth="1.8" fill="none"/><rect x="16" y="8" width="10" height="8" rx="1.5" stroke={d} strokeWidth="1.8" fill="none"/><rect x="4" y="18" width="10" height="8" rx="1.5" stroke={c} strokeWidth="1.8" fill="none"/><rect x="16" y="18" width="10" height="8" rx="1.5" stroke={d} strokeWidth="1.8" fill="none"/><circle cx="9" cy="12" r="1.5" fill={c}/><circle cx="21" cy="22" r="1.5" fill={c}/></svg>,
    <svg viewBox="0 0 32 32" className={className}><rect x="4" y="6" width="24" height="20" rx="2" stroke={d} strokeWidth="1.8" fill="none"/><path d="M4 12h24" stroke={d} strokeWidth="1.5"/><circle cx="10" cy="9" r="2" fill={c}/><circle cx="15" cy="9" r="2" fill={d}/><path d="M8 16h6M8 19h10M8 22h4" stroke={c} strokeWidth="1.5" strokeLinecap="round"/><rect x="20" y="15" width="6" height="8" rx="1" stroke={d} strokeWidth="1.5" fill="none"/></svg>,
    <svg viewBox="0 0 32 32" className={className}><rect x="4" y="6" width="24" height="20" rx="2" stroke={d} strokeWidth="1.8" fill="none"/><path d="M9 22V16M14 22V12M19 22V18M24 22V10" stroke={c} strokeWidth="2.5" strokeLinecap="round"/><path d="M9 16l5-4 5 6 5-8" stroke={d} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  ];
  return icons[variant % icons.length] || icons[0];
}

// Client logos fallback now loaded from DB via useFeaturedClientLogos()

// Workshop reviews + their show/hide rules moved to client/src/lib/workshop-reviews.ts
// (REVIEWS_ENABLED_SERIES / REVIEWS_ENABLED_SLUGS / getPublishedWorkshopReviews).

/* A scroll-linked art ground for the final CTA. Not an animation: the canvas
   tracks the scroll position directly, which is why it carries no easing. */
function FinalCtaArt({ imageBase, credit }: { imageBase: string; credit?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  /* ── PERF: this canvas is the heaviest asset on the page and it sits at the
     very BOTTOM of it, so fetching it on load is ~700KB spent on something
     nobody has scrolled to — measured on the Sales page, 47% of all image bytes
     on desktop and 52% on mobile, competing with the hero, which is the LCP
     element. A CSS background on a mounted element fetches immediately and there
     is no `loading="lazy"` for backgrounds, so the urls are withheld until the
     band is near the viewport. Same shape attendees-receive.tsx uses for video.

     1200px of rootMargin, not that file's 600: this is a full-bleed painting
     rather than a muted loop, so it must be there BEFORE the band arrives.

     FALLBACK IS EAGER. No IntersectionObserver means the art loads at once,
     exactly as it did before — a missing observer must not mean a missing
     painting. */
  const [artOn, setArtOn] = useState(false);
  useEffect(() => {
    const band = ref.current?.parentElement;
    if (!band) return;
    if (!("IntersectionObserver" in window)) { setArtOn(true); return; }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setArtOn(true); io.disconnect(); } },
      { rootMargin: "1200px 0px" },
    );
    io.observe(band);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current;
    const band = el?.parentElement;
    if (!el || !band) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      /* 2026-09-09: a hidden tab starves requestAnimationFrame, so a scroll
         that arrives while hidden set `ticking` with no callback to clear it —
         every later scroll then returned early and the parallax stayed frozen.
         Nothing to draw while hidden anyway: bail without arming the flag. */
      if (document.hidden) { ticking = false; return; }
      ticking = true;
      requestAnimationFrame(() => {
        const r = band.getBoundingClientRect();
        const p = Math.max(0, Math.min(1, (window.innerHeight - r.top) / (window.innerHeight + r.height)));
        el.style.transform = `translate3d(0, ${((p - 0.5) * 12).toFixed(2)}%, 0)`;
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <>
      {/* WHICH file is main's answer and it is the right one: both paths go out
          as custom properties and the STYLESHEET picks, because `image-set(...
          2x)` handed the 2400px master (811KB) to any phone above 1x density —
          a density descriptor cannot see the viewport. WHEN is the deferral
          above. The two compose: no urls at all until the band is near, then the
          media query in cowork-ds.css chooses between them. */}
      <div
        ref={ref}
        className="ds-cta-art"
        aria-hidden="true"
        style={
          artOn
            ? ({
                "--cta-art-1x": `url(${imageBase}-1200.webp)`,
                "--cta-art-2x": `url(${imageBase}-2400.webp)`,
              } as React.CSSProperties)
            : undefined
        }
      />
      <div className="ds-cta-veil" aria-hidden="true" />
      <p className="ds-cta-credit">
        {credit ?? (
          <>
            <strong>Claude</strong> Monet · Irises · c. 1914–17
          </>
        )}
      </p>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   The closing wall — paintings in gold frames.

   The last section is the one place on this page that gets to be playful, so
   the Monet stops being a blurred ground and becomes what it is: pictures,
   hung, in frames. Each one tilts a little off true the way a hung picture
   does, and lifts toward the reader on hover — the ONE place movement is
   allowed, because a picture on a wall is a thing you lean in to look at.

   PAINTINGS is deliberately a list: the brief is three or four, and the repo
   currently ships one. Adding the rest is a line each once the images are in
   client/public/images/ — no other change.

   The parallax reads the section's own scroll progress rather than the
   window's, so the frames drift against the wall instead of against the page. */
const PAINTINGS: { src: string; title: string; tilt: number; depth: number }[] = [
  { src: "/images/monet-irises-hero-1200.webp", title: "Irises · c. 1914–17", tilt: -2.1, depth: 0.5 },
];

/* Not currently mounted — see the closing section below. Kept intact so
   re-hanging it is one line, and so PAINTINGS stays the place to add the
   other three once those images exist. */
function ClosingWall() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const p = Math.max(0, Math.min(1, (window.innerHeight - r.top) / (window.innerHeight + r.height)));
        el.style.setProperty("--wall-p", ((p - 0.5) * 2).toFixed(3));
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div className="ds-wall" ref={ref} aria-hidden="true">
      {PAINTINGS.map((p) => (
        <figure
          key={p.src}
          className="ds-frame"
          style={{ "--tilt": `${p.tilt}deg`, "--depth": p.depth } as React.CSSProperties}
        >
          <span className="ds-frame-mat">
            <img src={p.src} alt="" loading="lazy" decoding="async" />
          </span>
          <figcaption>{p.title}</figcaption>
        </figure>
      ))}
    </div>
  );
}

// Final-CTA copy override, keyed by seriesSlug. The default below is the
// Cowork workshop's punchline. Workshops with a different audience (eg. the
// Fundamentals of AI mastermind, aimed at AI champions and team leaders)
// override here so the CTA reflects the right promise.
const DEFAULT_FINAL_CTA = {
  headline: "Go from helpless beginner to Claude Cowork power user in just 3.5 hours.",
  subtitle: "Or you could open another YouTube tab and end the morning where you started. Your call.",
};
const FINAL_CTA_BY_SERIES: Record<string, { headline: ReactNode; subtitle: string }> = {
  "ai-security-workshop": {
    headline: "Find out what your AI knows about you before someone else does.",
    subtitle: "Two hours, live, in plain language. You leave with your settings fixed and a kit for every tool you use.",
  },
  "the-ai-first-leader-workshop": {
    headline: "Leave with a plan to rebuild your business around AI.",
    subtitle: "Three hours: the frameworks, live demos of real working systems, and a 90-day roadmap you can start running the next morning.",
  },
  "claude-code-for-beginners-workshop": {
    // Mixed weight, rule 14: two steps apart, the heavier naming the subject.
    headline: (
      <>
        Go from “what the hell is this all about” to{" "}
        <strong style={{ fontWeight: 500 }}>Claude Code power user</strong> in a single day.
      </>
    ),
    subtitle: "",
  },
  "claude-for-beginners-workshop": {
    headline: "Go from helpless beginner to Claude power user in just 3 hours.",
    subtitle: "",
  },

  "claude-for-sales-teams-workshop": {
    headline: (
      <>
        Make Claude part of your sales team with{" "}
        <strong style={{ fontWeight: 500 }}>a proactive AI system that helps move every deal forward</strong>.
      </>
    ),
    subtitle: "",
  },
  "advanced-claude-code-workshop": {
    headline: "Become the advanced Claude Code user on your team.",
    subtitle: "Or stay the one finding out from customers what Claude shipped broken. Your call.",
  },
  // Carried over from DesignClosingCta when the video bookend retired. Without
  // an entry here the page fell through to DEFAULT_FINAL_CTA and closed a
  // design workshop on Cowork's promise.
  "claude-for-design-workshop": {
    // Plain text, no <strong> and nothing struck through. The three verbs carry
    // their own emphasis; the reskin's `#final-cta h2 strong` weight bump is
    // there if one of them should later take it.
    headline: "Design it. Build it. Ship it.",
    subtitle: "From first idea to a finished, live experience in one morning.",
  },
  "ai-video-workshop-for-beginners": {
    headline: (
      <>
        {/* Dave's words, 2026-10-27. */}
        Make a first draft of{" "}
        <strong style={{ fontWeight: 500 }}>your own commercial or short film</strong> in four hours.
      </>
    ),
    subtitle: "With a Webby-nominated director showing you what works, what breaks, and how to fix it.",
  },
};

// The per-series "Go VIP" addon config (PREMIUM_ADDON_CONFIG_BY_SERIES)
// moved to client/src/components/sections/workshop-ticket-block.tsx with the
// extracted ticket block. The server-side equivalent stays in
// server/routes.ts — both sides MUST stay in sync on priceCents.

// Series that opt out of the animated reviews header. Renders the static
// counterpart (ReviewsStaticHeader) instead.
const STATIC_REVIEWS_HEADER_SERIES = new Set<string>([
  "the-ai-first-leader-workshop",
  "advanced-claude-code-workshop",
]);

// Matched on event.seriesSlug (like REVIEWS_ENABLED_SERIES) so it renders on
// every dated session of these series, not a single hardcoded slug.
const SHOW_COMPARISON_FOR_SERIES = new Set([
  "claude-cowork-for-beginners-workshop",
  "claude-code-for-beginners-workshop",
  /* Sales runs it too (Esther, 2026-09-01: "you are missing the youtube section
     on this page"). The objection it answers — you could learn this off YouTube
     for nothing — lands harder here than on either of the others, because this
     page is asking $799 of someone who could go and watch a prospecting video
     tonight. */
  "claude-for-sales-teams-workshop",
  /* Esther, 2026-09-29: the Design page was missing it too. The objection is
     the same one every page answers — you could learn this off YouTube for
     nothing — and it lands here because the work a designer makes free-hand
     usually looks it. */
  "claude-for-design-workshop",
  /* Claude for Beginners runs its own version (afterComparison): nearly every
     3-hour Claude workshop advertised on Meta is free, so the objection here is
     "why pay?" rather than "why not YouTube?". */
  "claude-for-beginners-workshop",
]);

const MOBILE_LOGOS: Array<{ name: string; src: string }> = [
  { name: "AMC Networks", src: "/images/client-logos/amc-networks-480.webp" },
  { name: "Forbes", src: "/images/client-logos/forbes-2.webp" },
  { name: "Kars4Kids", src: "/images/client-logos/kars4kids.webp" },
  { name: "Michael Aram", src: "/images/client-logos/michael-aram.webp" },
  { name: "Asurion", src: "/images/client-logos/asurion-800.webp" },
  { name: "Nasdaq", src: "/images/client-logos/nasdaq-480.webp" },
];

// `cutout: true` means the photo already has a transparent background, so it
// renders with normal blending (no `darken` trick) and no organic vignette —
// both of those are only needed to melt a cream-baked rectangle into the cream
// section, and would wash out / fade the edges of a real alpha cutout.
const ABOUT_HEADSHOTS: Record<string, { photo: string; position?: string; scale?: string; cutout?: boolean; srcSet?: string; sizes?: string }> = {
  "Isaac Rudansky": { photo: "/images/instructors/isaac-clay-racket-cream.webp" },
  "Patrick Gilbert": { photo: "/images/instructors/patrick-clay-books-cream.webp" },
  "Esti Nadoff": { photo: "/images/instructors/esti-instructor-dress-cutout-2.webp", cutout: true },
  // Design calls her Esther Nadoff; Cowork still says Esti. Both keys point at
  // the same plates until the two series agree on one name.
  "Esther Nadoff": { photo: "/images/instructors/esti-instructor-dress-cutout-2.webp", cutout: true },
  "Danny Tawil": { photo: "/assets/headshots/headshot_danny_tawil.webp", position: "center calc(15% + 20px)" },
  "Nechama Teigman": { photo: "/images/instructors/nechama.webp" },
  "Dave Spector": { photo: "/assets/headshots/headshot_dave_spector.webp" },
};


const LEARN_SUBHEADERS: Record<string, string> = {
  "Launch autonomous Cowork agents": "Hand Claude a full task and let it plan, execute, and deliver on its own.",
  "Schedule recurring automations": "Set tasks to run on a cadence so reports and audits happen without you.",
  "Generate professional documents": "Turn plain-English prompts into polished decks, sheets, and reports.",
  "Connect 38+ workplace tools": "Wire Claude into Slack, Drive, Notion, GitHub, and the rest of your stack.",
  "Install and use plugins": "Add ready-made bundles that give Claude skills tuned for your exact role.",
  "Organize work with Projects": "Persistent workspaces where Claude remembers your context and files.",
  "Assign tasks from your phone": "Send work to Claude from iOS or Android and pick it up on desktop later.",
  "Understand when to use each Claude mode": "Learn when to reach for Cowork, Chat, or Code on any given task.",
};

type CompareVal = "check" | "x" | "maybe";
const COMPARISON_ROWS: Array<{ label: string; us: CompareVal; youtube: CompareVal; self: CompareVal }> = [
  { label: "Live hands-on instruction", us: "check", youtube: "x", self: "x" },
  { label: "Answers to your specific questions", us: "check", youtube: "x", self: "x" },
  { label: "Ship a working automation same day", us: "check", youtube: "maybe", self: "maybe" },
  { label: "Recording + searchable transcript", us: "check", youtube: "check", self: "x" },
  { label: "Two weeks of email support after", us: "check", youtube: "x", self: "x" },
  { label: "Template + plugin starter pack", us: "check", youtube: "x", self: "x" },
  { label: "Accountability to actually finish", us: "check", youtube: "x", self: "x" },
];

/* Sales gets its own rows. Two reasons, and the first is not optional:
   the shared set's last row reads "Template + plugin starter pack", and
   "template" is the one word Ronnie's brief bars from this page — every output
   is written fresh, live, in the seller's own voice. Second, the generic rows
   are pitched at someone automating their workday; a seller weighing $799
   against a free prospecting video wants to see THEIR objections answered, so
   the rows name deals, pipeline and follow-ups.
   The shape is deliberately unchanged: same seven rows, same verdicts, so the
   table reads identically to the ones on the Cowork and Code pages. */
const COMPARISON_ROWS_SALES: typeof COMPARISON_ROWS = [
  { label: "Live hands-on instruction", us: "check", youtube: "x", self: "x" },
  { label: "Answers about your actual deals", us: "check", youtube: "x", self: "x" },
  { label: "Run it on your own pipeline same day", us: "check", youtube: "maybe", self: "maybe" },
  { label: "Recording + searchable transcript", us: "check", youtube: "check", self: "x" },
  { label: "Two weeks of email support after", us: "check", youtube: "x", self: "x" },
  { label: "Your sales profile and prompt library", us: "check", youtube: "x", self: "x" },
  { label: "Accountability to actually finish", us: "check", youtube: "x", self: "x" },
];

/* Design gets its own rows for the same reason Sales does: the shared set is
   pitched at someone automating a workday, and a designer weighing the ticket
   against a free YouTube tutorial wants THEIR objections answered — taste,
   consistency, and whether the thing they make is actually shippable.
   Same seven rows, same verdicts, so the table reads identically across pages. */
const COMPARISON_ROWS_DESIGN: typeof COMPARISON_ROWS = [
  { label: "Live hands-on instruction", us: "check", youtube: "x", self: "x" },
  { label: "Feedback on your own work", us: "check", youtube: "x", self: "x" },
  { label: "Ship a real page the same day", us: "check", youtube: "maybe", self: "maybe" },
  { label: "Recording + searchable transcript", us: "check", youtube: "check", self: "x" },
  { label: "Two weeks of email support after", us: "check", youtube: "x", self: "x" },
  { label: "A reusable Skill in your own voice", us: "check", youtube: "x", self: "x" },
  { label: "Accountability to actually finish", us: "check", youtube: "x", self: "x" },
];

const COMPARISON_ROWS_BY_SERIES: Record<string, typeof COMPARISON_ROWS> = {
  "claude-for-sales-teams-workshop": COMPARISON_ROWS_SALES,
  "claude-for-design-workshop": COMPARISON_ROWS_DESIGN,
};

// Workshop reviews live in client/src/lib/workshop-reviews.ts. Per-series
// override or per-review filtering happens via the `series` field on each
// review record. See REVIEWS_ENABLED_SERIES / REVIEWS_ENABLED_SLUGS for the
// gating sets that decide whether the bento renders on a given workshop.

// Google-review accent colors. Kept literal because the visual reference is
// intentionally Google-style (blue reviewer name, gold stars, gray meta).
// Site fonts (Copernicus / Inter / JetBrains Mono) are used everywhere else.
const G_BLUE = "#1a73e8";
const G_STAR = "#e8a117";
const G_TEXT = "#202124";
const G_MUTED = "#5f6368";
const G_BORDER = "#dadce0";

/** AdVenture brand mark only — four squares, no wordmark. */
function AdVentureMark({ size = 30 }: { size?: number }) {
  const cell = size / 2;
  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      aria-label="AdVenture Media"
      role="img"
    >
      <div className="absolute" style={{ top: 0, left: 0, width: cell, height: cell, backgroundColor: "#5832D1" }} />
      <div className="absolute" style={{ top: 0, right: 0, width: cell, height: cell, backgroundColor: "#E0433E" }} />
      <div className="absolute" style={{ bottom: 0, left: 0, width: cell, height: cell, backgroundColor: "#000000" }} />
    </div>
  );
}

function ReviewStars() {
  // role="img": a bare span is a generic element, and generic elements are not
  // allowed to carry an accessible name. The five SVGs are decorative, so the
  // group itself is the image.
  return (
    <span className="inline-flex" style={{ gap: 1 }} role="img" aria-label="5 out of 5 stars">
      {Array.from({ length: 5 }).map((_, k) => (
        <svg key={k} width={17} height={17} viewBox="0 0 24 24" fill={G_STAR} aria-hidden="true">
          <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      ))}
    </span>
  );
}

function ReviewCard({ review, index, noReveal = false }: { review: WorkshopReview; index: number; noReveal?: boolean }) {
  // On mobile the carousel scrolls horizontally; the per-card scroll-reveal
  // fade-in fights that motion and makes swiping feel janky, so it's disabled
  // there via noReveal — the card renders as a plain article.
  if (noReveal) {
    return (
      <article
        className="bg-white flex flex-col h-full"
        style={{ padding: "32px 36px", color: G_TEXT, gap: 20 }}
      >
        {reviewCardBody(review)}
      </article>
    );
  }
  return (
    <motion.article
      className="bg-white flex flex-col h-full"
      style={{ padding: "32px 36px", color: G_TEXT, gap: 20 }}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15, margin: "0px 0px -8% 0px" }}
      transition={{
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1],
        delay: Math.min(index * 0.08, 0.32),
      }}
    >
      {reviewCardBody(review)}
    </motion.article>
  );
}

function reviewCardBody(review: WorkshopReview) {
  return (
    <>
      <header className="flex items-center" style={{ gap: 14 }}>
        <div
          className="rounded-full flex items-center justify-center shrink-0 text-white"
          style={{
            width: 48,
            height: 48,
            backgroundColor: review.avatarBg,
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            fontSize: 16,
          }}
          aria-hidden="true"
        >
          {review.initials}
        </div>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span
            style={{
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: 17,
              lineHeight: 1.3,
              color: G_BLUE,
            }}
          >
            {review.name}
          </span>
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 14, lineHeight: 1.3, color: G_MUTED }}>
            {review.stats}
          </span>
        </div>
      </header>

      <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
        <ReviewStars />
        <span style={{ fontFamily: "var(--font-sans)", fontSize: 14, color: G_MUTED }}>{review.posted}</span>
        {review.isNew && (
          <span
            style={{
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: 12,
              letterSpacing: "0.3px",
              padding: "2px 7px",
              border: `1px solid ${G_BORDER}`,
              borderRadius: 2,
              color: G_TEXT,
              marginLeft: 4,
            }}
          >
            NEW
          </span>
        )}
      </div>

      <p
        className="line-clamp-[12]"
        style={{
          margin: 0,
          fontFamily: "var(--font-sans)",
          fontSize: 15,
          lineHeight: 1.65,
          color: G_TEXT,
          textWrap: "pretty",
        }}
      >
        {review.text}
      </p>

    </>
  );
}

// Random-char ticker for the eyebrow label. Cycles glyphs every ~30ms then
// settles to the final character with a per-char cascading delay (matches
// the GSAP version in the design source). Initial state is non-breaking
// spaces so the layout reserves the full eyebrow width before the ticker
// fires — prevents a layout shift on first paint.
function TickerEyebrow({ text, start }: { text: string; start: boolean }) {
  const finalChars = text.toUpperCase().split("");
  const [chars, setChars] = useState<string[]>(() => finalChars.map(() => " "));
  const POOL = "#@%&*+=$ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const TICK_MS = 30;
  const PER_CHAR_DELAY = 45;
  const FIRST_CHAR_DELAY = 220;

  useEffect(() => {
    if (!start) return;
    const startedAt = performance.now();
    let raf = 0;
    let lastTick = 0;
    const settled = new Array(finalChars.length).fill(false);

    function loop(now: number) {
      const elapsed = now - startedAt;
      const next = finalChars.map((finalChar, i) => {
        if (finalChar === " ") return " ";
        const settleAt = FIRST_CHAR_DELAY + i * PER_CHAR_DELAY;
        if (elapsed >= settleAt) {
          settled[i] = true;
          return finalChar;
        }
        if (now - lastTick >= TICK_MS) {
          return POOL[Math.floor(Math.random() * POOL.length)];
        }
        return chars[i] || POOL[Math.floor(Math.random() * POOL.length)];
      });
      if (now - lastTick >= TICK_MS) lastTick = now;
      setChars(next);
      if (settled.every(Boolean)) return;
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  return (
    <span
      className="type-eyebrow"
      style={{
        marginBottom: 0,
        color: "var(--terra)",
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {chars.map((c, i) => (
        <span key={i} style={{ display: "inline-block" }}>
          {c === " " ? " " : c}
        </span>
      ))}
    </span>
  );
}

/**
 * Static counterpart to ReviewsAnimatedHeader. Same visual layout (eyebrow,
 * line, asterisk, headline with terra underline on "building") but no
 * Framer Motion. Used for series whose authors want the section to stop
 * replaying animations every time the React tree remounts (HMR in dev,
 * route changes in prod). The dev HMR retrigger is the bigger issue.
 */
function ReviewsStaticHeader() {
  return (
    <div className="mb-16 md:mb-20 max-w-[var(--max-w-heading)]">
      <div className="flex items-center mb-10" style={{ gap: 16 }}>
        <span
          className="type-eyebrow"
          style={{
            marginBottom: 0,
            color: "var(--terra)",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          Workshop reviews
        </span>
        <div
          className="h-px flex-1"
          style={{ backgroundColor: "var(--terra)", opacity: 0.5, maxWidth: 200 }}
          aria-hidden="true"
        />
        <span style={{ color: "var(--terra)", display: "inline-flex" }} aria-hidden="true">
          <WavyMark size={16} />
        </span>
      </div>
      <h2
        className="serif-section text-charcoal"
        style={{
          fontWeight: 500,
          fontSize: "clamp(34px, 5vw, 68px)",
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
          margin: 0,
          textWrap: "balance" as "balance",
        }}
      >
        Over 100,000 attendees and a whole lotta love
      </h2>
    </div>
  );
}

function ReviewsAnimatedHeader({ headline, eyebrow = "Workshop reviews" }: { headline?: ReactNode; eyebrow?: string } = {}) {
  const HEADLINE = "Over 100,000 attendees and a whole lotta love";

  // Trigger the eyebrow ticker only once the header scrolls in.
  const [tickerStart, setTickerStart] = useState(false);

  return (
    <div className="mb-16 md:mb-20 max-w-[var(--max-w-heading)]">
      {/* Eyebrow row: animated label + line + asterisk */}
      <motion.div
        className="flex items-center mb-10"
        style={{ gap: 16 }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        onViewportEnter={() => setTickerStart(true)}
        variants={{
          hidden: {},
          visible: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
        }}
      >
        <motion.span
          variants={{
            hidden: { opacity: 0, y: 6 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
          }}
        >
          <TickerEyebrow text={eyebrow} start={tickerStart} />
        </motion.span>
        <motion.div
          className="h-px flex-1"
          style={{ backgroundColor: "var(--terra)", opacity: 0.5, maxWidth: 200, transformOrigin: "0% 50%" }}
          variants={{
            hidden: { scaleX: 0 },
            visible: { scaleX: 1, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.55 } },
          }}
          aria-hidden="true"
        />
        <motion.span
          style={{ color: "var(--terra)", display: "inline-flex", transformOrigin: "50% 50%" }}
          variants={{
            hidden: { opacity: 0, rotate: -360, scale: 0.4 },
            visible: { opacity: 1, rotate: 900, scale: 1, transition: { duration: 1.6, ease: [0.4, 0, 0.2, 1] } },
          }}
          aria-hidden="true"
        >
          <WavyMark size={16} />
        </motion.span>
      </motion.div>

      {/* Headline: single clean fade-up */}
      <motion.h2
        className="serif-section text-charcoal"
        style={{
          fontWeight: 500,
          fontSize: "clamp(34px, 5vw, 68px)",
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
          margin: 0,
          textWrap: "balance",
        }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
      >
        {headline ?? HEADLINE}
      </motion.h2>
    </div>
  );
}

function WorkshopReviewsBento({ reviews }: { reviews: WorkshopReview[] }) {
  // The bento targets a 5-card layout. With fewer, render the available cards
  // in the same hierarchy (feature, then row pairs) and skip empty slots.
  const r = reviews;
  if (r.length === 0) return null;
  return (
    <div className="flex flex-col" style={{ gap: 28 }}>
      {/* Row 1: full-width feature card */}
      {r[0] && <ReviewCard review={r[0]} index={0} />}

      {/* Row 2: left-heavy bento (1.45fr / 1fr on desktop, stacked on mobile) */}
      {(r[1] || r[2]) && (
        <div
          className="grid grid-cols-1 md:[grid-template-columns:1.45fr_1fr] items-stretch"
          style={{ gap: 28 }}
        >
          {r[1] && <ReviewCard review={r[1]} index={1} />}
          {r[2] && <ReviewCard review={r[2]} index={2} />}
        </div>
      )}

      {/* Row 3: right-heavy bento (1fr / 1.45fr on desktop, stacked on mobile) */}
      {(r[3] || r[4]) && (
        <div
          className="grid grid-cols-1 md:[grid-template-columns:1fr_1.45fr] items-stretch"
          style={{ gap: 28 }}
        >
          {r[3] && <ReviewCard review={r[3]} index={3} />}
          {r[4] && <ReviewCard review={r[4]} index={4} />}
        </div>
      )}
    </div>
  );
}

/** Mobile-only horizontal swipe carousel for reviews. Desktop: hidden (sm:hidden).
 *  Native CSS scroll-snap (lighter than Embla, smoother native touch, avoids the
 *  iOS scroll-snap + JS-transform flicker bug). Affordances signaling swipeability:
 *  (1) a peek of the next card's edge, (2) thin arrows, (3) a one-shot "nudge" —
 *  the track eases left and settles back once when the slider scrolls into view. */
/** Reviews shorter than this don't render in the mobile carousel — one-liner
 *  quotes leave a tall dead band under the card (the track is as tall as its
 *  tallest card). Desktop layouts keep the full set. */
const MOBILE_REVIEW_MIN_CHARS = 140;

/* Portrait overrides for the .ds-cowork skin only. That page stands its
   instructors against the same painted Monet ground as its hero; every other
   workshop page keeps the cream-backed originals above. */
/* v2 portraits (2026-09-02): the new studio shots, matted off their white
   backdrop and composited onto a Monet — ONE PAINTING EACH, not three crops of
   the same canvas. All three are late, near-abstract works with no horizon and
   no architecture; anything with a skyline or a building fights the face at
   card size. Sources are the Art Institute's open-access files (public domain).
   New filenames rather than overwriting v1: Cloudflare caches by name, and a
   same-name binary swap needs a manual purge. */
const monetV2 = (slug: string) => ({
  photo: `/images/instructors/${slug}-monet-v2-672.webp`,
  /* The stem, so a page whose band renders these bigger than Cowork's 318px
     cards can rebuild the set at its own widths without a second map. */
  variantBase: `/images/instructors/${slug}-monet-v2`,
  /* 384 and 672, and nothing larger. The card is 318px on a 1x desktop and
     338px on a phone, so 672 is already 2x on the phone; the 800 this replaced
     was 44% heavier for detail invisible at that size (checked at full phone
     resolution, cropped to the face — indistinguishable). */
  srcSet: [384, 672].map((w) => `/images/instructors/${slug}-monet-v2-${w}.webp ${w}w`).join(", "),
  sizes: "(max-width: 767px) 86vw, 318px",
});

const ABOUT_HEADSHOTS_DSCOWORK: Record<
  string,
  { photo: string; variantBase?: string; srcSet?: string; sizes?: string }
> = {
  "Isaac Rudansky": monetV2("isaac"),
  "Patrick Gilbert": monetV2("patrick"),
  "Nechama Teigman": monetV2("nechama"),
  /* The Design series is Isaac + Esti, and without her here she was the one
     card on the band still standing on flat cream while Isaac stood on a
     painting. `esti-monet-v2` is the plate already live on the teams page; its
     -384 and -672 widths were generated for this, since that set shipped
     480/800/1200/1600 for a much larger card. */
  "Esti Nadoff": monetV2("esti"),
  "Esther Nadoff": monetV2("esti"),
};


/* main's local TestimonialArrow lived here and is gone: same geometry as
   @/components/ui/drawn-arrow on a 0 0 74 26 viewBox, differing only in rendered
   width (44 vs the shared default 46). Both branches wrote the same arrow in the
   same week; the shared module is the survivor and the call sites below pass
   width={44} so main's tuning for this room is kept exactly. */

function MobileReviewsCarousel({ reviews: allReviews, longArrows = false }: { reviews: WorkshopReview[]; longArrows?: boolean }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [nudged, setNudged] = useState(false);

  // Mobile-only cut: keep medium/long reviews. If a series has nothing but
  // short quotes, fall back to the full set rather than rendering nothing.
  const mediumOrLong = allReviews.filter((r) => r.text.length >= MOBILE_REVIEW_MIN_CHARS);
  const reviews = mediumOrLong.length > 0 ? mediumOrLong : allReviews;

  // Fire the swipe-affordance nudge exactly once, when the slider scrolls in.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNudged(true);
          io.disconnect();
        }
      },
      { threshold: 0.45 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const scrollByCard = (dir: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-review-card]");
    const step = card ? card.offsetWidth + 16 : el.clientWidth * 0.82;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (reviews.length === 0) return null;
  return (
    // Break out of the container's px-6 so the carousel spans full width and the
    // peek of the next card reads cleanly against the screen edge.
    <div className="-mx-6">
      <div className="flex justify-end gap-2 px-6 mb-4">
        <button
          type="button"
          onClick={() => scrollByCard(-1)}
          aria-label="Previous testimonial"
          className={
            longArrows
              ? "h-11 px-2 flex items-center justify-center text-charcoal/70 hover:text-charcoal transition-colors"
              : "w-[68px] h-11 rounded-full flex items-center justify-center border border-charcoal/20 text-charcoal/60 hover:text-charcoal hover:border-charcoal/40 transition-colors"
          }
        >
          {longArrows ? <DrawnArrow dir="prev" width={44} /> : <ChevronLeft className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={() => scrollByCard(1)}
          aria-label="Next testimonial"
          className={
            longArrows
              ? "h-11 px-2 flex items-center justify-center text-charcoal/70 hover:text-charcoal transition-colors"
              : "w-[68px] h-11 rounded-full flex items-center justify-center border border-charcoal/20 text-charcoal/60 hover:text-charcoal hover:border-charcoal/40 transition-colors"
          }
        >
          {longArrows ? <DrawnArrow dir="next" width={44} /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
      <div
        ref={scrollerRef}
        className="overflow-x-auto snap-x snap-mandatory scrollbar-hide scroll-pl-6"
      >
        {/* items-stretch keeps every card the same height (the tallest in the
            filtered set), per Isaac 2026-07-02. */}
        <div className={`flex items-stretch gap-4 px-6 ${nudged ? "review-swipe-nudge" : ""}`}>
          {reviews.map((review, i) => (
            <div key={i} data-review-card className="flex-none w-[80vw] snap-start">
              <ReviewCard review={review} index={i} noReveal />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// One representative sample image per group (`shot`), with the concrete
// examples as callouts underneath. `shot` = basename under
// /images/code-builds/showcase/ (responsive .webp once captured).
const THINGS_YOULL_BUILD = [
  { persona: "For your business", shot: "/images/code-builds/showcase/business", items: [
    { icon: Globe2, text: "A custom landing page for your product or service" },
    { icon: BarChart3, text: "An internal dashboard that tracks what matters" },
    { icon: Calculator, text: "A pricing calculator or ROI estimator" },
  ]},
  { persona: "For your workflow", shot: "/images/code-builds/showcase/workflow", items: [
    { icon: FileSpreadsheet, text: "An expense tracker that categorizes spending automatically" },
    { icon: Mail, text: "An email workflow that drafts responses for you" },
    { icon: Bot, text: "An AI-powered chatbot trained on your data" },
  ]},
  { persona: "For your team", shot: "/images/code-builds/showcase/team", items: [
    { icon: Presentation, text: "A client report builder that pulls and formats data" },
    { icon: Users, text: "A team onboarding portal with checklists" },
  ]},
];

/** Showcase screenshot for a "things you'll build" tile. Renders the real
 *  capture when present; falls back to a branded placeholder (self-healing via
 *  onError) so the layout previews before captures land. */
function BuildShot({ shot, label }: { shot: string; label: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className="relative w-full aspect-[16/10] bg-cream flex items-center justify-center"
        style={{ border: "1px dashed hsla(60, 3%, 8%, 0.28)" }}
      >
        <DotMatrixAccent position="center" size="md" className="opacity-[0.12]" />
        <span
          className="relative type-eyebrow uppercase text-charcoal/40"
          style={{ fontSize: "10px", letterSpacing: "1.6px" }}
        >
          Screenshot pending
        </span>
      </div>
    );
  }
  return (
    <img
      src={`${shot}.webp`}
      alt={label}
      className="w-full aspect-[16/10] object-cover object-top"
      style={{ border: "1px solid hsla(60, 3%, 8%, 0.18)" }}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

/** Viewport-width watcher for the pinned build scroll (no shared hook exists). */
function useIsNarrow(breakpoint = 1024) {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [breakpoint]);
  return narrow;
}

/** "Oh, the things you might build" as a pinned scroll-jack: the left column
 *  pins to the viewport while each group's sample image scrolls past on the
 *  right; an IntersectionObserver drives the active group (label + callouts +
 *  progress pips cross-fade in the sticky column). Mobile falls back to a
 *  stacked structural grid. Code design language throughout. */
function ThingsYouBuildScroll({ groups }: { groups: typeof THINGS_YOULL_BUILD }) {
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [active, setActive] = useState(0);
  const isNarrow = useIsNarrow();
  const count = groups.length;

  useEffect(() => {
    if (isNarrow) return; // no pin/track on mobile — stacked layout
    const observers: IntersectionObserver[] = [];
    panelRefs.current.forEach((el, i) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(i); },
        { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [count, isNarrow]);

  const Callouts = ({ group, large }: { group: (typeof groups)[number]; large?: boolean }) => (
    <div className={large ? "space-y-4" : "space-y-3"}>
      {group.items.map((item, k) => (
        <div key={k} className="flex items-start gap-3">
          <item.icon className={`${large ? "w-5 h-5" : "w-4 h-4"} mt-[3px] text-indigo/70 shrink-0`} strokeWidth={1.5} />
          <span className={`${large ? "type-body" : "text-[14px]"} leading-snug text-charcoal/80`}>{item.text}</span>
        </div>
      ))}
    </div>
  );

  // ── Mobile: stacked structural grid ──
  if (isNarrow) {
    return (
      <div className="overflow-hidden" style={{ border: "var(--border-structural)" }}>
        <div className="grid grid-cols-1 gap-px" style={{ backgroundColor: "hsla(60, 3%, 8%, 0.60)" }}>
          {groups.map((group, gi) => (
            <div key={gi} className="relative bg-cream p-6 flex flex-col">
              <EdgeMark position="top-left" />
              <EdgeMark position="bottom-right" />
              <BuildShot shot={group.shot} label={`Example: ${group.persona}`} />
              <p className="type-eyebrow text-indigo uppercase mt-6 mb-4">{group.persona}</p>
              <Callouts group={group} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Desktop: pinned scroll-jack ──
  return (
    <div className="relative flex" style={{ border: "var(--border-structural)" }}>
      {/* LEFT — sticky column: active group's label + callouts + progress pips */}
      <div
        className="w-[42%] shrink-0 self-start sticky top-0 h-screen flex items-center bg-cream"
        style={{ borderRight: "var(--border-structural)" }}
      >
        <div className="relative w-full px-8 lg:px-12">
          <div className="relative">
            {groups.map((group, i) => (
              <div
                key={i}
                style={{
                  position: i === 0 ? "relative" : "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  opacity: active === i ? 1 : 0,
                  transform: active === i ? "translateY(0)" : "translateY(8px)",
                  transition: "opacity 350ms ease, transform 350ms ease",
                  pointerEvents: active === i ? "auto" : "none",
                }}
              >
                <p className="type-eyebrow text-indigo uppercase mb-5">{group.persona}</p>
                <Callouts group={group} large />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-12">
            {groups.map((_, i) => (
              <div
                key={i}
                style={{
                  width: active === i ? 40 : 12,
                  height: 3,
                  background: active === i ? "var(--indigo)" : "hsla(60, 3%, 8%, 0.18)",
                  transition: "width 400ms ease, background 400ms ease",
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT — scrolling sample-image panels, one 100vh panel per group */}
      <div className="flex-1">
        {groups.map((group, i) => (
          <div
            key={i}
            ref={(el) => { panelRefs.current[i] = el; }}
            className="h-screen flex items-center px-8 lg:px-12"
          >
            <div
              className="w-full"
              style={{
                opacity: active === i ? 1 : 0.25,
                transform: active === i ? "scale(1)" : "scale(0.97)",
                transition: "opacity 400ms ease, transform 400ms ease",
              }}
            >
              <BuildShot shot={group.shot} label={`Example: ${group.persona}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Cowork-specific "What you'll do" cards — the six real builds, in workshop
 *  order. Clay icons: -200.webp variants (200×200, from the 300px PNG masters → use
 *  mix-blend-multiply so the white drops into the card's beige). */
const COWORK_BUILD_ITEMS = [
  {
    icon: "/images/icons/clay/connect-200.webp",
    headline: "Customize your Claude app with connectors and personal skills",
    description:
      "Claude out of the box won't change your life on day one. It's a new employee, and new employees need training.\n\nYou'll learn [[connectors]] and [[skills]], the two customizations that actually matter, then set up your own account live with your instructor. Claude plugs straight into Gmail, Outlook, Zoom, Google Calendar, QuickBooks, and hundreds more.\n\nYou leave with Claude wired into the tools you already work in.",
    image: "/images/cowork-builds/connectors-skills.webp",
  },
  {
    icon: "/images/icons/clay/agent-200.webp",
    headline: "Build your very first fully autonomous agent",
    description:
      "With your tools connected, we build the real thing: a fully autonomous agent, step by step, together.\n\nIt triages your inbox, reads your calendar, searches the web for context, and packages it all into a clean daily or weekly briefing dashboard. Google or Microsoft, Gmail or Outlook: it works the same.\n\nYou'll use it the very next morning. If nothing else, you walk away with an agent that saves you [[hours of tedious work]]. Every single day.",
    image: "/images/cowork-builds/autonomous-agent.webp",
  },
  {
    icon: "/images/icons/clay/skills-200.webp",
    headline: "Turn a complex session of work into a custom skill",
    description:
      "Custom skills are how you train Claude for good... and the fastest path to getting real time back.\n\nEverybody works differently. Even a simple task hides a dozen steps that must happen exactly right: pulling data from files and spreadsheets, researching the web, formatting a deck just so, firing the right notifications.\n\nWe'll take one of your real multi-step workflows and turn it into a skill you rerun with [[a single keystroke]]. Then you'll build a skills library that grows with you, so Claude keeps getting better at your job.",
    image: "/images/cowork-builds/custom-skill.webp",
  },
  {
    icon: "/images/icons/clay/palette-200.webp",
    headline: "Learn how to create stunning visuals that don't look like AI slop",
    description:
      "Distinctive visuals get their own block of this workshop. Default AI output has a look, and everyone can smell it.\n\nYou'll learn the design vocabulary and specialized tools that fix it, [[no code required]], and put them to work the same day. Decks, reports, and artifacts that stop looking generated and start looking like you.\n\nYou leave with a style guide you'll reuse on every project after this one.",
    image: "/images/cowork-builds/distinctive-visuals.webp",
  },
  {
    icon: "/images/icons/clay/analyze-200.webp",
    headline: "Learn how Claude can organize your files and analyze complex data",
    description:
      "One of the most powerful ways to use Claude: point it at several messy, complex data sets at once.\n\nYou'll practice on a [[ready-made data pack]] with copy-and-paste prompts. Claude combines workbooks and sheets, extracts the insights that matter, and organizes your files so they're ready to use.\n\nFrom there it's a short hop to presentations, data visualizations, and research reports.",
    // The INPUT half of this card, from the Roger BD agent video: the prospect
    // name being typed in. Esther chose this frame over the finished brief
    // (2026-08-28) after I flagged that it is visibly Claude Code while the hero
    // promises "No coding, no terminal." Her call, and the crop softens it — the
    // left nav carrying the Home/Code toggle is cut, so what is left is the
    // Roger project chip and the typed company name. The `Local` chip and the
    // folder picker still read as a desktop tool.
    //
    // The finished brief is staged at roger-company-brief.webp if the output
    // half is ever wanted here instead, and roger-buyer-research.webp is the
    // frame that actually shows source links.
    image: "/images/sales-builds/roger-prompt-in.webp",
  },
  {
    icon: "/images/icons/clay/publish-200.webp",
    headline: "Turn raw data into a published, interactive visualization",
    description:
      "Every company deals with data, from ten-person local shops to national firms. And raw data is useless until someone can see what it means.\n\nYou'll build an advanced, fully functional data visualization dashboard: accurate, interactive, and genuinely useful.\n\nThen you'll [[publish it live to the web]] and share the link with anyone on your team.",
    // Roger's "how this was vetted" page: four independent experts, two rounds,
    // all must clear 8/10. A repeatable mechanism, which is what this card is
    // about. Replaces a real-estate portfolio dashboard.
    image: "/images/sales-builds/roger-expert-panel.webp",
  },
];

/** Short REAL attendee quotes pulled from the Cowork source doc (names shortened
 *  to first name + last initial). These layer in alongside the longer, already-
 *  published reviews from workshop-reviews.ts in the scrolling marquee. NOTE:
 *  these came from the workshop chat, not yet cleared as public testimonials —
 *  confirm attendee permission + full names before this page goes live. */
const COWORK_DOC_QUOTES: WorkshopReview[] = [
  { published: true, name: "Kaylee C.", initials: "KC", avatarBg: "#D58A3A", posted: "2 weeks ago", text: "This was a super helpful workshop and you did an excellent job explaining everything." },
  { published: true, name: "Kristina H.", initials: "KH", avatarBg: "#3E73C7", posted: "2 weeks ago", text: "Thank you Isaac! You did a great job." },
  { published: true, name: "Damien R.", initials: "DR", avatarBg: "#5C8C57", posted: "2 weeks ago", text: "This has been excellent. Do you have a follow-up skill seminar we should sign up for?" },
  { published: true, name: "Josh S.", initials: "JS", avatarBg: "#7C4FB3", posted: "3 weeks ago", text: "Highly recommend the Claude Cowork workshop." },
  { published: true, name: "MeLisa D.", initials: "MD", avatarBg: "#2E94A3", posted: "3 weeks ago", text: "Dots connected. Very cool." },
  { published: true, name: "Megha A.", initials: "MA", avatarBg: "#A24B7B", posted: "3 weeks ago", text: "Thanks so much, great session." },
  { published: true, name: "Elizabeth H.", initials: "EH", avatarBg: "#1F5A7C", posted: "1 month ago", text: "Thank you! Great work." },
];

// ─── Claude Code for Beginners: the six real builds (accordion) ───
// Copy authored from the workshop's learning outcomes + agenda. Screenshots
// currently reuse the Cowork captures as PLACEHOLDERS — swap for real
// Claude-Code build captures before this treatment goes live.
const CODE_BUILD_ITEMS: CoworkBuildItem[] = [
  {
    icon: "ds:laptop",
    headline: "Install Claude Code and run it for the first time",
    description:
      "Claude Code sounds intimidating. It isn't. It's software you install, and by the end of the first hour it's running on your own machine.\n\nWe set it up together, step by step, and cover all three ways it works: the browser, the desktop app, and the terminal. You leave knowing which one fits how you actually work. No prior setup, no command-line background, no getting left behind.\n\nYou walk in having never touched it. You walk out with it [[running on your computer]].",
    image: "/images/code-builds/install-run.webp",
  },
  {
    icon: "ds:sparkle",
    headline: "Build a real app by describing what you want",
    description:
      "This is the part people don't believe until they see it. You describe what you want in plain English, and Claude Code writes the actual code.\n\nWe build a working app together: a tool, a tracker, a small internal system. You make the decisions, Claude does the typing. When something breaks, you'll learn how to tell it what's wrong and watch it fix itself.\n\nYou leave having built [[something real]], not a toy demo.",
    image: "/images/cowork-builds/autonomous-agent.webp",
  },
  {
    icon: "ds:pen",
    headline: "Design and build a landing page from a blank screen",
    description:
      "A blank page to a real, good-looking landing page, before lunch.\n\nYou'll learn how to direct Claude on layout, copy, and mobile so the result looks designed instead of generated. No CSS, no design degree.\n\nYou leave with a page you'd actually [[put your name on]].",
    image: "/images/code-builds/landing-page.webp",
  },
  {
    icon: "ds:checklist",
    headline: "Turn a messy manual task into a repeatable workflow",
    description:
      "Everyone has that one task that eats an hour every week. We take one of yours and turn it into something Claude Code runs on command.\n\nYou'll learn to break a real workflow into steps Claude can repeat exactly, then save it so you rerun it whenever you need it.\n\nYou leave with [[time back]], every week, starting the next morning.",
    image: "/images/cowork-builds/custom-skill.webp",
  },
  {
    icon: "ds:deck",
    headline: "Organize and analyze your real data",
    description:
      "Point Claude Code at messy files and spreadsheets and let it do the sorting, combining, and analysis you've been dreading.\n\nWe'll practice on a ready-made data pack with copy-and-paste prompts, then pull out the insights that actually matter and organize the files so they're usable.\n\nFrom there it's a short hop to reports, charts, and [[decisions you can defend]].",
    // The INPUT half of this card, from the Roger BD agent video: the prospect
    // name being typed in. Esther chose this frame over the finished brief
    // (2026-08-28) after I flagged that it is visibly Claude Code while the hero
    // promises "No coding, no terminal." Her call, and the crop softens it — the
    // left nav carrying the Home/Code toggle is cut, so what is left is the
    // Roger project chip and the typed company name. The `Local` chip and the
    // folder picker still read as a desktop tool.
    //
    // The finished brief is staged at roger-company-brief.webp if the output
    // half is ever wanted here instead, and roger-buyer-research.webp is the
    // frame that actually shows source links.
    image: "/images/sales-builds/roger-prompt-in.webp",
  },
  {
    icon: "ds:external",
    headline: "Deploy your project live to a real URL",
    description:
      "A project stuck on your laptop helps nobody. So you'll push yours live to the internet, on a real URL, before the session ends.\n\nWe deploy together through Google Cloud, step by step. You'll share the link with your team, a client, or anyone you want that same afternoon.\n\nYou leave having [[shipped something live]], with the workflow to do it again on your own.",
    // Roger's "how this was vetted" page: four independent experts, two rounds,
    // all must clear 8/10. A repeatable mechanism, which is what this card is
    // about. Replaces a real-estate portfolio dashboard.
    image: "/images/sales-builds/roger-expert-panel.webp",
  },
];

// ─── Claude Code for Beginners: persona carousel ───
// Card grounds are the Cowork design system's four APPROVED tone-on-tone
// pairings, used in both directions where the system lists them: teal 300/900,
// lavender 900/400, slate 200/800, green 400/900. bg is the surface, fg the
// type on it — never a colour from another ramp. They are
// values rather than tokens, so they don't follow the .ds-cowork reskin
// automatically; if this page leaves that system, revisit them here.
// The portraits in ds/ are the SAME photographs as the originals one level up,
// with the studio backdrop chroma-shifted to match the card ground (see
// scripts/_persona-cards-ds-recolour.py). Each bg below must stay equal to its
// image's backdrop or the card and the photo won't line up.
// NOTE: the fourth entry pointed at consultant.webp, which does not exist —
// the file is consultants.webp. That card's image was broken.
// Reuses the shared persona-card portraits (generic role imagery); copy is
// authored for the code-beginner audience.
const CODE_PERSONAS: Persona[] = [
  /* Vivid 2026 (Esther, 2026-10-08): the same six plates re-shot onto flat
     Vivid walls (.gen-work/code-personas-vivid, edit pass, then flattened).
     Each `bg` is the plate's own measured wall, so card and photo meet with no
     step; three land one value off the target hex because WebP cannot hold
     it exactly. Plum, Crimson, Navy, Canopy, Gold, Plum. Each title is its wall's tone-on-tone ink
     (Gold has no legal pair, so it takes Paper). */
  {
    key: "founders",
    title: "Founders",
    build:
      "Ship the internal tool, landing page, or MVP you've been quoting out to developers yourself, in an afternoon instead of a sprint.",
    bg: "#5E1756",
    fg: "#C8AFF0",  // Orchid
    img: "/images/persona-cards/vivid-code/founders.webp",
    alt: "A founder beside the clay Claude robot.",
  },
  {
    key: "marketers",
    title: "Marketers",
    build:
      "Build the landing pages, calculators, and campaign microsites you need without waiting on a dev queue or paying for another subscription.",
    bg: "#9C0032",
    fg: "#F3BDAF",  // Pink
    img: "/images/persona-cards/vivid-code/marketers.webp",
    alt: "A marketer beside the clay Claude robot.",
  },
  {
    key: "ops",
    title: "Operations & finance",
    build:
      "Turn the spreadsheet you rebuild every month into a real tool that pulls, cleans, and reports on your data on command.",
    bg: "#293750",
    fg: "#B4C8E1",  // Cloud
    img: "/images/persona-cards/vivid-code/finance-ops.webp",
    alt: "An operations lead beside the clay Claude robot.",
  },
  {
    key: "consultants",
    title: "Consultants & agencies",
    build:
      "Hand clients working dashboards and tools instead of slide decks, and bill for the outcome instead of the hours.",
    bg: "#0F503C",
    fg: "#BACD2D",  // Lime
    img: "/images/persona-cards/vivid-code/consultants.webp",
    alt: "A consultant holding the clay Claude robot.",
  },
  {
    key: "product",
    title: "Product managers",
    build:
      "Prototype the feature instead of describing it. Build a clickable, working version to put in front of your team and users this week.",
    bg: "#8C5F13",
    fg: "#F5F5F7",  // Paper
    img: "/images/persona-cards/vivid-code/product.webp",
    alt: "A product manager beside the clay Claude robot.",
  },
  {
    key: "designers",
    title: "Designers",
    build:
      "Take your designs all the way to a live, responsive, shipped page yourself, without handing them off and hoping.",
    bg: "#5E1756",
    fg: "#C8AFF0",  // Orchid
    img: "/images/persona-cards/vivid-code/designers.webp",
    alt: "A designer beside the clay Claude robot.",
  },
];

// ─── Claude for Sales — the curriculum, as one system ────────────────────────
// Same shape as CODE_BUILD_ITEMS so the alternating full-width rows inherit
// exactly: a painted ds: icon beside the headline, a [[marked]] phrase closing
// each description, one screenshot per row. Content is Ronnie's brief — a
// Foundation that teaches the AI who you are, three engines following the
// motion of a deal, then the method that makes the count unlimited.
//
// PLACEHOLDER: `image` points at Cowork captures. Sales needs its own five
// 3:2 screenshots at /images/sales-builds/ — a real profile, a real intel
// brief, a drafted email, a recap, a skill being saved.
/* Copy written against docs/copy-guide.md.
   The row TITLE is now just the component's name, and the body is a single
   paragraph. Two earlier passes are recorded because both were wrong in a way
   worth not repeating:
     · "Foundation:", "Engine 1:", "Engine 2:" — internal scaffolding that told
       a scanner nothing about what any row held.
     · then a mono kicker naming the artifact ABOVE an opinionated headline.
       Legible, but two registers of type in a collapsed row competing to be
       read first, for a row that only needs to say what it is.
   The name is the title. The opinion lives in the paragraph, where there is
   room for it.

   Bodies are one paragraph, longest sentence 21 words, no em dashes, and the
   word this page's brief forbids outright ("template") appears nowhere. */
/* ─── Claude for Sales: what you build ───
   THE JOB OF THIS COPY IS TO BE UNDERSTOOD, not to be admired. Esther's note
   (2026-08-27) was that the cards read as word salad and did not say what a
   buyer walks away with.

   So every card now answers the same two questions in the same order: what do
   you put in, and what comes back out. "You give it X. Back comes Y." The
   phrasing varies but the shape does not, which is what lets someone skim five
   cards and come away with a map instead of five essays.

   THE CHAIN IS CARRIED IN THE COPY, not in furniture. There is no numbering and
   no summary line above the cards — both were tried and cut (the numbers in
   the eyebrow, and "One foundation, three engines, and the method to build the
   fourth one yourself", which Esther read as nonsense). Instead card 1 says the
   profile is read by "every engine below", card 4 says it "hands that last part
   to your Outreach engine", and card 5 says you have now built the same thing
   three times. The sequence is legible without a scaffold announcing it.

   THE ONE WORD THAT CANNOT APPEAR is "templates" — a hard rule from Ronnie's
   brief, because the entire pitch is that nothing here is filled in around a
   blank. Card 3 makes that point positively instead: the opening line says
   something true about them.

   The screenshots are still the Cowork page's, and they do not illustrate any
   of this. See docs/sales-page-screenshot-brief.md for what has to be captured
   to replace them. */
/* THE SIX STEPS OF THE MORNING, IN ESTHER'S OWN WORDS (2026-09-08). She wrote
   all six headings and bodies and sent them as the section's copy; this is a
   transcription, not a rewrite, and it should stay that way. Two deliberate
   departures, both flagged to her:

     · Section 4 once ended "instead of relying on a generic template", which
       had to become "instead of something generic" — "template" is the one word
       this page never uses (Ronnie's standing brief). Her 2026-09-08 rewrite
       replaced that clause entirely with "that reflect what the prospect is
       looking for", so the substitution is moot and the rule still stands.
     · Straight apostrophes rather than typographic ones, to match every other
       string in this file.

   WHY SIX AND NOT FIVE. The previous set named the ENGINES you build (research
   engine, outreach engine, the method). Hers names the STEPS OF A DEAL, which is
   the sequence a seller already has in their head: research, prep, the call,
   the follow-up, the CRM, then joining them up. The last card changed job
   completely — "The method" was about the repeatable habit, "Connect the entire
   sales process" is about the pieces feeding each other — so it is a new card,
   not a rename.

   ALL SIX IMAGES ARE REAL CAPTURES, and five of them are Esther's own, shot on
   2026-09-09 after three of my passes failed in three different ways. The
   history is worth keeping because each failure taught something:

     1. Pillow typesetting. Content right, read as a text dump — no leading, no
        grid, and half the list marks came out as tofu because the system fonts
        carry no glyph for them.
     2. DS2026 sheets, properly built. Still wrong: "you just put text it needs
        to be visual first all of this so someone can glance at visual without
        studying it". A card sitting BESIDE its own description does not get
        read twice, so anything that must be read is wasted.
     3. Diagrams with the connector logos. Better, and still a drawing of the
        thing rather than the thing: "these still arent great. i need you to
        pull real screenshots from real dahboards show visually how it
        connects."

   She was right at every step, and the fix was not a better drawing. It was
   building the artefacts and screenshotting them. .gen-work/cards/build.mjs
   still regenerates the six sheets if a slot ever needs a stand-in again.

   WHAT THE SIX SHOW, in order: the prospect brief with its five sources named
   in the footer; the call brief, with the opening line, the questions and a
   two-column "when she says / you say"; the action-item queue, six things
   staged off the call with NOTHING ticked; the drafted email in a real Gmail
   compose window with every line traced to a timestamped quote; the HubSpot
   deal record with the stage moved and what was written listed beside it; and
   the whole deal on one timeline, seven stops from Aug 17 to closed won Sep 17.

   ONE THING MAKES THEM CREDIBLE RATHER THAN PROMOTIONAL, and it is hers, not
   mine: every card shows what is NOT done. Staged, not saved. Invite unsent.
   One thing to decide. Pricing not yet confirmed. My designed sheets all showed
   finished work, which is exactly why they read as claims. An agent that has
   already emailed the prospect and written to the CRM unprompted is a
   liability; an agent with everything staged and waiting on you is the product,
   and the set says so on every card.

   ONE PROSPECT RUNS THROUGH ALL SIX, so the section's argument — that the steps
   feed each other — is visible rather than asserted. Kindred Field and Dana
   Reyes-Whitlock are constructed for the demo, which is the requirement and not
   an accident: docs/sales-page-screenshot-brief.md bars real prospect data from
   this page.

   NO IMAGE IS SHARED WITH ANOTHER PAGE. Row 6 briefly held the Claude
   Connectors panel, which is the Cowork page's own file; her timeline replaced
   it, so the duplicate is gone and the row shows the outcome rather than the
   plumbing.

   The predecessors all remain on disk and nothing was overwritten:
   roger-company-brief.webp, roger-buyer-research.webp, roger-company-detail.webp,
   roger-expert-panel.webp and roger-prompt-in.webp.

   EVERY ROW HAS ITS OWN aspect-ratio RULE in cowork-ds.css, matched to its
   file. Not optional and not a preference: the frame is object-fit:cover, so a
   ratio that does not match crops the file silently, and the six rules must stay
   per-row because one collapsed rule loses on specificity to the base scope's
   own :nth-child selectors — measured, that let the Code page's ratios apply and
   cut up to a fifth off some images with no error anywhere. The reasoning is
   written out beside those rules.

   AND THE REVEAL ANIMATION WILL LIE TO YOU. These rows fade in on an
   intersection observer, so a screenshot taken right after expanding one catches
   it at partial opacity and reads as a washed-out contrast failure. Scroll the
   row to the middle of the viewport and wait for computed opacity to reach 1
   before believing any capture of this section. */
// Descriptions rewritten to punch (Isaac, 2026-09-09 review); the earlier explanatory versions are in git history.
const SALES_BUILD_ITEMS: CoworkBuildItem[] = [
  {
    icon: "ds:compass",
    headline: "Research your prospect",
    description:
      "One company name in. A sourced brief out: what they sell, who signs, what changed this quarter, and where it hurts. Every claim cited. Done before your coffee cools.",
    /* ESTHER'S OWN CAPTURE, 2026-09-09 10:47. The prospect brief exactly as the
       agent renders it: who she is meeting, the four numbers that matter, a
       dated Happening Now column, three ranked things the buyer likely cares
       about, and a footer naming the five places it pulled from with a source
       count and a timestamp. That footer is what makes this the right shot for
       this card — the copy promises understanding of "what's happening in their
       business", and the sources are the evidence it was not guessed.

       Shipped at its native 1644x908 with no resampling: 2.4x the 673px box the
       image renders into. Roger's Golf Galaxy brief, which held this slot
       before, is still on disk at roger-company-brief.webp.

       The company and the buyer are constructed for the demo. That is the
       requirement, not an accident — docs/sales-page-screenshot-brief.md bars
       real prospect data from this page. Flagged to Esther to confirm. */
    image: "/images/sales-builds/card-1-research.webp",
  },
  {
    icon: "ds:checklist",
    headline: "Prepare for the call",
    description:
      "Turn the brief into a call sheet you'll actually hold. The opening line, four questions, the objections with your answers ready, the case study to reach for. Nothing forgotten mid-conversation.",
    /* ESTHER'S OWN CAPTURE, 2026-09-09 10:43, and it is the literal call brief
       this card's copy asks for — every clause has a panel: the opening line to
       use, four questions to ask, a two-column "when she says / you say" for
       the objections, the three case studies to reach for, and a Don't Forget
       strip. It reads as a thing you would actually hold during the call, which
       is the promise.

       Shipped at its native 1646x906, no resampling. Roger's buyer read, which
       held this slot before, is still on disk at roger-buyer-research.webp. */
    image: "/images/sales-builds/card-2-call-brief.webp",
  },
  {
    icon: "ds:laptop",
    headline: "Turn the call into next steps",
    description:
      "Drop in the transcript. Out come the decisions, objections, commitments, and action items, staged and waiting on your OK. Nothing sends until you say so.",
    /* ESTHER'S OWN CAPTURE, 2026-09-09 11:48, and she swapped it in for the
       11:26 ledger deliberately. Both show the same six items off the same call
       with the same tool marks — Gmail, HubSpot, Slides, Calendar, Zoom — but
       the status column is inverted. The ledger had four already sent and
       logged. This one has NOTHING ticked: in draft, staged not saved, Claude
       is creating, invite unsent, ready to file, in the draft.

       That is the far better claim and the safer one. An agent that has already
       emailed your prospect and written to your CRM unprompted is a liability;
       an agent that has everything staged and waiting on you is the product.
       The left rail says when each needs you — REVIEW NOW, IN 15 MIN, ON YOUR
       OK — so the card reads as a queue you approve rather than actions taken
       behind your back. It also matches card 4's "one thing to decide" and card
       5's "still open", so the same promise holds across the set.

       Shipped at native 1640x902. Two earlier headline variants exist on her
       machine ("Cleaned up while you were still on the call" 11:16,
       "Follow-ups" 11:26); this is the one she chose. */
    image: "/images/sales-builds/card-3-recap.webp",
  },
  {
    icon: "ds:pen",
    headline: "Draft the follow-up and proposal",
    description:
      "The follow-up written from what they actually said, in your voice, every line traceable to a moment on the call. Proposal, recap, objection reply. You read it, you send it.",
    /* ESTHER'S OWN CAPTURE, 2026-09-09 11:27, and the strongest of the four: a
       REAL Gmail compose window holding the drafted follow-up, with the inbox
       behind it blurred out. Beside it, EVERY LINE HAS A SOURCE — four
       timestamped quotes from the call and what each one became in the draft,
       including one that became the Cc. Then ONE THING TO DECIDE, which leaves
       the judgement with the seller.

       That last panel is why this shot is honest about the product rather than
       overselling it: the draft is ready, and a person still sends it. Shipped
       at native 1642x908.

       She made a version headed "Written out of what she actually said" at
       11:26 and chose this one. */
    image: "/images/sales-builds/card-4-follow-up.webp",
  },
  {
    icon: "ds:stamp",
    headline: "Keep your CRM up to date",
    description:
      "Deal stage, amount, next step, new contacts, the open question. Logged from the conversation, not retyped at 6 PM. Your pipeline stays accurate without becoming a second job.",
    /* ESTHER'S OWN CAPTURE, 2026-09-09 11:38, and the one this section was
       missing longest. A real HubSpot deal record: the stage tracker with
       Qualified and Discovery ticked and Proposal current, the amount, the
       contact who joined the call added, the activity logged, the deal score
       with its key factors. Beside it, WRITTEN FROM THE TRANSCRIPT lists exactly
       what got written and ends on STILL OPEN, "pricing not yet confirmed" —
       which is the line that makes it a record rather than a brochure.

       Shipped at native 1634x902. The designed sheet that held this slot is
       regenerable from .gen-work/cards/build.mjs.

       NOTE the HubSpot pane's own dates read 2025 (close date Jun 30 2025, Q1
       2025, next activities in April 2025) while the rest of the set reads 2026.
       Illegible at the 673px render, flagged to Esther anyway. */
    image: "/images/sales-builds/card-5-crm.webp",
  },
  {
    icon: "ds:sparkle",
    headline: "Connect the entire sales process",
    description:
      "Research feeds outreach. Calls feed follow-up. Follow-up feeds the CRM. One system running beside your live deals from first touch to closed-won, and you built it in a morning.",
    /* ESTHER'S OWN CAPTURE, 2026-09-09 11:57, and it is the card that makes the
       section an argument instead of a list. Seven stops on one timeline for
       ONE deal — research Aug 17, prep and call and recap and CRM on Sep 09,
       proposal Sep 15, closed won Sep 17 at $380,000 — then 31 days / 12 fields
       / 6 follow-ups underneath. Every preceding card is a stop on it, which is
       what "connect the entire sales process" claims and what six separate
       screenshots could only imply.

       It replaced the Claude Connectors panel, which was the right idea and the
       wrong altitude: it showed the plumbing rather than the outcome, and it was
       the Cowork page's file, so it put the same image on two workshop pages.
       That duplicate is now gone.

       Shipped at native 1644x908. A light-ground variant sits at 11:56 on her
       machine; she chose the plum one, which is also the family this row's card
       ground already carries.

       roger-expert-panel.webp is deliberately NOT used anywhere here: it shows a
       four-expert review panel, which is about vetting research, not about
       joining steps together. */
    image: "/images/sales-builds/card-6-chain.webp",
  },
];

// ─── Claude for Sales: audience carousel ───
// Framed by the SELLING MOTION, never the job title.
//
// The card grounds are inherited EXACTLY from CODE_PERSONAS, because each one
// is baked into its photograph — the ds/ portraits are the same shots with the
// studio backdrop chroma-shifted to the card's colour, so bg and image are a
// matched pair and cannot be reassigned independently.
//
// What rotates instead is WHICH AUDIENCE SITS ON WHICH CARD. Same five
// families as the Code page, same approved pairings, a different sequence —
// which is the point of rotating rather than swapping.
//
// ORDER IS BY AUDIENCE SIZE, NOT BY COLOUR (Esther, 2026-08-28: "most popular
// type first"). Cold outbound leads because it is the shape the curriculum
// itself takes — research, then outreach — and the shape the hero promises
// ("three prospects from your own pipeline"). Founder-led follows as the
// broadest single-seat identity, then the team buyer.
//
// So the colour sequence is now an OUTPUT of the ordering rather than an input.
// The grounds still travel with their photographs and cannot be reassigned, so
// re-ranking these entries re-sequences the colours too. That is fine here:
// these are separate cards in a horizontal scroller, not stacked rooms, so the
// adjacency rules that bind the fluency blocks do not apply — the pre-existing
// order broke them anyway (plum sat next to teal twice).
/* Vivid pass (Esther, 2026-10-08): the same plates on flat Vivid walls, as
   Code and Cowork use; each bg is the plate's measured wall and fg its
   tone-on-tone ink. Named accounts moves last so the two Plum cards are not
   side by side. */
const SALES_PERSONAS: Persona[] = [
  {
    key: "outbound",
    title: "Cold outbound",
    build:
      "Research a company properly before the first touch instead of skimming a homepage, then send something they'll actually answer. On every name in the list, not just the top ten.",
    bg: "#293750",
    fg: "#B4C8E1",  // Navy / Cloud
    img: "/images/persona-cards/vivid-code/finance-ops.webp",
    alt: "A seller beside the clay Claude robot.",
  },
  {
    key: "founder-led",
    title: "Founder-led\nsales",
    build:
      // Every other persona here is a concrete moment: a lead landing at 4:50,
      // walking into a call knowing what changed last quarter. This one was the
      // odd abstraction out ("stop being the bottleneck on your own pipeline"),
      // and its metaphor asked one plate to both keep and lose the same work.
      // The founder's actual situation is the specific thing: they sell in the
      // hours left over from running the company.
      "You are the whole sales team, working the hours left over from running the company. Hand the prep and the follow-up to the engines, so those hours go on the conversation.",
    bg: "#8C5F13",
    fg: "#F5F5F7",  // Gold / Paper
    img: "/images/persona-cards/vivid-code/product.webp",
    alt: "A founder beside the clay Claude robot.",
  },
  {
    key: "leading-a-team",
    /* "Running a SALES team" (Esther, 2026-09-08). This card is inherited from
       the Code page's persona set, where "Running a team" meant an engineering
       lead. On a sales page it reads as any manager, which is both vaguer and
       wrong for the card's own body copy about pipeline review and forecasting. */
    title: "Running a sales team",
    build:
      "Build the engines once, then hand your reps a system that already knows how your team sells. Get back the hours that pipeline review and forecasting eat.",
    bg: "#0F503C",
    fg: "#BACD2D",  // Canopy / Lime
    img: "/images/persona-cards/vivid-code/consultants.webp",
    alt: "A sales lead beside the clay Claude robot.",
  },
  {
    key: "inbound",
    title: "Inbound & demo-led",
    build:
      "A lead lands at 4:50 PM. Have the full picture of who they are and a first reply in your voice before you leave for the day.",
    bg: "#007791",
    fg: "#F5F5F7",  // Ocean / Paper (Esther, 2026-10-09: fewer purples)
    img: "/images/persona-cards/vivid-code/designers-ocean.webp",
    alt: "A seller beside the clay Claude robot.",
  },
  {
    key: "relationship",
    title: "Field & relationship\nselling",
    build:
      "Real estate, insurance, services, manufacturing. The system tunes to what you sell, so the brief covers what matters in your market instead of somebody else's.",
    bg: "#9C0032",
    fg: "#F3BDAF",  // Crimson / Pink
    img: "/images/persona-cards/vivid-code/marketers.webp",
    alt: "A seller beside the clay Claude robot.",
  },
  {
    key: "named-accounts",
    title: "Named accounts",
    build:
      "Walk into a call already knowing what changed last quarter, who moved, and which of your three angles fits. Then leave the call with the recap already drafted.",
    bg: "#5E1756",
    fg: "#C8AFF0",  // Plum / Orchid
    img: "/images/persona-cards/vivid-code/founders.webp",
    alt: "A seller beside the clay Claude robot.",
  },
];

// ─── Claude for Design — premium build accordion ─────────────────────────────
// First-draft copy adapted from the design workshop's "What you'll do" outcomes
// (shared/workshop-content.ts). NOTE: clay icons are placeholders drawn from the
// shared Cowork icon set — design-specific clay icons still need to be generated
// (/clay-icon). Screenshots are placeholders from the existing design assets;
// swap for real per-build captures when they land.
const DESIGN_BUILD_ITEMS: CoworkBuildItem[] = [
  /* Esther's copy, verbatim, 2026-09-29. Titles carry NO numbers — she asked
     for none and the accordion does not add its own; it renders `headline` as
     written.

     No [[highlight]] spans either, because her copy does not mark one and
     choosing the phrase would be choosing her emphasis for her. The .cba-hl
     device is still wired up, so adding one is a pair of brackets per item.

     One paragraph each, which also sidesteps the accordion bug that clips
     everything after the first (paragraphs 2+ render at zero height on this
     component, on the Code page too).

     IMAGES: Esther's assignment, 2026-09-29 — a closed rotation, every
     capture reused exactly once, so each card shows the artefact its copy
     names rather than something adjacent to it:

       Learn the Language of Design   design-language    the finished hero,
                                                        marked up: this card's
                                                        six words pinned to the
                                                        thing each one names
       Find the Right References      moodboard-carried  the reference board
       Build a Design System          design-system      the Design System
                                                        panel itself
       Write a Better Design Brief    claude-skill       design-prompt-writer
                                                        SKILL.md, which IS the
                                                        brief
       Add Images + Motion            claude-iterate     an image element
                                                        selected, transform and
                                                        filter open
       Build + Publish                brand-canvas       the Brand Canvas, all
                                                        assets at once

     design-language.webp is BUILT, not captured: landing-page.webp on a
     #15271D mat (the hero's own background, sampled) at 1600x1067 so it fills
     the accordion's 3:2 box with nothing cropped, plus Lime #BACD2D leaders,
     dots and one measure arrow. Lime is the forest ground's approved Vivid
     fill, and the hero IS a forest ground, so the markup is system-legal
     rather than a seventh colour. Labels are 40px JetBrains Mono in the
     source, which lands at ~17 CSS px in the 680px box — the one thing on
     this plate that had to stay readable at display size. Every leader is
     drawn twice, a 5px #0A1610 stroke under a 2px Lime one, so the lines
     survive crossing the bright dandelion field. Rebuild: the generator is
     .gen-work/plate.ts plus a throwaway client/public/_annotate-tmp.html.

     INVARIANT (see cowork-build-accordion.tsx): each of these ships -480,
     -800 and -1200 beside it. All six do. */
  {
    icon: "/images/icons/clay/palette-200.webp",
    headline: "Learn the Language of Design",
    description:
      "Know how to talk about layout, type, color, spacing, hierarchy, and style.",
    image: "/images/design-workshop/claude-design/design-language.webp",
  },
  {
    icon: "/images/icons/clay/palette-200.webp",
    headline: "Find the Right References",
    description:
      "Use existing designs to show Claude the look and direction you want.",
    image: "/images/design-workshop/claude-design/moodboard-carried.webp",
  },
  {
    icon: "/images/icons/clay/palette-200.webp",
    headline: "Build a Design System",
    description:
      "Set your fonts, colors, spacing, imagery, and reusable styles.",
    image: "/images/design-workshop/claude-design/design-system.webp",
  },
  {
    icon: "/images/icons/clay/palette-200.webp",
    headline: "Write a Better Design Brief",
    description:
      "Give Claude clear direction before it starts designing.",
    image: "/images/design-workshop/claude-design/claude-skill.webp",
  },
  {
    icon: "/images/icons/clay/palette-200.webp",
    headline: "Add Images + Motion",
    description:
      "Create visuals, animations, and interactions that make the work feel finished.",
    image: "/images/design-workshop/claude-design/claude-iterate.webp",
  },
  {
    icon: "/images/icons/clay/palette-200.webp",
    headline: "Build + Publish",
    description:
      "Turn the idea into a finished page, deck, email, social asset, or experience.",
    image: "/images/design-workshop/claude-design/brand-canvas.webp",
  },
];


// ─── Claude for Design — persona carousel ────────────────────────────────────
// Audience mirrors the design FAQ ("marketers, founders, operators, anyone who
// waited 6 weeks for an agency"). Reuses existing persona-card imagery.
const DESIGN_PERSONAS: Persona[] = [
  /* The `ds/` plates, and each card's ground is the DS room its OWN photo was
     chroma-shifted to — measured off each file's top-left corner:

       Designers         Canopy    #0F503C   ink Lime    #BACD2D  (5.31)
       Founders          Plum      #5E1756   ink Orchid  #C8AFF0  (6.26)
       Marketing         Crimson   #9B0032   ink Pink    #F3BDAF  (5.21)
       Product managers  Gold      #8B5F13   ink Paper   #F5F5F7  (4.57)

     These four grounds MUST match the studio wall baked into each plate, or a
     visible step appears across the middle of the card. They now use the Vivid
     2026 persona plates shipped with the design system
     (design-system-vivid-2026/personas/), and the ground is that plate's own
     backdrop, so card and wall line up exactly.

     The persona-to-accent mapping is the design system's, not ours:
     CONFIG.personas pairs Canopy/Designer, Plum/Founder and Crimson/Marketer.
     Product manager maps to Tangerine there, but that plate is the ONLY warm
     one shot with the plain clay bot seated on a lap rather than the blazered
     bot on a shoulder — next to the other three it reads as a different
     mascot. Gold is the nearest warm accent whose plate has the right bot and
     the right pose, so Product manager takes Gold. rust and sunset were
     checked and rejected for the same reason as tangerine.
     Room per card, never two families inside one.

     They were the old site palette until 2026-09-23: plum 300 #3B2438, teal
     300 #1E3D3B, lavender #2B3450 and slate 200 #16202B, none of them in
     Vivid. */
  {
    key: "designers",
    title: "Designers",
    build:
      "Skip the handoff. Take your own concepts all the way to a live, responsive, shipped page yourself, and spin up three directions in the time a single mockup used to take.",
    bg: "#0F503C",
    img: "/images/persona-cards/vivid/forest.webp",
    alt: "A designer beside the clay Claude robot.",
  },
  {
    key: "founders",
    title: "Founders",
    build:
      "Get a real landing page live this week without hiring an agency or waiting six weeks. Brief Claude, pick a direction, and ship it yourself before the idea goes cold.",
    bg: "#5E1756",
    img: "/images/persona-cards/vivid/plum.webp",
    alt: "A smiling founder with the clay Claude robot on his shoulder.",
  },
  {
    key: "marketing",
    title: "Marketing",
    build:
      "Launch campaign pages and social creative on your own timeline. Test three hero variations, write the copy, and publish, all before the next standup.",
    bg: "#9B0032",
    img: "/images/persona-cards/vivid/crimson.webp",
    alt: "A marketer high-fiving the clay Claude robot.",
  },
  {
    key: "product",
    title: "Product managers",
    build:
      "Turn a rough concept into a clickable, well-designed page you can put in front of real users. Validate the idea with something real, not another wireframe.",
    bg: "#8B5F13",
    img: "/images/persona-cards/vivid/gold.webp",
    alt: "A product manager beside the clay Claude robot.",
  },
];

// ─── AI Video Workshop for Beginners — build accordion ──────────────────────
// Dave Spector's beats, in teaching order (Dave's brief, 2026-10-23). Each
// card shows the thing it teaches (Esther, 2026-10-26), and since 2026-10-28
// all four follow ONE character, the man in Dave's Natural Actor 3 clip: his
// sheet, the clip itself, a take that drifts to someone else beside one that
// holds, and the timeline of a four-shot coffee spot starring him. The sheet,
// takes and spot are Nano Banana Pro stills generated from a frame of the
// clip; the take-1 drift has to read as a real production problem, not creepy
// and not sloppy. Other spots in client/public/videos/creative-work/ are NOT
// cleared for this page.
const VIDEO_BUILD_ITEMS: CoworkBuildItem[] = [
  {
    /* Chrome-tinted marks, generated 2026-10-28 to match the approved
       /workshops set (which has no person, camera or clapperboard). */
    icon: "/images/ai-video-workshop/icons/bust.webp",
    headline: "Prompt AI images of people who look human",
    description:
      "Most AI-generated people give themselves away with waxy skin and unnatural movements. Learn the prompting techniques to get people that [[feel alive]].",
    // One character runs through all four cards (Esther, 2026-10-28): the
    // Natural Actor 3 man. His sheet, generated from a frame of Dave's clip,
    // replaces Dave's sheet of the woman.
    image: "/images/ai-video-workshop/actor-sheet.webp",
  },
  {
    icon: "/images/ai-video-workshop/icons/camera.webp",
    headline: "Turn your images into video that moves naturally",
    description:
      "A still image is the easy part. Learn how to direct motion so your people talk, laugh, and move in [[shockingly natural]] ways.",
    image: "/images/ai-video-workshop/natural-actor-3.webp",
    // Dave's "Natural Actor 3" (2026-10-28), in place of his earlier 8s cut,
    // which read desaturated on the site. New filenames, so no cache-bust.
    video: "/videos/ai-video-workshop/natural-actor-3.mp4",
  },
  /* The storyboard card ("See how we made our commercials, step by step")
     is gone at Dave's ask (2026-10-27): he teaches two skills per commercial
     and then an exercise, not a step-by-step walkthrough. */
  {
    icon: "/images/ai-video-workshop/icons/compass.webp",
    headline: "Course-correct when your prompts aren't working",
    description:
      "Everyone hits the moment where the tool keeps giving you the wrong thing, like a character who turns into someone else between shots. Learn how to spot what went wrong and [[get back on track]] instead of rerolling for an hour.",
    // Same window shot twice: take 1 drifts to a different man, take 2
    // matches his sheet on the first card.
    image: "/images/ai-video-workshop/actor-course-correct.webp",
  },
  {
    icon: "/images/ai-video-workshop/icons/clapper.webp",
    headline: "Build a first draft of your own commercial or short film",
    description:
      "Bring an idea, or find one in the room. You'll create images and clips throughout the workshop and shape them into a [[first draft]] you can keep refining after you log off.",
    image: "/images/ai-video-workshop/first-draft-edit.webp",
    // A working edit, rendered as one loop: his three-shot coffee spot
    // (Seedance clips from the stills on the cards above) plays in the
    // viewer while the playhead, timecode and active shot track it
    // (Esther, 2026-10-28: "it needs to actually work").
    video: "/videos/ai-video-workshop/first-draft-edit.mp4",
  },
];

// ─── AI Video Workshop for Beginners — persona carousel ──────────────────────
// Framed by what each person wants to make, not by job title. Business owners
// lead: Dave wants them in the room most. The Vivid persona plates and their
// measured walls are shared with Cowork (VIVID_PERSONA below).
const VIDEO_PERSONAS: Persona[] = [
  /* Dave, 2026-10-27: marketers and business owners lead. Filmmakers sit
     between the two plum cards so no colour repeats side by side. */
  {
    key: "marketing",
    title: "Marketers",
    build:
      "Stop waiting on a production budget to test a video idea. Make a rough cut of the concept yourself, show it to your team, and decide with something real on screen.",
    bg: "#9C0032",
    fg: "#F3BDAF",
    img: "/images/persona-cards/vivid-code/marketers.webp",
    alt: "A marketer high-fiving the clay Claude robot.",
  },
  {
    key: "founders",
    title: "Business owners",
    build:
      "You've seen an AI ad and thought, I could make that. You probably can make a version of it. Find out what it really takes to get from a decent first try to something you'd put your name on.",
    bg: "#5E1756",
    fg: "#C8AFF0",
    img: "/images/persona-cards/vivid-code/founders.webp",
    alt: "A business owner with the clay Claude robot on his shoulder.",
  },
  {
    key: "executives",
    /* Esther, 2026-10-29: "Filmmakers" in place of "The curious". */
    title: "Filmmakers",
    build:
      "You already know how to shoot and cut. Learn where AI fits in your workflow, from pitch visuals to the shots you couldn't afford to film.",
    bg: "#8C5F13",
    fg: "#F5F5F7",
    img: "/images/persona-cards/vivid-code/executives.webp",
    alt: "A smiling professional beside the clay Claude robot.",
  },
  {
    key: "designers",
    title: "AI creators",
    build:
      "You've made a few clips and hit a wall. Pick up the prompting habits and fixes that turn an interesting experiment into something people watch to the end.",
    bg: "#5E1756",
    fg: "#C8AFF0",
    img: "/images/persona-cards/vivid-code/designers.webp",
    alt: "A creator beside the clay Claude robot.",
  },
  {
    key: "sales",
    title: "Social media managers",
    build:
      "Your feed needs more video than you can shoot. Learn to make clips for Reels, TikTok, and Shorts without booking a shoot every week.",
    bg: "#0F503D",
    fg: "#BACD2D",
    img: "/images/persona-cards/vivid-code/sales.webp",
    alt: "A smiling social media manager with the clay Claude robot, in headphones, on his shoulder.",
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// PREMIUM PAGE LAYOUT — opt-in, per series.
// Presence of an entry here switches a workshop's detail page from the default
// editorial treatment (WorkshopHero + learning-outcome grid) to the premium
// treatment (photo-blend CoworkHero + build accordion + persona carousel +
// scrolling testimonials + attendees-receive band). Follows the same
// per-series-map idiom as FINAL_CTA_BY_SERIES / SHOW_COMPARISON_FOR_SERIES.
// ═══════════════════════════════════════════════════════════════════════════
interface PremiumLayoutConfig {
  /** Wrapper class (shrinks section headings). */
  pageClass: string;
  hero: {
    /** Responsive image base; omit to reuse the shared Isaac clay-shoulder portrait. */
    imageBase?: string;
    /** Art-directed MOBILE plate. Use when the desktop plate's composition does
     *  not survive a narrow box — see CoworkHero.imageBaseMobile. */
    imageBaseMobile?: string;
    /** Serve AVIF ahead of WebP for this hero. Requires .avif beside every
     *  .webp the srcsets name — a missing one renders nothing rather than
     *  falling back. Only Sales has them. */
    heroAvif?: boolean;
    /** Print the session's time range and platform under the date on each open
     *  card. Pure data — read off the session row and the event's `platform` —
     *  so there is no copy here to go stale. Off elsewhere, so the Cowork and
     *  Code cards are unchanged. */
    showSessionMeta?: boolean;
    /** Feather the photo's TOP + RIGHT edges into cream (in addition to the
     *  shared left + bottom fades). The Design portrait sits on a warm painterly
     *  wall whose top/right padding reads as a pasted rectangle against the cooler
     *  page cream; these extra fades dissolve it. Cowork's portrait doesn't need it. */
    blendEdges?: boolean;
    headline: ReactNode;
    /** The headline as one clean sentence. Set it only when `headline` carries
     *  decorative markup that duplicates or reorders words — see
     *  CoworkHero.headlinePlain. Sales sets it (the caret insert); the other
     *  three pages leave the <h1> holding their headline directly. */
    headlinePlain?: string;
    subtitle: ReactNode;
    primaryCta: { label: string; href: string };
    secondaryCta: { label: string; href: string };
    /** Eyebrow text as a function of the number of open (bookable) dates. */
    eyebrow: (openCount: number) => string;
    /** Date-card layout; "inline" puts "Tuesday, September 15" on one line. */
    dateLine?: "stacked" | "inline";
    /** Play-once clip over the hero photo; see CoworkHero.introVideo. */
    introVideo?: { src: string; poster: string; srcSmall?: string };    /** Fixed number of date cards (past tiles fill what upcoming leaves). */
    dateCards?: number;
    /** Real-dates pages (no seat clock): how many of the dateCards are the most
     *  recent sold-out dates. When set, the strip is capped at dateCards. */
    soldOutCards?: number;
    /** One session shown as an event card (see CoworkHero.singleEvent). */
    singleEvent?: boolean;
  };
  /** Eyebrow above the build accordion. Defaults to "Live, hands-on training",
   *  which is what the other three premium pages use. */
  learnEyebrow?: string;
  /** Heading above the build accordion. */
  learnHeading: ReactNode;
  buildItems: CoworkBuildItem[];
  /** Render every build panel open, permanently. Pages whose CSS already forces
   *  that shape (`.ds-sales`) set this so the component stops measuring panel
   *  heights the stylesheet then throws away. */
  buildAlwaysOpen?: boolean;
  /** Lay the build items out as a sticky side-by-side scroller (picture held
   *  on the left, cards scrolling on the right) instead of the accordion. The
   *  AI Video page sets it; its items' `icon` is then an image path. */
  buildScroller?: boolean;
  /** Extra sections rendered immediately BEFORE the build accordion, under the
   *  logo strip. The Design page leads with its Samples Showcase here: show the
   *  work first, then explain how it gets made. Sales keeps its gallery after
   *  the accordion, so the two slots stay independent. */
  extraBeforeBuild?: ReactNode;
  /** Extra sections rendered immediately after the build accordion, before the
   *  persona carousel. Design uses it for the Before/After proof; Sales for its
   *  artifact gallery. Neither has an equivalent in the Cowork set. */
  extraAfterBuild?: ReactNode;
  /** Persona carousel cards; omit to hide the section. */
  personas?: Persona[];
  /** Cards rise into place one after another when the row comes into view. */
  personaFloatIn?: boolean;
  /** OnPrompt Studio value-stack band (partner offering — Cowork only). */
  showOnPromptValueStack?: boolean;
  /** Custom OnPrompt band rendered in the same slot as the value-stack. Design
   *  uses its own dark OnPromptStudioSection here instead of the Cowork stack. */
  onPromptSection?: ReactNode;
  /* onPromptPlacement was declared here and is gone: main's `dsCowork` gate already puts OnPrompt after the attendee room, and this page's pageClass includes ds-cowork, so the field controlled nothing. A config field that reads
     like it steers behaviour but steers nothing is worse than no field. */
  /* drawnArrows was declared here and is gone: same: `longArrows={dsCowork}` on the reviews carousel already covers this page. A config field that reads
     like it steers behaviour but steers nothing is worse than no field. */
  /** Overrides the reviews eyebrow, which defaults to "Workshop reviews".
   *
   *  WHY THIS EXISTS. getPublishedWorkshopReviews() returns every review whose
   *  `series` field is empty, and only 2 of the 23 reviews in
   *  shared/workshop-reviews.ts are tagged — so the other 21, written by Cowork
   *  and Code attendees, render on EVERY premium page. On this page one of them
   *  reads "I recently completed Isaac's Claude CoWork workshop" while the
   *  heading above it says "Do we ask OUR attendees for reviews?", and zero
   *  reviews are tagged for the sales series. Under a bare "Workshop reviews"
   *  label a buyer fairly reads all of them as reviews of THIS workshop.
   *
   *  The honest fix is to say whose reviews they are. The long-term fix is to
   *  tag all 23 with their real series, at which point this page shows none
   *  until the sales cohort has run and the override can come back out. */
  reviewsEyebrow?: string;
  /** Overrides the instructor-section heading.
   *
   *  The shared default is "Hosted by best-selling educators and actual AI
   *  practitioners." That is defensible on the pages Isaac fronts alone, and it
   *  is an unsupported claim about the two people who front THIS one: Ronnie and
   *  Brigid are working account executives, not best-selling educators, and this
   *  is a paid enrollment page. Their actual credential is better for a sales
   *  audience anyway — they run the pipeline the workshop teaches. */
  instructorHeading?: string;
  /** Per-page instructor portraits, keyed by instructor name.
   *
   *  WHY A THIRD MAP. Portraits already resolve through two: ABOUT_HEADSHOTS,
   *  then ABOUT_HEADSHOTS_DSCOWORK spread over it when the page is ds-cowork.
   *  This page carries ds-cowork in its pageClass, so that second map wins here
   *  and would drag Isaac back to the v1 portrait — adding v2 to it instead
   *  would change the Claude Code page, which is not ours to change. A config
   *  map is checked last and is scoped to exactly one page. */
  headshotOverrides?: Record<string, { photo: string; srcSet?: string; sizes?: string; cutout?: boolean }>;
  /** Replaces the standard indigo final-CTA section entirely. Design uses a
   *  cream, video-blend closing that mirrors its original hero. */
  finalCtaOverride?: ReactNode;
  /** Attendees-receive band. */
  showAttendeesReceive?: boolean;
  /** Overrides the attendees-band variant; default is the ds-cowork/light rule. */
  attendeesVariant?: AttendeesReceiveVariant;
  attendeesArt?: AttendeesArt;
  /** Overrides the attendees-band heading. Omit and the shared sentence stands. */
  attendeesHeading?: string;
  /** Overrides the attendees-band value list. Omit and the shared six items
   *  stand — the Cowork, Code and Design pages all do. */
  attendeesItems?: ReceiveItem[];
  /** False drops the reserve button from the attendees card (Sales). */
  attendeesCta?: boolean;
  /** Overrides the persona carousel's default header. */
  personaEyebrow?: string;
  personaHeading?: ReactNode;
  /** Right-column line under the persona heading. */
  personaSubheading?: ReactNode;
  /** Extra class on the persona <section> (Sales: the plum brush room). */
  personaClassName?: string;
  /** Scrolling testimonials marquee. */
  testimonials?: {
    reviewsSeriesSlug: string;
    docQuotes: WorkshopReview[];
    /** Only these reviewers, by name, whatever their series tag. For a page
     *  whose own cohort has not run yet and that should show only reviews with
     *  a known source. */
    reviewNames?: string[];
  };
  /** Replaces the whole instructor band with a section of the page's own.
   *  The AI Video page shows Dave in motion in a Monet scene with a floating
   *  name card instead of the portrait row (Esther, 2026-10-26). The
   *  instructor list in workshop-content.ts still feeds the JSON-LD. */
  instructorOverride?: ReactNode;
  /** Instructor band overrides. `instructorEyebrow: null` removes the eyebrow
   *  row entirely. Defaults to what every other series shows, so leaving it
   *  unset changes nothing. */
  instructorEyebrow?: string | null;
  /** Comparison band eyebrow. `null` removes the row (Isaac, 2026-09-17, Sales:
   *  "remove Honest comparison eyebrow"); unset keeps the shared default. */
  comparisonEyebrow?: string | null;
  /** `false` drops the YouTube comparison table and its heading; only
   *  `afterComparison` renders in the room (Design, 2026-10-07). */
  comparisonTable?: boolean;
  /** `false` drops the LinkedIn links under the instructor bios (Isaac,
   *  2026-09-17, Sales: "remove isaac's linkedin"; his is the only one this
   *  cohort has). Unset keeps them. */
  instructorLinkedin?: boolean;
  /** Portrait widths for the instructor band. The shared monetV2 set stops at
   *  672, which is 2x for the three-up 318px cards and soft anywhere a band
   *  renders bigger. A page that widens its portraits declares its own. */
  instructorPhotoWidths?: number[];
  instructorPhotoSizes?: string;
  /** Cut-out portraits, by instructor name: the transparent photo of the
   *  person, shown in front of one shared painted band (see cowork-ds.css).
   *  Unset keeps the single composited photo. */
  instructorLayers?: Record<string, { cutout: string; cutoutSrcSet: string }>;
  /** Responsive image base for a scroll-drifting art ground behind the final
   *  CTA. The canvas runs 150% tall at top:-25% and drifts 12% of its own
   *  height across the crossing — it tracks the scroll, so there is no easing. */
  finalCtaArt?: string;
  /** Credit line under the closing painting. Defaults to the Code page's Irises.
   *
   *  It lives beside `finalCtaArt` because the second page to use that shipped a
   *  Water Lilies ground over a line reading "Irises". Painting and attribution
   *  move together or they drift apart. */
  finalCtaCredit?: ReactNode;
  /** Overrides the agenda band's heading. Omit and the band keeps the shared
   *  "Workshop agenda / what a day to be Ailive" treatment. Same mixed-weight
   *  construction either way: 300 frames it, 500 names it, script accent after. */
  agendaHeading?: ReactNode;
  /** Overrides the FAQ eyebrow. Omit (undefined) and every page keeps today's
   *  "▸ Common questions"; pass `null` to render no eyebrow at all, which is
   *  what Sales does — the heading under it already asks the question, so the
   *  label was naming the section twice. */
  faqEyebrow?: string | null;
  /** What the sticky bar puts on its LEFT side.
   *
   *  "facts" (default, every page but Sales): the pipe-separated date / format /
   *  price line the ticket block mirrors.
   *  "name-date": the workshop's own title plus a badge carrying the next open
   *  date. No price, no format, no "+N more" — see the Sales config for why.
   *
   *  The right side (seat note + Reserve CTA) is the same either way. */
  jumpNavVariant?: "facts" | "name-date";
  /** Sales: render the reviews band after the agenda instead of before it. */
  reviewsAfterAgenda?: boolean;
  /** Rendered inside #comparison directly after the table. */
  afterComparison?: ReactNode;
  /** Rendered after the FAQ, before the closing CTA (Sales: the Claudebot chat). */
  beforeFinalCta?: ReactNode;
}

/* Claude for Beginners (2026-10-26). A draft of Isaac's curriculum, built from
   his Cowork for Beginners set but about the Claude app itself. Every card
   shows a real capture of the Claude app from our other workshops, matched to
   what the card teaches (connectors panel, spreadsheet analysis, Skills list,
   the prompt box, the weekly briefing Claude builds from a calendar). */
const BEGINNER_BUILD_ITEMS: CoworkBuildItem[] = [
  {
    icon: "/images/icons/clay/skills-200.webp",
    headline: "Set Claude up so it already knows you and your work",
    description:
      "Most people open Claude, ask a question, and get an answer that could be for anyone. The fix is setup, and nobody shows you how.\n\nWe build your account live: [[Projects]] for each part of your job, instructions for how you like things done, and your own files loaded in.\n\nFrom then on, every conversation starts already briefed.",
    image: "/images/cowork-builds/autonomous-agent.webp",
    media: (
      <ClaudeFlowDemo
        script={{
          label: "Demo: inside a Claude Project with your brand guide, last quarter's report and your instructions loaded, you ask for the Monday team update. Claude reads your instructions and files, then returns a finished update in your voice.",
          context: { label: "Project: Marketing team", files: ["brand-voice.pdf", "Q3-report.xlsx", "How I like things"] },
          prompt: "Draft this week's Monday update for my team.",
          steps: ["Reading your project instructions", "Matching the voice in brand-voice.pdf", "Pulling the numbers from Q3-report.xlsx"],
          result: {
            title: "Monday update · Week of Nov 23",
            lines: ["Hi team, three things this week:", "1. The launch brief goes to legal on Tuesday.", "2. Q3 recap deck is in the shared drive.", "3. Webinar sign-ups close Friday at noon."],
          },
        }}
      />
    ),
  },
  {
    icon: "/images/icons/clay/agent-200.webp",
    headline: "Get answers that sound like you, not like AI",
    description:
      "Generic in, generic out. That's why so much AI writing reads stiff and average.\n\nWe teach the few habits that change the result: real context, an example of what good looks like, asking Claude to question you first, and pushing back on a weak draft.\n\nYou rewrite one of your own tasks live and see the difference [[side by side]].",
    image: "/images/sales-builds/roger-prompt-in.webp",
  },
  {
    icon: "/images/icons/clay/connect-200.webp",
    headline: "Connect Claude to Gmail, Google Drive, and your calendar",
    description:
      "Claude gets far more useful when it can see what you see.\n\nWe set up [[Connectors]] live, so Claude can find the thread, summarize the doc, and prep you for the meeting without you copying and pasting a thing. We also walk through the privacy settings, so you decide what Claude can see.\n\nYou leave with Claude working inside the tools you already use.",
    image: "/images/cowork-builds/connectors-skills.webp",
  },
  {
    icon: "/images/icons/clay/analyze-200.webp",
    headline: "Hand Claude your spreadsheets, PDFs, and research",
    description:
      "This is where the hours come back.\n\nDrop in a messy spreadsheet and get the answer you needed from it. Turn a 40-page PDF into the three points that matter. Run [[Research]] on a question and get sources you can check.\n\nAll on your own files, with Isaac beside you.",
    image: "/images/cowork-builds/data-analysis.webp",
  },
  {
    icon: "/images/icons/clay/palette-200.webp",
    headline: "Turn your weekly work into Skills that run on their own",
    description:
      "The tasks you repeat every week shouldn't start from scratch every week.\n\nWe turn one of yours into a [[Skill]] you can rerun any time, and set it on a schedule. Then you make a deck, a document, and a chart with Claude that look like you made them.\n\nYou walk out with work that's finished, and a setup that keeps working.",
    image: "/images/cowork-builds/custom-skill.webp",
  },
];

/* Same Vivid plates as Cowork, with the lines rewritten around what each person
   wants Claude to do for them rather than around Cowork features. */
const BEGINNER_PERSONA_BUILD: Record<string, string> = {
  founders: "Get the first draft of everything off your plate: proposals, hiring posts, investor updates, and the research behind them.",
  marketing: "Draft campaigns, briefs, and reports in your brand's voice, and pull answers out of your data without waiting on an analyst.",
  sales: "Research accounts before the call, write follow-ups that sound like you, and keep up with a pipeline that never slows down.",
  "finance-ops": "Make sense of messy spreadsheets, reconcile reports faster, and turn the numbers into a summary leadership will actually read.",
  consultants: "Turn interviews and documents into findings and decks in hours, and put more of your billable time into the thinking.",
  designers: "Turn rough ideas into briefs, decks, and first drafts fast, and keep your own time for the parts only you can design.",
};
/* Order alternates walls so no two neighbours share one: Plum, Crimson,
   Canopy, Navy, Plum, Canopy. Executives is out because its plate sits on
   Gold, which is not a Vivid room (fails roomOk). */
const BEGINNER_PERSONA_ORDER = ["founders", "marketing", "sales", "finance-ops", "designers", "consultants"];

/* Current portraits (Esther, 2026-10-08): each instructor's newest cutout on
   their Monet, exactly as the /workshops overview shows them, flattened to one
   3:4 file. The Vivid pages (Code, Cowork) soften the edges in CSS. */
const vividHeadshot = (key: string) => ({
  photo: `/images/instructors/vivid-${key}-800.webp`,
  srcSet: `/images/instructors/vivid-${key}-480.webp 480w, /images/instructors/vivid-${key}-800.webp 800w`,
  sizes: "(max-width: 767px) 86vw, 400px",
});
/* The people as cut-outs in front of one faded painting, the Design page's
   instructor treatment (PR #259). lift-patrick / lift-nechama are their
   current cutouts set on lift-isaac's 4:5 canvas with the same headroom. */
const liftLayer = (key: string) => ({
  cutout: `/images/instructors/lift-${key}-800.webp`,
  cutoutSrcSet: `/images/instructors/lift-${key}-480.webp 480w, /images/instructors/lift-${key}-800.webp 800w, /images/instructors/lift-${key}-1200.webp 1200w`,
});
const VIVID_INSTRUCTOR_LAYERS = {
  "Isaac Rudansky": liftLayer("isaac"),
  "Patrick Gilbert": liftLayer("patrick"),
  "Nechama Teigman": liftLayer("nechama"),
  "Esther Nadoff": liftLayer("esther"),
  "Esti Nadoff": liftLayer("esther"),
};
const VIVID_HEADSHOTS = {
  "Isaac Rudansky": vividHeadshot("isaac"),
  "Patrick Gilbert": vividHeadshot("patrick"),
  "Nechama Teigman": vividHeadshot("nechama"),
  "Esther Nadoff": vividHeadshot("esther"),
  "Esti Nadoff": vividHeadshot("esther"),
};

/* Cowork's eight cards on Vivid walls (Esther, 2026-10-08): the same plates
   re-shot onto flat Vivid walls, shared with Code where the person is the same.
   `bg` is each plate's own measured wall. A copy, not an edit of
   COWORK_PERSONAS, so other pages using that list do not move. */
const VIVID_PERSONA: Record<string, { bg: string; fg: string }> = {
  founders:      { bg: "#5E1756", fg: "#C8AFF0" }, // Plum / Orchid
  executives:    { bg: "#8C5F13", fg: "#F5F5F7" }, // Gold / Paper
  marketing:     { bg: "#9C0032", fg: "#F3BDAF" }, // Crimson / Pink
  sales:         { bg: "#0F503D", fg: "#BACD2D" }, // Canopy / Lime
  designers:     { bg: "#5E1756", fg: "#C8AFF0" }, // Plum / Orchid
  "finance-ops": { bg: "#293750", fg: "#B4C8E1" }, // Navy / Cloud
  consultants:   { bg: "#0F503C", fg: "#BACD2D" }, // Canopy / Lime
  product:       { bg: "#8C5F13", fg: "#F5F5F7" }, // Gold / Paper
};
const VIVID_PERSONA_FILE: Record<string, string> = { marketing: "marketers" };
const COWORK_PERSONAS_VIVID: Persona[] = COWORK_PERSONAS.map((p) => ({
  ...p,
  ...(VIVID_PERSONA[p.key] ?? {}),
  img: `/images/persona-cards/vivid-code/${VIVID_PERSONA_FILE[p.key] ?? p.key}.webp`,
}));
const BEGINNER_PERSONAS: Persona[] = BEGINNER_PERSONA_ORDER.map((k) => COWORK_PERSONAS_VIVID.find((p) => p.key === k)!).map((p) => ({
  ...p,
  build: BEGINNER_PERSONA_BUILD[p.key],
}));

/* Ask-your-AI card for Code and Cowork (Esther, 2026-10-08): Design's card,
   in the comparison room where the YouTube table was, with the office photo of
   Isaac and Patrick that Isaac picked for Sales. */
const VIVID_ASK_AI = (
  <div>
    <AskAiAboutUs
      embedded
      testId="section-ask-ai-workshops"
      headingId="comparison-heading"
      image={{
        src: "/assets/office/office_walking_conversation-800.webp",
        srcSet:
          "/assets/office/office_walking_conversation-480.webp 480w, /assets/office/office_walking_conversation-800.webp 800w",
        avifSrcSet:
          "/assets/office/office_walking_conversation-480.avif 480w, /assets/office/office_walking_conversation-800.avif 800w, /assets/office/office_walking_conversation.avif 1024w",
        sizes: "(min-width: 1024px) 900px, (min-width: 768px) 1000px, 100vw",
        alt: "Isaac Rudansky and Patrick Gilbert walking through the AdVenture Media office",
        width: 1024,
        height: 683,
      }}
      eyebrow="Don't take our word for it"
      headline="Ask your favorite AI about our live workshops."
      body={
        <>
          Get an unbiased read from ChatGPT, Claude, or Gemini on what these
          workshops are like, who they're for, and what attendees say. Then
          come back and grab a seat.
        </>
      }
    />
  </div>
);

const PREMIUM_LAYOUT_BY_SERIES: Record<string, PremiumLayoutConfig> = {
  // Claude for Design — Cowork-style premium treatment. Requires the design
  // entry in scarcity-config.ts (gated on Isaac's FTC/trust sign-off) for the
  // photo-blend hero's seat picker to render.
  "claude-for-design-workshop": {
    // Same reskin as the Claude Code page (cowork-ds.css), with ds-lav
    // re-pointing the lead family from teal to lavender — this page's painting
    // is Water Lilies and #2B3450 is the colour of that water. Layout and
    // motion are untouched by either class.
    pageClass: "premium-workshop ds-cowork ds-vivid ds-lav",
    hero: {
      eyebrow: (openCount) =>
        openCount >= 2 ? "NEW DATES JUST ADDED"
        : openCount === 1 ? "FINAL SEATS THIS MONTH"
        : "NEW DATES COMING SOON",
      /* Esther, 2026-09-29: the cards should say the times and that it is on Zoom,
         the way Sales does. Formatted from each session's own start/end and
         timezone, so nothing here can go stale when a date moves. */
      showSessionMeta: true,
      /* Esther, 2026-11-03: three date cards at most, one of them sold out. */
      dateCards: 3,
      headline: (
        <>
          {/* Esther, 2026-09-29: the proofreader's insert, the same device Isaac
              put on Sales and the kids page. The word is "interactive" — the
              answer to the objection this page exists to answer, which is that
              AI design looks like AI design. Caret in the gap between "The" and
              "Claude", the word hand-lettered above it the way a red pen marks
              an insert.

              Caret and word are both absolutely positioned inside a 0.14em
              span, so the insert adds no width and can never reflow the
              headline. Because that markup says the sentence in an order nobody
              reads it in, the <h1> takes `headlinePlain` and this tree renders
              aria-hidden beside it. */}
          The{" "}
          <span className="ch-edit-mark">
            <span className="ch-edit-words">
              <span>interactive</span>
            </span>
            <svg className="ch-edit-caret" viewBox="0 0 24 14" fill="none">
              <path
                d="M2 12.4 C 6.5 6.8, 9.4 2.6, 12 2 C 14.7 2.6, 17.6 7.2, 22 12.1"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Claude Design<br />
          Workshop
        </>
      ),
      // What a screen reader hears: the sentence, once, in reading order.
      headlinePlain: "The interactive Claude Design Workshop",
      subtitle:
        "Learn the step-by-step Claude Design workflow, from setting up your design system to creating polished, on-brand work that looks nothing like off-the-shelf AI design.",
      primaryCta: { label: "Reserve a seat", href: "#tickets" },
      secondaryCta: { label: "View curriculum", href: "#learn" },
      // Esther and the clay bot, matted OUT of the studio wall so the hero's
      // painted Water Lilies ground shows behind them — the same shot Isaac has
      // on the Code page, bot on the shoulder and all.
      //
      // Cut by scripts/_esther-bot-hero-cutout.py, which uses a segmentation
      // model rather than the flood fill the previous plate used: this wall is
      // terra stucco and the bot is terra clay, 33 units apart, so no colour
      // tolerance separates them.
      //
      // blendEdges is gone with the backdrop. Its two overlays (ch-fade-top,
      // ch-fade-right) existed to feather the edges of a pasted rectangle into
      // the page; a cutout has no rectangle to feather, and leaving them on
      // would have washed out the painting at the top and right instead.
      imageBase: "/images/claude-design-hero-esther-bot",
      /* No heroAvif: this plate is a cut-out with a transparent background
         and the AVIF encoder available here drops alpha, which painted the
         area around the photo black over the Monet (2026-10-08). The WebP
         keeps its alpha; it is preloaded from server/page-meta.ts. */
    },
    // Esther, 2026-09-29. Design-only; the other three pages keep "Live,
    // hands-on training".
    learnEyebrow: "What You’ll Learn",
    learnHeading: (
      <>
        Design, build, and ship fast, with an instructor by your side.
      </>
    ),
    buildItems: DESIGN_BUILD_ITEMS,
    // Samples leads the page: the work lands before the explanation of how it
    // gets made. Before/After stays behind the accordion, where it reads as
    // proof of the standard rather than a second opening.
    extraBeforeBuild: <SamplesShowcaseSection />,
    extraAfterBuild: <BeforeAfterSection />,
    // Two instructors, not three, so the band loses its eyebrow and says who
    // is teaching rather than announcing that instructors exist.
    instructorEyebrow: null,
    instructorHeading: "Hosted by best-selling educators who design for a living.",
    /* Esther, 2026-10-07: no LinkedIn under the bios, matching Sales (rulebook 4.6). */
    instructorLinkedin: false,
    /* Two portraits instead of Cowork's three, so each renders 434px rather
       than 318px. The shared set stops at 672, which is 1.55x here and visibly
       soft; 800 is 1.84x. The 1200 that would be true 2x costs 449KB for the
       pair against 233KB, for a difference you cannot see inside a 6px frame. */
    instructorPhotoWidths: [480, 800],
    /* Esther + Isaac, 2026-10-08: the people as cut-outs standing in front of
       one large faded Monet band behind both of them (Branch of the Seine
       near Giverny, Mist), instead of a painting cropped into each card.
       lift-* are lifted from the v2 portraits with macOS Vision's subject
       mask, so the whole head is there (the older cutout-* crop the crown). */
    instructorLayers: {
      "Esther Nadoff": {
        cutout: "/images/instructors/lift-esther-800.webp",
        cutoutSrcSet: "/images/instructors/lift-esther-480.webp 480w, /images/instructors/lift-esther-800.webp 800w, /images/instructors/lift-esther-1200.webp 1200w",
      },
      "Isaac Rudansky": {
        cutout: "/images/instructors/lift-isaac-800.webp",
        cutoutSrcSet: "/images/instructors/lift-isaac-480.webp 480w, /images/instructors/lift-isaac-800.webp 800w, /images/instructors/lift-isaac-1200.webp 1200w",
      },
    },
    instructorPhotoSizes: "(max-width: 767px) 86vw, 434px",
    personas: DESIGN_PERSONAS,
    // longArrows swaps the Lucide chevron for the drawn carousel mark, which is
    // part of the reskin. maxCards is NOT — the Code page trims to three
    // features as an editorial call, and Design keeps its full set.
    /* Esther, 2026-10-08: light, with a Canopy accent; dark on dark read heavy. */
    onPromptSection: <OnPromptStudioSection theme="light" ticketHref="#tickets" longArrows />,
    showAttendeesReceive: true,
    /* Esther, 2026-09-29: the Cowork/Sales treatment — the list sits on a card
       over a painting rather than flat on the room's ground. Irises, because
       this page already closes on Water Lilies and a page should not show the
       same plate twice. The plaque names it the way a gallery wall label does. */
    attendeesVariant: "card-art",
    attendeesArt: {
      base: "/images/monet-irises-hero",
      alt: "",
      position: "center 42%",
      artist: "Claude Monet",
      title: "Irises",
      year: "c. 1914\u201317",
      /* the right-hand fade dissolves into a blurred copy of the painting
         rather than into the band's own colour */
      tail: "blur",
      /* no plaque on phones; the banner just fades into the page */
      mobilePlaque: false,
    },
    /* Esther, 2026-10-07: "copy the Sales section". Sales' heading, no button
       (the ticket bar and the closing CTA carry the action), Sales' list. */
    attendeesHeading: "Every attendee walks away with:",
    attendeesCta: false,
    attendeesItems: [
      { label: "Lifetime access to OnPrompt Studio ($799 value)" },
      { label: "Lifetime access to the workshop recording and transcript" },
      { label: "30 days of AdVenture Pro, free" },
      { label: "50% off future workshops and private coaching" },
      { label: "Two weeks of email support after the workshop" },
      { label: "Private Slack community for networking and troubleshooting", comingSoon: true },
    ],
    /* Esther, 2026-10-07: "take out the YouTube section". The room keeps only
       the ask-your-AI panel. */
    comparisonTable: false,
    /* Esther, 2026-10-07: no "Common questions" eyebrow; the heading says it. */
    faqEyebrow: null,
    testimonials: {
      reviewsSeriesSlug: "claude-for-design-workshop",
      docQuotes: [],
    },
    // The cream video bookend (DesignClosingCta) is retired in favour of the
    // Code page's close: the standard CTA over a Monet that drifts as you
    // cross it. Water Lilies rather than Irises — one painting per page.
    /* "Sure, YouTube is free" heads the comparison table, so the ask-your-AI
       band sits directly under it rather than in a room of its own further
       down — same placement Isaac settled on for Sales. */
    afterComparison: (
      <div>
        <AskAiAboutUs
          embedded
          testId="section-ask-ai-workshops"
          headingId="comparison-heading"
          /* Esther, 2026-10-08: the team photo (Esther, Isaac and the bot on
             the Monet backdrop) instead of the office couch shot. */
          image={{
            src: "/images/design-workshop/team-esther-isaac-bot-1400.webp",
            srcSet: "/images/design-workshop/team-esther-isaac-bot-960.webp 960w, /images/design-workshop/team-esther-isaac-bot-1400.webp 1400w, /images/design-workshop/team-esther-isaac-bot-2112.webp 2112w",
            avifSrcSet: "/images/design-workshop/team-esther-isaac-bot-960.avif 960w, /images/design-workshop/team-esther-isaac-bot-1400.avif 1400w, /images/design-workshop/team-esther-isaac-bot-2112.avif 2112w",
            /* object-fit: cover fills a ~640x590 box from a 3:2 photo, so the
               picture renders ~880px wide (wider on tablet, where the box is
               taller), not 640: ask for that, or it gets upscaled and soft. */
            sizes: "(min-width: 1024px) 900px, (min-width: 768px) 1000px, 100vw",
            alt: "Esther Nadoff and Isaac Rudansky with the clay robot on a stool, in front of a painted Monet backdrop",
            width: 2112,
            height: 1408,
          }}
          eyebrow="Don't take our word for it"
          headline="Ask your favorite AI about our live workshops."
          body={
            <>
              Get an unbiased read from ChatGPT, Claude, or Gemini on what these
              workshops are like, who they're for, and what attendees say. Then
              come back and grab a seat.
            </>
          }
        />
      </div>
    ),
    /* Esther, 2026-09-29: the sticky bar reads like Sales' — the workshop's
       name and the next date, nothing else. The "facts" variant it had before
       ran a pipe-separated line of date fragments, the format and the price,
       which is a lot of small type for a bar whose only job is to say what
       this is and when the next one runs. It also drops the price and the
       seat count from the sticky, which is where neither belongs. */
    jumpNavVariant: "name-date",
    finalCtaArt: "/images/monet-waterlilies-hero",
    finalCtaCredit: (
      <>
        <strong>Claude</strong> Monet · Water Lilies · 1906
      </>
    ),
  },
  "claude-cowork-for-beginners-workshop": {
    /* Vivid 2026 (Esther, 2026-10-08): on the system, Navy/Cloud chrome (the
       Cowork card's family on /workshops). See html.ds-cwk-page in cowork-ds.css. */
    pageClass: "premium-workshop ds-cowork ds-vivid ds-cwk",
    hero: {
      /* The matted version of the same Isaac plate, so the hero's Monet shows
         behind him instead of his beige studio wall (Esther, 2026-10-08). */
      imageBase: "/images/claude-cowork-hero-clay-shoulder-cutout",
      showSessionMeta: true,
      dateLine: "inline",
      dateCards: 3,
      eyebrow: (openCount) =>
        openCount >= 2 ? "TWO NEW DATES JUST ADDED"
        : openCount === 1 ? "FINAL DATE THIS MONTH — SEATS OPEN"
        : "NEW DATES COMING SOON",
      headline: (
        <>
          The Ultra-Spectacular{" "}
          <span
            style={{
              fontFamily: "var(--font-hand)",
              fontWeight: 700,
              fontSize: "clamp(15px, 0.42em, 28px)",
              lineHeight: 1.05,
              color: "var(--pg-chrome, var(--terra))",
              display: "inline-block",
              verticalAlign: "middle",
              transform: "rotate(-3deg)",
              whiteSpace: "nowrap",
            }}
          >
            (their words,
            <br />
            not ours)
          </span>{" "}
          Claude Cowork for Beginners Workshop
        </>
      ),
      subtitle: "Master Anthropic's most widely adopted tool. It's changing how people work across the world.",
      primaryCta: { label: "Reserve a seat", href: "#tickets" },
      secondaryCta: { label: "View curriculum", href: "#learn" },
    },
    learnHeading: (
      <>
        {/* No aside, as on Sales and Design (Esther, 2026-10-08). */}
        Accomplish everything on this list in just 3.5 hours, with a live instructor by your side.
      </>
    ),
    buildItems: COWORK_BUILD_ITEMS,
    personas: COWORK_PERSONAS_VIVID,
    /* The same OnPrompt section as Design and Code, on Paper, in place of the
       older flat value stack. */
    onPromptSection: <OnPromptStudioSection theme="light" ticketHref="#tickets" longArrows maxCards={3} />,
    showAttendeesReceive: true,
    attendeesVariant: "card-art",
    attendeesArt: {
      base: "/images/monet-irises-hero",
      alt: "",
      position: "center 42%",
      artist: "Claude Monet",
      title: "Irises",
      year: "c. 1914\u201317",
      tail: "blur",
      mobilePlaque: false,
    },
    attendeesHeading: "Every attendee walks away with:",
    attendeesCta: false,
    attendeesItems: [
      { label: "Lifetime access to OnPrompt Studio ($799 value)" },
      { label: "Lifetime access to the workshop recording and transcript" },
      ...(trialWindowOpen()
        ? [
            { label: `${PLUS_NAME}, free through ${trialEndLabel()}` },
            { label: `${MEMBER_WORKSHOP_PERCENT_OFF}% off future workshops as a ${PLUS_NAME} member` },
          ]
        : []),
      { label: "Two weeks of email support after the workshop" },
      { label: "Private Slack community for networking and troubleshooting", comingSoon: true },
    ],
    instructorEyebrow: null,
    instructorLinkedin: false,
    headshotOverrides: VIVID_HEADSHOTS,
    instructorLayers: VIVID_INSTRUCTOR_LAYERS,
    comparisonEyebrow: null,
    /* Esther, 2026-10-08: take out the YouTube section, as on Design. */
    comparisonTable: false,
    afterComparison: VIVID_ASK_AI,
    faqEyebrow: null,
    jumpNavVariant: "name-date",
    testimonials: {
      reviewsSeriesSlug: "claude-cowork-for-beginners-workshop",
      docQuotes: COWORK_DOC_QUOTES,
    },
    finalCtaArt: "/images/monet-seine-mist-hero",
    finalCtaCredit: (
      <>
        <strong>Claude</strong> Monet · Branch of the Seine near Giverny (Mist) · 1897
      </>
    ),
  },
  "claude-code-for-beginners-workshop": {
    // ds-cowork applies the Claude Cowork design system to this page only —
    // palette, three faces, drawn marks, glass dates. Layout and motion are
    // untouched; see client/src/styles/cowork-ds.css.
    pageClass: "premium-workshop ds-cowork ds-vivid ds-code",
    hero: {
      // Isaac matted OUT of his studio backdrop, so the hero's painted Monet
      // ground shows behind him — the same painting the instructor portraits
      // stand against. He used to arrive with his own tan seamless attached,
      // feathered into the paper by .ch-photo's two-mask fade; with a cutout
      // there is no backdrop to feather, and that fade is reduced to the
      // bottom edge only (see cowork-ds.css).
      imageBase: "/images/claude-cowork-hero-clay-shoulder-cutout",
      /* Vivid 2026 (Esther, 2026-10-08): the date cards say the time and Zoom,
         the way Design and Sales do. Always three cards, weekday and date on
         one line (Vivid page, Page components). */
      showSessionMeta: true,
      dateLine: "inline",
      dateCards: 3,
      eyebrow: (openCount) =>
        openCount >= 2 ? "NEW DATES JUST ADDED"
        : openCount === 1 ? "FINAL SEATS THIS MONTH"
        : "NEW DATES COMING SOON",
      headline: (
        <>
          <span style={{ fontWeight: 400 }}>Claude Code</span> for Beginners Workshop{" "}
          {/* The script accent closes the sentence rather than interrupting it, and
              runs a step smaller than before — a margin note on the end of the line,
              not a second subject wedged into the middle of it. */}
          <span
            style={{
              fontFamily: "var(--font-hand)",
              fontWeight: 400,
              fontSize: "clamp(18px, 0.34em, 28px)",
              lineHeight: 0.9,
              color: "var(--pg-chrome, var(--terra))",
              display: "inline-block",
              verticalAlign: "0.08em",
              transform: "rotate(-3deg)",
              whiteSpace: "nowrap",
            }}
          >
            (no code required)
          </span>
        </>
      ),
      subtitle: "Install Claude Code, build something real, and ship it live to the internet, all in one morning. Designed for people who have never written code.",
      primaryCta: { label: "Reserve a seat", href: "#tickets" },
      secondaryCta: { label: "View curriculum", href: "#learn" },
    },
    learnHeading: (
      <>
        {/* No crayon aside, as on Sales and Design (Esther, 2026-10-08). */}
        Walk out having built and shipped all of this, with a live instructor beside you the whole way.
      </>
    ),
    buildItems: CODE_BUILD_ITEMS,
    personas: CODE_PERSONAS,
    // The pinned OnPrompt section — its left column sticks while the cards
    // scroll past — rather than the flat value stack.
    // theme="light" so the section ships its DARK OnPrompt logo and its own
    // light-ground borders — restyling in CSS alone would have left the white
    // logo invisible on paper.
    /* Vivid 2026 (Esther, 2026-10-08): OnPrompt on Paper with one accent,
       as on Design. */
    onPromptSection: <OnPromptStudioSection theme="light" ticketHref="#tickets" longArrows maxCards={3} />,
    showAttendeesReceive: true,
    /* The Sales/Design attendees band: a painting sharp then blurred, the
       frosted card, the house plaque, no button. A different Monet from the
       closing Irises, because a page never shows the same plate twice. */
    attendeesVariant: "card-art",
    attendeesArt: {
      /* Esther, 2026-10-08: a different Monet from the Grainstack. Water
         Lilies reads green, the page's own family, and is not the closing
         Irises. */
      base: "/images/monet-waterlilies-art",
      alt: "",
      position: "center 46%",
      width: 1600,
      height: 1537,
      artist: "Claude Monet",
      title: "Water Lilies",
      year: "1906",
      tail: "blur",
      mobilePlaque: false,
    },
    attendeesHeading: "Every attendee walks away with:",
    attendeesCta: false,
    attendeesItems: [
      { label: "Lifetime access to OnPrompt Studio ($799 value)" },
      { label: "Lifetime access to the workshop recording and transcript" },
      ...(trialWindowOpen()
        ? [
            { label: `${PLUS_NAME}, free through ${trialEndLabel()}` },
            { label: `${MEMBER_WORKSHOP_PERCENT_OFF}% off future workshops as a ${PLUS_NAME} member` },
          ]
        : []),
      { label: "Two weeks of email support after the workshop" },
      { label: "Private Slack community for networking and troubleshooting", comingSoon: true },
    ],
    /* Eyebrows only where they add something the heading doesn't say. */
    instructorEyebrow: null,
    instructorLinkedin: false,
    headshotOverrides: VIVID_HEADSHOTS,
    instructorLayers: VIVID_INSTRUCTOR_LAYERS,
    comparisonEyebrow: null,
    /* Esther, 2026-10-08: take out the YouTube section, as on Design. */
    comparisonTable: false,
    afterComparison: VIVID_ASK_AI,
    faqEyebrow: null,
    jumpNavVariant: "name-date",
    testimonials: {
      reviewsSeriesSlug: "claude-code-for-beginners-workshop",
      docQuotes: [],
    },
    finalCtaArt: "/images/monet-irises-hero",
    finalCtaCredit: (
      <>
        <strong>Claude</strong> Monet · Irises · c. 1914–17
      </>
    ),
  },

  /* Claude for Beginners (Esther, 2026-10-29: "just mimic what we have for the
     cowork page, but call it The Claude for Beginners Workshop, and there are
     multiple dates"). A copy of the Cowork config: same page class, hero plate,
     date strip, OnPrompt band, attendees band, Ask-your-AI card and closing
     painting. The content (curriculum, agenda, FAQ) is this workshop's own.
     No reviews: the Cowork reviews belong to that workshop.
     ds-cfb (Esther, 2026-10-29): the Vivid orange (Sunset) as the accent and
     the orange Monet, Stacks of Wheat (Sunset, Snow Effect), behind the hero. */
  "claude-for-beginners-workshop": {
    pageClass: "premium-workshop ds-cowork ds-vivid ds-cwk ds-cfb",
    hero: {
      /* Isaac with the silver mini Thinker on his shoulder, in place of the
         Cowork clay bot (Esther, 2026-10-29). Same framing as the Cowork plate. */
      imageBase: "/images/claude-beginners-hero-isaac",
      showSessionMeta: true,
      dateLine: "inline",
      /* Three cards: the last sold-out class, then the next two open dates
         (Esther, 2026-11-03). Sold-out dates come from the Cowork series this
         one replaced (PAST_SERIES_ALIASES in server/storage.ts). */
      dateCards: 3,
      soldOutCards: 1,
      /* No eyebrow (Esther: "new dates just added... I don't know if we want that"). */
      eyebrow: () => "",
      headline: <>The Claude for Beginners Workshop</>,
      /* Third person, about the attendee: what they leave with, then who
         teaches it (Esther, 2026-10-29). */
      subtitle:
        /* "Best-selling" rests on his Udemy course (shared/instructors.ts:
           237,000+ students); Esther, 2026-10-29: don't name Udemy here. */
        /* Esther, 2026-11-02: "#1 bestselling"; client names not bold. */
        "Learn to use Claude for real work in one live, hands-on morning. Join #1 bestselling instructor Isaac Rudansky for the same practical training he brings to enterprise teams at Chase, ESPN, and T-Mobile, now open to everyone.",
      primaryCta: { label: "Reserve your seat", href: "#tickets" },
      secondaryCta: { label: "View curriculum", href: "#curriculum" },
    },
    learnHeading: (
      <>
        Accomplish everything on this list in just 3 hours, with a live instructor by your side.
      </>
    ),
    buildItems: BEGINNER_BUILD_ITEMS,
    personas: BEGINNER_PERSONAS,
    onPromptSection: <OnPromptStudioSection theme="light" ticketHref="#tickets" longArrows maxCards={3} />,
    showAttendeesReceive: true,
    attendeesVariant: "card-art",
    attendeesArt: {
      base: "/images/monet-grainstack-hero",
      alt: "",
      position: "center 50%",
      artist: "Claude Monet",
      title: "Grainstack (Sunset)",
      year: "1891",
      tail: "blur",
      mobilePlaque: false,
    },
    attendeesHeading: "Every attendee walks away with:",
    /* Reviews band (Esther, 2026-11-02, "add the testimonials section from the
       other workshop pages"). No cohort of this workshop has run yet, so: only
       reviews with a known source (Google, or emailed to Isaac), labelled as
       coming from our Claude workshops. The TestimonialsMarquee-era batch has
       unclear provenance and stays off. */
    reviewsEyebrow: "Reviews from our Claude workshops",
    testimonials: {
      reviewsSeriesSlug: "claude-for-beginners-workshop",
      docQuotes: [],
      reviewNames: [
        "Anna Holder", "Ben Diamond", "Ambassador Georgette Mosbacher", "Craig Underwood", "Daniel Miller",
        "Grant McKelvey", "Wendy McCulloch", "Angus Rogers", "Jack Puderbeutel", "Caroline Clock", "Samuel Gluzberg",
      ],
    },
    attendeesCta: false,
    attendeesItems: [
      { label: "Lifetime access to OnPrompt Studio ($799 value)" },
      { label: "Lifetime access to the workshop recording and transcript" },
      ...(trialWindowOpen()
        ? [
            { label: `${PLUS_NAME}, free through ${trialEndLabel()}` },
            { label: `${MEMBER_WORKSHOP_PERCENT_OFF}% off future workshops as a ${PLUS_NAME} member` },
          ]
        : []),
      { label: "Two weeks of email support after the workshop" },
      { label: "A certificate of completion, signed by Isaac" },
    ],
    instructorEyebrow: null,
    instructorLinkedin: false,
    headshotOverrides: VIVID_HEADSHOTS,
    instructorLayers: VIVID_INSTRUCTOR_LAYERS,
    comparisonEyebrow: null,
    comparisonTable: false,
    afterComparison: VIVID_ASK_AI,
    faqEyebrow: null,
    jumpNavVariant: "name-date",
    /* Closes on the same orange Stacks of Wheat the hero opens on. */
    finalCtaArt: "/images/monet-wheat-sunset-hero",
    finalCtaCredit: (
      <>
        <strong>Claude</strong> Monet · Stacks of Wheat (Sunset, Snow Effect) · 1890–91
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // AI VIDEO WORKSHOP FOR BEGINNERS — Dave Spector (2026-10-23)
  // Built on the Cowork page wholesale: `ds-cwk` brings every Vivid rule that
  // page has (hero wash, learn cards, instructor band, persona cards), and
  // `ds-vid` re-points only the chrome family and the painting. Crimson
  // chrome, because the Grainstack in fog reads warm pink. One Monet for hero
  // and close, as Cowork does with the Seine; Water Lilies on the attendees
  // card so the page never shows the same plate side by side.
  //
  // No testimonials and no reviews band on purpose: no video cohort has run,
  // and the untagged reviews in workshop-reviews.ts are Claude workshop
  // reviews. No OnPrompt band and no ask-your-AI panel either, both of which
  // speak about the Claude workshops.
  //
  // Date, price and the events row are Isaac's. Until the row exists, preview
  // with ?previewDates=1 on a dev server (see PREVIEW_STUB_SERIES).
  // ═══════════════════════════════════════════════════════════════════════════
  "ai-video-workshop-for-beginners": {
    pageClass: "premium-workshop ds-cowork ds-vivid ds-cwk ds-vid",
    hero: {
      /* Dave's lift cutout set on the Cowork hero's 1600x893 canvas, same
         headroom and right-hand placement, so the Monet shows behind him. */
      imageBase: "/images/ai-video-hero-dave-cutout",
      /* Phones get their own square plate (Esther, 2026-10-26): the mobile
         box is near-square and pins the wide plate to its right edge, which
         left Dave off-centre with his head under the nav. */
      imageBaseMobile: "/images/ai-video-hero-dave-cutout-m",
      /* Dave, 2026-10-29: the AI turn-and-clap clip made him look off, so the
         hero went back to the still photo. 2026-10-30: his own clip (smile,
         then points at camera), cut to a centred square and played once. */
      introVideo: {
        src: "/videos/ai-video-workshop/hero-dave-point.mp4",
        srcSmall: "/videos/ai-video-workshop/hero-dave-point-640.mp4",
        poster: "/images/ai-video-workshop/hero-dave-point-poster.webp",
      },
      showSessionMeta: true,
      dateLine: "inline",
      eyebrow: (openCount) =>
        /* Esther, 2026-10-26: a brand-new series, so say that rather than
           "new dates". */
        openCount >= 1 ? "NEW WORKSHOP SERIES" : "NEW DATES COMING SOON",
      headline: (
        <>
          AI Video Workshop for Beginners{" "}
          {/* The margin note closes the line, as on Code. */}
          <span
            style={{
              fontFamily: "var(--font-hand)",
              fontWeight: 400,
              fontSize: "clamp(18px, 0.34em, 28px)",
              lineHeight: 0.9,
              color: "var(--pg-chrome, var(--terra))",
              display: "inline-block",
              verticalAlign: "0.08em",
              transform: "rotate(-3deg)",
              whiteSpace: "nowrap",
            }}
          >
            (no filmmaking background required)
          </span>
        </>
      ),
      headlinePlain: "AI Video Workshop for Beginners (no filmmaking background required)",
      /* Esther, 2026-10-29: lead with live + feedback, the edge no recorded
         AI-video course in the Meta Ad Library offers.
         Esther, 2026-11-04: credits line added so ad clicks see the $100 up top. */
      subtitle:
        "A live, hands-on Zoom workshop with a 2026 Webby-nominated director. Make a first draft of your own AI commercial in four hours, with feedback on your work. Includes $100 in AI video credits.",
      primaryCta: { label: "Reserve a seat", href: "#tickets" },
      secondaryCta: { label: "View curriculum", href: "#learn" },
    },
    learnHeading: (
      <>
        {/* Esther, 2026-10-28: shorter, centred over the scroller (was Dave's
            "Go from a blank prompt to a first draft of your own commercial
            or short film in four hours."). */}
        From blank prompt to first draft in four hours.
      </>
    ),
    buildItems: VIDEO_BUILD_ITEMS,
    /* Esther, 2026-10-28: Pika's side-by-side scroll in place of the stacked
       cards. */
    buildScroller: true,
    // The work first, then how it gets made, as on Design.
    extraBeforeBuild: <SampleWorkSection />,
    personas: VIDEO_PERSONAS,
    /* Esther, 2026-10-28: the cards float in as the row arrives. */
    personaFloatIn: true,
    /* Esther, 2026-10-29: the business lane recorded courses leave open is
       now the heading itself (replaced the AI-slop line). */
    personaHeading: "Make ads for your own brand without booking a shoot.",
    showAttendeesReceive: true,
    attendeesVariant: "card-art",
    attendeesArt: {
      base: "/images/monet-waterlilies-art",
      alt: "",
      position: "center 46%",
      width: 1600,
      height: 1537,
      artist: "Claude Monet",
      title: "Water Lilies",
      year: "1906",
      tail: "blur",
      mobilePlaque: false,
    },
    attendeesHeading: "Every attendee walks away with:",
    attendeesCta: false,
    /* Esther, 2026-10-28: more on the list. Every line is something the page
       already promises (FAQ + curriculum). The certificate is real: the series
       has an entry in shared/certificate-config.ts. */
    attendeesItems: [
      /* Dave, 2026-10-31: the first draft leads ("they can't walk away with
         me"), then live feedback. Credits: $100 per student, 2026-11-04 (was $25 from fal).
         Provider not named on purpose: they are not a sponsor. */
      { label: "A first draft of your own commercial or short film" },
      { label: "Live feedback on your work from Dave during the session" },
      { label: "$100 in AI video credits" },
      /* Dave, 2026-10-29: the three lines below are his wording. */
      { label: "Make AI images and video clips throughout the session" },
      { label: "A character you can keep consistent from shot to shot" },
      { label: "A certificate of completion" },
      { label: "Hands-on time in a variety of top video, image and voiceover models" },
      { label: "Starter assets and a partner to build with (unless you prefer to work alone)" },
      { label: "Lifetime access to the workshop recording and transcript" },
      { label: "Two weeks of email support after the workshop" },
    ],
    /* Esther, 2026-10-26: Dave in motion in a Monet scene, his name on a
       floating card, in place of the shared portrait row. */
    instructorOverride: <DaveInstructorSection />,
    faqEyebrow: null,
    jumpNavVariant: "name-date",
    finalCtaArt: "/images/monet-grainstack-hero",
    finalCtaCredit: (
      <>
        <strong>Claude</strong> Monet · Grainstack (Sun in the Mist) · 1891
      </>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CLAUDE FOR SALES — sibling of the Claude Code page
  // Built against the Claude Code page at commit 97dc0d8e, and it inherits that
  // page's architecture wholesale: same section order, same components, same
  // container measure, same 10px radius, same glass construction, same drawn
  // buttons and painted marks, same scroll timings. Nothing here restyles a
  // component. The only per-page differences are WHICH TONE-ON-TONE FAMILY
  // lands in each room, and the painting that closes it.
  //
  // The accent sequence and the adjacency rules it obeys are documented in
  // cowork-ds.css under "SALES — THE ACCENT SEQUENCE". Short version: all five
  // families appear, gold appears once, lavender appears once and never beside
  // slate, and plum is always flanked by paper and never touches teal.
  //
  // Content authority is Ronnie's curriculum brief:
  // claude.ai/code/artifact/af9bbfa3-cd51-4506-bbdb-b1e8e753f7a9
  // Its one hard rule: never the word "templates".
  //
  // Isaac, 2026-09-09 review: this page now HAS a scarcity-config entry, where it
  // deliberately had none. Cowork's seat clock is on, plus one synthetic sold-out
  // past date (Tue 2026-09-08), so the strip reads three cards wide via hero.dateCards.
  // ═══════════════════════════════════════════════════════════════════════════
  "claude-for-sales-teams-workshop": {
    pageClass: "premium-workshop ds-cowork ds-vivid ds-sales",
    hero: {
      // Dates, never numbers.
      /* Esther, 2026-09-07: "two dates open for enrollment take that out and
         say live hands ons workshop". The count was doing the date cards' job —
         they list the dates right underneath, so saying how many there are is a
         caption for something already on screen. The eyebrow is the one line
         above the product name, and what a buyer needs from it is what this IS:
         live and hands-on, not a recording and not a lecture.

         Still a function of openCount, because the signature is shared with the
         other three pages and because a page with nothing open must not promise
         a live workshop it has no date for. */
      eyebrow: (openCount) =>
        openCount >= 1 ? "LIVE, HANDS-ON WORKSHOP" : "NEW DATES COMING SOON",
      // Ronnie + Brigid + the clay mascot; pipeline in scripts/_sales-hero-*.
      imageBase: "/images/claude-sales-hero-studio-v9",
      // A separate MOBILE composition: centred pair, no baked left fade. The
      // desktop plate has that fade in its pixels, which on a phone (where the
      // mask is bottom-only) reads as a white wedge across the picture.
      imageBaseMobile: "/images/claude-sales-hero-studio-v9-m",
      heroAvif: true,
      /* Esther, 2026-09-07: "same structure as the augusty 18, spet 15.... and
         information". So the open cards keep the sold-out card's shape exactly —
         small TUESDAY over a big Sep 15 — and the time and platform are added
         BELOW that rather than folding the day into the date line. The kicker
         that briefly sat on top of each card moved up to the hero eyebrow, where
         it gets said once instead of three times.

         No copy in this flag: "10:00 AM to 1:30 PM EDT" and "on Zoom" are
         formatted from the session's own start/end and the event row's
         `platform`, so moving a date cannot leave a wrong time on a card. */
      showSessionMeta: true,
      // ── THE PROOFREADER'S INSERT ─────────────────────────────────────────
      // Isaac, 2026-09-09: "Do the up-triangle insert thing we have on Claude
      // for Kids here: the ^practical Claude for Sales Teams Workshop. Remove
      // the other Crayonize text." So the crayon aside ("on your pipeline, not
      // demo data") is gone, and the one hand-lettered mark left in this hero is
      // the insert itself: a caret in the gap between "The" and "Claude" with
      // "practical" written above it. Same construction as the kids page
      // (.kids-edit-* in index.css), ported to .ds-sales .ch-edit-* at the end
      // of cowork-ds.css rather than reused, so neither page can restyle the
      // other.
      //
      // Caret and word are both absolutely positioned inside a 0.14em span, so
      // the insert adds no width and can never reflow the headline. That markup
      // says the sentence in an order nobody reads it in, so the <h1> takes
      // `headlinePlain` and this tree renders aria-hidden beside it — see
      // CoworkHero.headlinePlain.
      headline: (
        <>
          {/* The canonical name, not a paraphrase of it: "The Claude for Sales
              Teams Workshop" is what the event row, server/page-meta.ts and the
              <title> all say. An earlier pass here dropped "Teams", which split
              the h1 from the name the page is indexed under. */}
          The{" "}
          <span className="ch-edit-mark">
            <span className="ch-edit-words">
              <span>practical</span>
            </span>
            <svg className="ch-edit-caret" viewBox="0 0 24 14" fill="none">
              <path
                d="M2 12.4 C 6.5 6.8, 9.4 2.6, 12 2 C 14.7 2.6, 17.6 7.2, 22 12.1"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span style={{ fontWeight: 400 }}>Claude for Sales Teams</span> Workshop
        </>
      ),
      // What a screen reader hears: the sentence, once, in reading order.
      headlinePlain: "The practical Claude for Sales Teams Workshop",
      /* Isaac cut this to a punchline on 2026-09-09: the old line spent its
         first sentence on the benefit before naming the product. This one names
         the product and the objection. Esther's six-pass version, and the
         reasoning behind each pass, is in git history on this line. */
      subtitle:
        "Build a Claude-powered system that handles prospecting, proposals, follow-ups, sequences, and automations. Zero code required.",
      primaryCta: { label: "Reserve a seat", href: "#tickets" },
      secondaryCta: { label: "View curriculum", href: "#learn" },
      // Isaac, 2026-09-09: the weekday belongs on the same line as the date,
      // and the strip is always three cards now that Cowork's seat clock is on.
      dateLine: "inline",
      dateCards: 3,
    },
    /* Isaac, 2026-09-09: the heading should say what happens in the room rather
       than name the artefact you leave with. Esther's "Build your own AI sales
       agent", and the four headings before it, are in git history. */
    learnHeading: <>Live, step-by-step instruction in the tools and techniques of an AI-powered sales team.</>,
    buildItems: SALES_BUILD_ITEMS,
    /* 2026-09-09: this page's CSS already opens every panel and hides the
       toggle (`.ds-cowork #learn .cba-row > div` in cowork-ds.css), so the
       accordion was measuring six scrollHeights on mount for values the
       stylesheet immediately overrode. Say so in the props instead. */
    buildAlwaysOpen: true,
    /* The pinned plum "You'll actually learn Claude" scene is OUT (Esther,
       2026-08-31). SalesFluencySection is still exported and still carries the
       scroll-scrub machinery, so putting it back is one line — but it renders
       nowhere now, and the .ds-sales #fluency rules in cowork-ds.css are
       consequently dormant rather than wrong. */
    extraAfterBuild: <SalesArtifactGallery />,
    personas: SALES_PERSONAS,
    // Isaac, 2026-09-09 review: the OnPrompt Studio band is off this page entirely.
    // It is a partner offering, and it was the longest detour between the curriculum
    // and the price. The Code and Cowork pages keep theirs; git history has the dark
    // three-feature version this page ran.
    reviewsEyebrow: "Reviews from our Claude workshops",
    // Isaac, 2026-09-09: name the deals they close, not the pipeline they run.
    instructorHeading:
      "Hosted by sellers who close Fortune 100 clients, seven-figure contracts, and everything in between.",
    /* The v2 set (Esther, 2026-09-02): the same three people, re-shot seated with
       far bolder wardrobe, composited onto their Monets by
       scripts/_sales-instructor-v2-monet.py. The v1 files are still on disk, so
       reverting is a matter of deleting these three lines. */
    /* RONNIE AND BRIGID ONLY, and through main's own monetV2() so all six
       portraits are built one way.
       Isaac needs nothing here any more: ABOUT_HEADSHOTS_DSCOWORK gives every
       ds-cowork page monetV2("isaac"), and this page is one. Ronnie and Brigid
       are not in that map — they front no other page — so this is where they get
       v2. main generated all six files in #148, which is why this branch's own
       composites and _sales-instructor-v2-monet.py are gone: two pipelines
       writing the same filenames is a trap, and main's is the shipped one. */
    headshotOverrides: {
      "Ronnie Cardno": monetV2("ronnie"),
      "Brigid Healy": monetV2("brigid"),
    },
    showAttendeesReceive: true,
    /* Isaac, 2026-09-09: the Cowork-style card-over-painting treatment. Grainstack
       rather than the Seine, which the closing CTA on this page already uses. */
    attendeesVariant: "card-art",
    attendeesArt: {
      base: "/images/monet-grainstack-hero",
      alt: "",
      position: "left 40%",
      /* Isaac, 2026-09-09 (second pass): "Put the Claude Monet Grainstack (Sun
         in the Mist) 1891 in an interesting, more artistic frame in the bottom
         left-hand corner, almost like a plaque for that artwork." The same three
         facts the right-aligned credit line carried, split so the plaque can set
         each the way a museum wall label does. */
      artist: "Claude Monet",
      title: "Grainstack (Sun in the Mist)",
      year: "1891",
      /* Isaac, 2026-09-17: the right-hand fade dissolves into a blurred copy
         of the painting instead of the band's teal. */
      tail: "blur",
      /* Isaac, 2026-09-17: no plaque on phones; the banner just fades into
         the page. */
      mobilePlaque: false,
    },
    /* Isaac, 2026-09-09: shorter, and it has to hold one line. The old sentence
       ("...with more than just the workshop training") spent its second half
       arguing with itself; the colon does that job. */
    attendeesHeading: "Every attendee walks away with:",
    /* Isaac, 2026-09-17: "no button, just the bullets". The ticket bar and
       the closing CTA carry the reserve action on this page. */
    attendeesCta: false,
    /* Six items, but not the shared six. The Roger agent and the Claude recipe
       generator are not part of this seat, so listing them here would promise
       something the buyer does not get. Order is Isaac's: the priced item
       first, then what the seat itself keeps, then the support around it. The
       other pages keep the default list.

       Isaac, 2026-09-17: "add 30 days free of AdVenture Pro and 50% off future
       workshops and private coaching." Slotted after the recording so the
       coming-soon Slack line stays last. Now AdVenture Plus, and only while
       the free window is open. */
    attendeesItems: [
      { label: "Lifetime access to OnPrompt Studio ($799 value)" },
      { label: "Lifetime access to the workshop recording and transcript" },
      // The free window is a fixed calendar date, so these two lines are
      // only true while it's open (Patrick, 2026-09-30: no free-month codes
      // for workshop buyers after it closes).
      ...(trialWindowOpen()
        ? [
            { label: `${PLUS_NAME}, free through ${trialEndLabel()}` },
            { label: `${MEMBER_WORKSHOP_PERCENT_OFF}% off future workshops as a ${PLUS_NAME} member` },
          ]
        : []),
      { label: "Two weeks of email support after the workshop" },
      { label: "Private Slack community for networking and troubleshooting", comingSoon: true },
    ],
    // Kept for section parity. It renders once this series has published
    // reviews; until then the section absents itself rather than inventing
    // quotes for a cohort that has not run this curriculum yet.
    testimonials: {
      reviewsSeriesSlug: "claude-for-sales-teams-workshop",
      docQuotes: [],
    },
    /* Vivid pass (Esther, 2026-10-08): the heading says it; no eyebrow. */
    personaEyebrow: "",
    /* Isaac, 2026-09-17: "remove Honest comparison eyebrow". */
    comparisonEyebrow: null,
    /* Vivid pass (Esther, 2026-10-08): the YouTube table comes out, as on
       Design, Code and Cowork; the room keeps the Ask-your-AI card. */
    comparisonTable: false,
    /* The people as cut-outs in front of one faded Monet, the Design page's
       instructor band; no eyebrow over the heading. */
    instructorEyebrow: null,
    instructorLayers: {
      "Ronnie Cardno": {
        cutout: "/images/instructors/lift-ronnie-800.webp",
        cutoutSrcSet: "/images/instructors/lift-ronnie-480.webp 480w, /images/instructors/lift-ronnie-800.webp 800w, /images/instructors/lift-ronnie-1200.webp 1200w",
      },
      "Brigid Healy": {
        cutout: "/images/instructors/lift-brigid-800.webp",
        cutoutSrcSet: "/images/instructors/lift-brigid-480.webp 480w, /images/instructors/lift-brigid-800.webp 800w, /images/instructors/lift-brigid-1200.webp 1200w",
      },
      "Isaac Rudansky": {
        cutout: "/images/instructors/lift-isaac-800.webp",
        cutoutSrcSet: "/images/instructors/lift-isaac-480.webp 480w, /images/instructors/lift-isaac-800.webp 800w, /images/instructors/lift-isaac-1200.webp 1200w",
      },
    },
    /* Isaac, 2026-09-17: "remove isaac's linkedin". */
    instructorLinkedin: false,
    /* Isaac, 2026-09-09: the heading asks the question and the room becomes the
       plum brush plate (styled in cowork-ds.css).

       NO personaSubheading, second pass the same day: "Remove 'It doesn't
       matter what you sell, it matters that you sell' from the right-hand
       side." The prop stays on PremiumLayoutConfig — the other pages may still
       want a second column — but this heading was carrying the whole thought
       already, and the line beside it was a second, quieter version of it. */
    /* Isaac, 2026-09-17: replaces "Should you attend? (your competitors
       probably already have)". The second sentence keeps the muted second
       tone the parenthetical had. */
    personaHeading: (
      <>
        Come in as a total beginner.{" "}
        <span className="pc-yeah">Leave as a Claude-enabled sales genius with superpowers.</span>
      </>
    ),
    personaClassName: "pc-sales-plum",
    /* PLAIN "Workshop agenda" (Esther, 2026-09-08: "The morning / research,
       outreach, follow-ups ... undo this... say Workshop Agenda").

       "The morning" was a mood, and the script aside underneath was a second
       attempt to explain the mood. A reader scanning for a schedule had to get
       past both before finding one. The other three premium pages already use
       the base "Workshop agenda" heading, so this override existed only to be
       cleverer than them; dropping it to the shared default is the fix, and it
       keeps the mixed-weight treatment (300 frames, 500 names) for free.

       Left as an explicit override rather than deleted so the deviation is
       visible: this page deliberately has NO script aside, where the base
       heading carries "what a day to be Ailive". On a page whose subject is
       someone's pipeline, a pun is the wrong register. */
    agendaHeading: (
      <>
        Workshop <strong className="ed-agenda-subject">agenda</strong>
      </>
    ),
    // Grainstack in Mist, one of the eight Monets in the brand library. Irises
    // anchors the Code page and reads teal-green — the family Sales rotates
    // away from — so reusing it would undo the rotation at the last moment.
    //
    // WHY THIS PAINTING, measured rather than chosen by eye. The brief asked
    // for a Monet whose colours run purple, and not one of the eight in the
    // library is: violet pixels (hue 250-330, saturation > 0.14) came out at
    // 0.6% for Grainstack, 0.5% for Weeping Willow and <=0.1% for the rest.
    // Grainstack reads rose and tan, not purple. So this one was added from the
    // same CC0 source the library itself came from (Art Institute of Chicago,
    // id 16564) and measures 1.3% — 2.6x the best of the eight — in a genuine
    // lavender-violet family at hue 245deg. The close's field and type are
    // derived from that hue at the system's own LAVENDER DARK contrast ratios;
    // see `.ds-sales #final-cta` in cowork-ds.css.
    finalCtaArt: "/images/monet-seine-mist-hero",
    finalCtaCredit: (
      <>
        <strong>Claude</strong> Monet · Branch of the Seine near Giverny (Mist) · 1897
      </>
    ),
    /* Isaac, 2026-09-09: "Remove 'Common questions' eyebrow." The heading it
       sits over is "Got questions?", so the label was announcing the section
       twice in three words. */
    faqEyebrow: null,
    /* Isaac, 2026-09-09: "In the dropdown bar on scroll, on the left-hand side,
       we should have the name of the workshop. We don't need the price. Just say
       'September 15th' in the date, like the next upcoming, in a little bit of a
       badge." The facts line was a second ticketing panel in 12px mono; by the
       time a reader has scrolled past the hero they know the format and the
       price is one click away in the drawer the CTA opens. */
    jumpNavVariant: "name-date",
    /* Isaac, 2026-09-17: "bring workshop agenda section above reviews". */
    reviewsAfterAgenda: true,
    /* Isaac, 2026-09-09: "Sure, YouTube is free" already heads the comparison, so
       the ask-your-AI band sits directly under the table rather than in its own
       room further down. Light: on THIS page the comparison sits on paper
       (plum is always flanked by paper, see the accent sequence in cowork-ds.css). */
    afterComparison: (
      <div className="mt-14 md:mt-20">
        <AskAiAboutUs
          embedded
          testId="section-ask-ai-workshops"
          /* Isaac, 2026-09-09: "Instead of 'Do your homework,' say 'Don't take
             our word for it.'" The body used to OPEN on that same sentence, so
             the eyebrow and the first line would have said it twice. The body
             starts on the instruction now and the eyebrow carries the frame. */
          eyebrow="Don't take our word for it"
          headline="Ask your favorite AI about our live workshops."
          body={
            <>
              Get an unbiased read from ChatGPT, Claude, or Gemini on what these workshops are like, who they're for, and what attendees say. Then come back and grab a seat.
            </>
          }
          prompt="I'm considering attending one of AdVenture Media's live AI workshops (adventuremedia.ai/workshops). Tell me about the quality of these workshops, whether one is right for me, what the reviews say, and then send me links to register."
          /* Isaac, 2026-09-09: "Let's get the office image of me and Patrick
             walking in the motion blur." Two people mid-conversation reads as
             the thing the band is asking for (go and ask someone), where the
             desks shot read as stock. The set on disk tops out at the source's
             own 1024px, so there is no -1200 to name: two WebP widths, and the
             AVIF adds the full-size file as its third candidate. */
          image={{
            src: "/assets/office/office_walking_conversation-800.webp",
            srcSet:
              "/assets/office/office_walking_conversation-480.webp 480w, /assets/office/office_walking_conversation-800.webp 800w",
            avifSrcSet:
              "/assets/office/office_walking_conversation-480.avif 480w, /assets/office/office_walking_conversation-800.avif 800w, /assets/office/office_walking_conversation.avif 1024w",
            sizes: "(max-width: 1024px) 100vw, 33vw",
            alt: "Isaac Rudansky and Patrick Gilbert walking through the AdVenture Media office",
            width: 1024,
            height: 683,
          }}
        />
      </div>
    ),
    /* The Claudebot sales-agent chat (Isaac, 2026-09-09): sits after the FAQ so
       the objections it handles are the ones the reader just scrolled through. */
    /* Isaac, 2026-09-17: "lets remove the claude bot chat ... we can bring it
       back later, but for now remove it. It still needs a lot of work." The
       section, its route (server/sales-chat.ts) and the dossier all stay in
       the tree; only this slot is empty. Restore by putting the line back:
         beforeFinalCta: <SalesChatSection />,                                */
    beforeFinalCta: undefined,
  },
};

/* 2026-09-09 — cowork-ds.css is no longer in the global stylesheet; the server
   <link>s it only on the routes that need it, from a plain list it can read
   without importing this (React) module: DS_COWORK_SERIES in
   shared/premium-css-routes.ts. Add `ds-cowork` to a pageClass above and you
   must add that series slug there too. This shouts in dev when they drift. */
if (import.meta.env.DEV) {
  const missing = Object.entries(PREMIUM_LAYOUT_BY_SERIES)
    .filter(([slug, cfg]) => cfg.pageClass.includes("ds-cowork") && !DS_COWORK_SERIES.includes(slug))
    .map(([slug]) => slug);
  if (missing.length) {
    console.error(
      `[premium-css] ${missing.join(", ")} carries ds-cowork but is missing from ` +
        "DS_COWORK_SERIES (shared/premium-css-routes.ts) — its first paint ships unstyled.",
    );
  }
}

function ComparisonMark({ value, drawn = false }: { value: CompareVal; drawn?: boolean }) {
  // Cowork design system: the same hand as the buttons and the section icons,
  // and no box around either mark - the column already reads as a cell.
  //
  // DsIcon paints an aria-hidden <svg>, so on the pages that use the drawn
  // marks (Sales among them) a screen reader heard the row label followed by
  // three empty cells: the entire answer of the comparison was mute. The plain
  // marks below carry role="img" + aria-label; these carry an sr-only word so
  // both variants say the same thing.
  if (drawn && value === "check") {
    return (
      <>
        <DsIcon name="check" size={30} />
        <span className="sr-only">Yes</span>
      </>
    );
  }
  if (drawn && value === "x") {
    return (
      <>
        <DsIcon name="cross" size={26} />
        <span className="sr-only">No</span>
      </>
    );
  }
  if (value === "check") {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-label="Yes" role="img">
        <rect width="18" height="18" fill="var(--indigo)" />
        <path d="M4 9l3.5 3.5L14 6" stroke="var(--cream)" strokeWidth="2.25" strokeLinecap="square" fill="none" />
      </svg>
    );
  }
  if (value === "x") {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-label="No" role="img">
        <rect width="18" height="18" fill="none" stroke="hsla(60, 3%, 8%, 0.3)" />
        <path d="M5 5l8 8M13 5l-8 8" stroke="hsla(60, 3%, 8%, 0.5)" strokeWidth="1.5" strokeLinecap="square" />
      </svg>
    );
  }
  return (
    <span
      className="font-mono uppercase text-charcoal/75"
      style={{ fontSize: "10px", letterSpacing: "1.4px", fontWeight: 500 }}
    >
      Maybe
    </span>
  );
}

function ComparisonTable({
  drawn = false,
  rows = COMPARISON_ROWS,
}: {
  drawn?: boolean;
  /** Per-series row set. Defaults to the shared one; see
   *  COMPARISON_ROWS_BY_SERIES for why Sales needs its own. */
  rows?: typeof COMPARISON_ROWS;
}) {
  const gridCols = "grid-cols-[minmax(0,1.6fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)] md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]";
  return (
    <>
      <p className="type-body-lg text-charcoal/70 leading-relaxed mb-10 max-w-[640px]">
        You could piece this together for free. Most people who try still haven't shipped anything six months later. That's what you're actually paying for.
      </p>

      {/* Roles rather than a real <table>: the layout is a CSS grid whose
          column template is shared between the header strip and every row, and
          `#comparison` is targeted structurally from cowork-ds.css. The roles
          give assistive tech the same row/column navigation a <table> would,
          and change no rendering at all. */}
      <div className="border border-charcoal overflow-hidden" role="table" aria-label="What this workshop gives you, compared with YouTube and self-study">
        <div className={`grid ${gridCols} bg-charcoal`} role="row">
          <div
            role="columnheader"
            className="font-mono uppercase text-cream px-3 py-4 md:px-7 md:py-5 text-left"
            style={{ fontSize: "clamp(11px, 1.1vw, 14px)", letterSpacing: "1.4px", fontWeight: 500 }}
          >
            What you get
          </div>
          {[
            { label: "This workshop", highlight: true },
            { label: "YouTube" },
            { label: "Self-study" },
          ].map((h) => (
            <div
              key={h.label}
              role="columnheader"
              className={`font-mono uppercase px-2 py-4 md:px-4 md:py-5 text-center border-l border-cream/20 ${h.highlight ? "" : "text-cream"}`}
              style={{
                fontSize: "clamp(10px, 1.05vw, 14px)",
                letterSpacing: "1.4px",
                fontWeight: 500,
                color: h.highlight ? "var(--terra-lt)" : undefined,
              }}
            >
              {h.label}
            </div>
          ))}
        </div>

        {rows.map((row, i) => (
          <div
            key={row.label}
            role="row"
            className={`grid ${gridCols} items-stretch ${i > 0 ? "border-t border-charcoal/20" : ""}`}
          >
            <div
              role="rowheader"
              className="px-3 py-4 md:px-7 md:py-5 text-charcoal flex items-center"
              style={{ fontSize: "clamp(14px, 1.25vw, 18px)", lineHeight: 1.45 }}
            >
              {row.label}
            </div>
            <div
              role="cell"
              className="flex items-center justify-center px-2 py-4 md:px-4 md:py-5 border-l border-charcoal/15"
              style={{ backgroundColor: "rgba(217, 64, 0, 0.04)" }}
            >
              <ComparisonMark value={row.us} drawn={drawn} />
            </div>
            <div role="cell" className="flex items-center justify-center px-2 py-4 md:px-4 md:py-5 border-l border-charcoal/15">
              <ComparisonMark value={row.youtube} drawn={drawn} />
            </div>
            <div role="cell" className="flex items-center justify-center px-2 py-4 md:px-4 md:py-5 border-l border-charcoal/15">
              <ComparisonMark value={row.self} drawn={drawn} />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <a
          href="#tickets"
          className="btn btn-primary btn-lg shrink-0"
          onClick={(e) => {
            // OPENS THE CHECKOUT (sheet on mobile, drawer on desktop); anchor-scroll runs only when nothing is bookable.
            if (requestCheckout()) e.preventDefault();
          }}
        >
          Reserve my seat
        </a>
      </div>
    </>
  );
}

function AgendaTimeline({ items }: { items: WorkshopAgendaItem[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start 80%", "end 20%"] });
  // Animate scaleY (GPU-composited transform) rather than height (forces layout
  // + paint every scroll frame) to keep the timeline reveal smooth.
  const lineScaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const sessionTypeLabel: Record<string, string> = { lecture: "Lecture", hands_on: "Hands-on", qa: "Q&A", break: "Break" };
  const sessionTypeColor: Record<string, string> = {
    lecture: "", hands_on: "bg-amber-400/20 text-amber-300",
    qa: "", break: "bg-cream/10 text-cream/40",
  };
  const sessionTypeStyle: Record<string, React.CSSProperties> = {
    lecture: { backgroundColor: "rgba(217, 64, 0, 0.2)", color: "var(--terra-lt)" },
    qa: { backgroundColor: "rgba(217, 64, 0, 0.2)", color: "var(--terra-lt)" },
  };
  return (
    <div ref={containerRef} className="relative">
      <div className="absolute left-4 md:left-[140px] top-0 bottom-0 w-px bg-cream/10 z-10">
        <motion.div className="w-full h-full origin-top" style={{ scaleY: lineScaleY, backgroundColor: "var(--terra)", opacity: 0.6 }} />
      </div>
      <div className="space-y-0">
        {items.map((item, i) => (
          <div key={i} className="relative flex items-start gap-4 md:gap-0 border-b border-cream/5 last:border-b-0">
            <div className="absolute left-4 md:left-[140px] top-12 md:top-12 -translate-x-1/2 z-20">
              <div
                className="w-3 h-3 rounded-[2px] border-2"
                style={
                  item.sessionType === "break"
                    ? { borderColor: "hsla(48, 20%, 92%, 0.2)", backgroundColor: "transparent" }
                    : { borderColor: "var(--terra)", backgroundColor: "rgba(217, 64, 0, 0.3)" }
                }
              />
            </div>
            <div className="hidden md:flex w-[140px] shrink-0 items-start py-10 px-5 pr-8 text-right flex-col">
              <span className="type-label text-cream/80">{item.timeStart}</span>
              <span className="type-nav text-cream/50">{item.timeEnd}</span>
            </div>
            <div className="flex-1 py-10 px-5 md:pl-8 pl-10">
              <span className="md:hidden type-nav text-cream/60 block mb-1">{item.timeStart} – {item.timeEnd}</span>
              <div className="flex items-center gap-3 mb-1">
                <h3 className="type-h4 text-cream">{item.title}</h3>
              </div>
              {item.description && <p className="type-body text-cream/70" style={{ fontSize: "14px" }}>{item.description}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ── PREVIEW STUB FOR A SERIES WITH NO EVENTS ROW YET ─────────────────────────
   The ?previewDates=1 seam in EventDetailPage synthesizes DATES, but it still
   needs the series' events row to exist. A brand-new series has none until
   Isaac creates it (date, price and Stripe are his), so its page could not be
   reviewed at all. For a slug listed here, a 404 from /api/events on a dev
   server or PR preview, with ?previewDates=1, returns this placeholder.

   Same gates as the dates seam: stripped from production builds, explicit
   query param required, and only reached when the real row is missing. The
   tiers mirror the other workshops' live prices (Esther's call, 2026-10-26)
   and carry no Stripe price, so nothing can be bought from a preview. Delete
   a slug from this list once its row exists. */
/* Empty since 2026-10-29: AI Video got its real rows (events 135 + 136). An
   entry may carry `reviewKey` for a private `?review=<key>` link that renders
   on any host, production included, noindexed. */
const PREVIEW_STUB_SERIES: Record<string, { title: string; durationHours: number; platform: string; reviewKey?: string }> = {};

/** Preview dates (ET calendar days) for stub series, so the preview shows
 *  the agreed dates instead of "21 and 49 days from now". */
const PREVIEW_PINNED_DATES: Record<string, string[]> = {};

/** True when the URL carries this series' private review key. */
function hasReviewKey(slug: string): boolean {
  if (typeof window === "undefined") return false;
  const key = PREVIEW_STUB_SERIES[slug]?.reviewKey;
  return !!key && new URLSearchParams(window.location.search).get("review") === key;
}

function previewStubEvent(slug: string): EventWithSeries | null {
  const previewHost =
    typeof window !== "undefined" && /^pr-\d+---/.test(window.location.hostname);
  const review = hasReviewKey(slug);
  if (!import.meta.env.DEV && !previewHost && !review) return null;
  if (typeof window === "undefined") return null;
  if (!review && !new URLSearchParams(window.location.search).has("previewDates")) return null;
  const cfg = PREVIEW_STUB_SERIES[slug];
  if (!cfg) return null;
  // 10 AM ET in standard time (15:00 UTC); the dates seam re-times sessions from this start
  // and length.
  const start = new Date(Date.now() + 21 * 864e5);
  start.setUTCHours(15, 0, 0, 0);
  const end = new Date(start.getTime() + cfg.durationHours * 36e5);
  return {
    id: -1,
    slug,
    seriesSlug: slug,
    title: cfg.title,
    status: "active",
    eventType: "workshop",
    shortDescription: null,
    heroImageUrl: null,
    startDatetime: start,
    endDatetime: end,
    timezone: "America/New_York",
    format: "online",
    locationOnline: null,
    locationAddress: null,
    maxCapacity: null,
    seatsSold: 0,
    audienceDescription: null,
    audienceBullets: null,
    notForBullets: null,
    instructorNote: null,
    recordingAvailable: true,
    cancellationPolicy: null,
    platform: cfg.platform,
    durationDescription: `${cfg.durationHours} hours`,
    scholarshipAvailable: false,
    scholarshipEmail: null,
    seoDescription: null,
    ogImageUrl: null,
    published: false,
    createdAt: new Date(),
    googleCalendarEventId: null,
    googleCalendarId: null,
    googleMeetUrl: null,
    zoomJoinUrl: null,
    zoomMeetingId: null,
    zoomPasscode: null,
    zoomStartUrl: null,
    prepEmailSentAt: null,
    finalReminderSentAt: null,
    recordingUrl: null,
    recordingSyncedAt: null,
    recordingEmailSentAt: null,
    /* Esther, 2026-10-26: "make the price the same as the other workshops
       for now". Mirrors the live Design/Code/Cowork/Sales tiers exactly
       ($799 General Admission, $599 per seat at 3+). Preview only: the real
       price lives on the events row Isaac creates, and nothing here can be
       bought (no stripePriceId). */
    ticketTypes: [
      {
        id: -1,
        eventId: -1,
        label: "General Admission",
        priceCents: 79900,
        description: "Four-hour live workshop · Hands-on AI image and video exercises · Full recording access · 2 weeks personal email support",
        maxQty: 3,
        stripePriceId: null,
        earlyBirdPriceCents: null,
        earlyBirdDeadline: null,
        displayOrder: 0,
      },
      {
        id: -2,
        eventId: -1,
        label: "Team",
        priceCents: 59900,
        description: "Everything in General Admission · Flexible seat count (3+) · Invoice/PO billing available",
        maxQty: 20,
        stripePriceId: null,
        earlyBirdPriceCents: null,
        earlyBirdDeadline: null,
        displayOrder: 1,
      },
    ],
    seriesSessions: [],
    pastSessions: [],
  };
}

function EventDetailPage() {
  // Match both the canonical /workshops/:slug and the legacy /events/:slug
  // path (kept registered so direct hits work; Cloudflare 301s most of them).
  const [, paramsWorkshops] = useRoute("/workshops/:slug");
  const [, paramsEvents] = useRoute("/events/:slug");
  const slug = paramsWorkshops?.slug || paramsEvents?.slug || "";
  const [, setLocation] = useLocation();

  // Redirect mapped old slugs to their canonical new slug (preserving query
  // string). Always lands on /workshops/<canonical> regardless of which
  // route the visitor came in on.
  useEffect(() => {
    if (!slug) return;
    const target = getEventRedirect(slug);
    if (target) {
      const search = typeof window !== "undefined" ? window.location.search : "";
      setLocation(`/workshops/${target}${search}`, { replace: true });
    }
  }, [slug, setLocation]);

  const hasStaticLogos = (getWorkshopContent(slug)?.brandLogos?.length ?? 0) > 0;
  const { data: allClients } = useClients({ enabled: !hasStaticLogos });
  const clientLogosFallback = (allClients || [])
    .filter(
      c =>
        c.logoUrl &&
        c.name !== "1st Gig" &&
        c.name !== "A Cottage in the City" &&
        c.name !== "AD Trophy",
    )
    .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))
    .slice(0, 15)
    .map(c => ({ name: c.name, src: toWebpUrl(c.logoUrl) ?? c.logoUrl! }));

  /* 2026-09-09: the reviews section used to mount BOTH breakpoints and let
     `hidden sm:block` / `sm:hidden` decide which one you saw, so every desktop
     visitor carried the mobile carousel's 443 elements in the DOM (and every
     phone carried the tri-row marquee). One of them is mounted now. 640 is the
     Tailwind `sm` breakpoint those two classes already used — the classes stay
     on as the visual gate, so the two can never both be visible. SSR-safe:
     useIsNarrow starts false on the server AND on the first client render, so
     hydration matches; the section is far below the fold, well past the frame
     the effect needs to settle. */
  const reviewsNarrow = useIsNarrow(640);

  const heroRef = useRef<HTMLDivElement>(null);
  // 2026-09-09: `jumpNavVisible` used to live here. Page-level state flipped by
  // the hero observer re-rendered this entire component on every hero crossing;
  // JumpNav owns that observer and that state now.
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  /* 2026-09-09 — what was wrong here: every FAQ answer panel carried an inline
     `ref={(el) => { el.style.maxHeight = open ? el.scrollHeight + "px" : "0px" }}`
     callback. A fresh arrow per render made React detach and re-attach all 12
     refs on EVERY render of this page, and each attach read scrollHeight in
     between style writes — 12 forced layouts interleaved with 12 style writes,
     for a value that can only change when someone clicks. Stable setters below;
     the height is measured once, in toggleFaq. */
  const faqPanelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const faqPanelRefSetters = useRef<((el: HTMLDivElement | null) => void)[]>([]);
  const faqPanelRef = (i: number) => {
    if (!faqPanelRefSetters.current[i]) {
      faqPanelRefSetters.current[i] = (el: HTMLDivElement | null) => {
        faqPanelRefs.current[i] = el;
      };
    }
    return faqPanelRefSetters.current[i];
  };
  const toggleFaq = (i: number) => {
    const next = openFaq === i ? null : i;
    setOpenFaq(next);
    // Imperative, and the inline `maxHeight: 0` in the JSX never changes, so
    // React's style diff has nothing to write back over these values.
    faqPanelRefs.current.forEach((el, idx) => {
      if (el) el.style.maxHeight = idx === next ? el.scrollHeight + "px" : "0px";
    });
  };
  /* The height is now written once per click rather than on every render, so
     the open index must not outlive the answers it was measured against: a
     client-side hop to another workshop remounts the panels at max-height 0
     while `openFaq` still points at an index. Close on slug change. */
  useEffect(() => { setOpenFaq(null); }, [slug]);
  /* Hero-price test. The site splits Meta-ad visitors (META30 in hand) on the
     Code page 50/50 itself, so the ad budget stays in one ad set instead of
     two fixed halves. The arm is sticky per browser (localStorage): a visitor
     who comes back must see the same hero, or one arm leaks into the other.
     ?price=1 / ?price=0 force an arm on any page for QA; forced visits are
     tagged separately (see checkoutUtm) so they never count in the read.
     Read the URL once on mount: re-reading on every render would flip the
     hero if the query string changes under a client-side nav. */
  const [forcedPriceArm] = useState<"A" | "B" | null>(() => {
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams(window.location.search).get("price");
    return p === "1" ? "B" : p === "0" ? "A" : null;
  });

  // Marketing coupon capture (?coupon= / ?promo= / ?code= + 30-day
  // localStorage). The checkout sheet/drawer inside WorkshopTicketBlock owns
  // all other checkout state and validates the code itself — this page only
  // captures and hands it down as `initialCouponCode`.
  const [couponCode] = useState(() => {
    if (typeof window === "undefined") return "";
    const sp = new URLSearchParams(window.location.search);
    const fromUrl = sp.get("coupon") || sp.get("promo") || sp.get("code") || "";

    // Persist discount code from URL to localStorage with 30-day TTL
    if (fromUrl) {
      try {
        localStorage.setItem("workshop_discount_code", JSON.stringify({ code: fromUrl, storedAt: Date.now() }));
      } catch {}
      return fromUrl;
    }

    // Restore from localStorage if no URL param
    try {
      const stored = localStorage.getItem("workshop_discount_code");
      if (stored) {
        const { code, storedAt } = JSON.parse(stored);
        if (Date.now() - storedAt < 30 * 24 * 60 * 60 * 1000) return code;
        localStorage.removeItem("workshop_discount_code");
      }
    } catch {}
    return "";
  });
  // Note: the legacy inline exit popup (showExitPopup / exitEmail / etc.)
  // was removed in favor of ReminderPopup mounted at the router level.

  // Capture UTM params from URL on load
  const [utmParams] = useState(() => {
    if (typeof window === "undefined") return {};
    const sp = new URLSearchParams(window.location.search);
    return {
      utm_source: sp.get("utm_source") || undefined,
      utm_medium: sp.get("utm_medium") || undefined,
      utm_campaign: sp.get("utm_campaign") || undefined,
      utm_content: sp.get("utm_content") || undefined,
    };
  });

  // Email from URL — pre-fills the Stripe checkout email field when the
  // visitor was already captured (e.g. from the reminder popup).
  const [prefillEmail] = useState(() => {
    if (typeof window === "undefined") return undefined;
    return new URLSearchParams(window.location.search).get("email") || undefined;
  });

  const { data: fetchedEvent, isLoading, error } = useQuery<EventWithSeries>({
    queryKey: ["/api/events", slug],
    queryFn: async () => {
      const res = await fetch(`/api/events/${slug}`);
      if (!res.ok) {
        const stub = previewStubEvent(slug);
        if (stub) return stub;
        throw new Error("Event not found");
      }
      return res.json();
    },
    enabled: !!slug,
    staleTime: 30000,
  });

  // ── LOCAL PREVIEW ONLY ────────────────────────────────────────────────────
  // A premium page cannot show its hero until the series has published future
  // dates: the hairline is built from `seriesSessions`, and the server only
  // returns published, still-bookable ones. A past date short-circuits the
  // whole page to "This event has ended" long before the hero renders.
  //
  // ?previewDates=1 on a DEV server synthesizes the upcoming session(s) so a page
  // whose enrollment date is not set yet can be reviewed. Vite replaces
  // `import.meta.env.DEV` with a literal false in a production build, so this
  // branch is stripped from the shipped bundle and can never fire for a real
  // visitor. It carries no seat numbers either.
  const event = useMemo<EventWithSeries | undefined>(() => {
    /* WHERE THIS IS ALLOWED TO FIRE. Dev, plus a PR PREVIEW revision — nowhere
       else. Preview hostnames are minted by cloudbuild-preview.yaml as
       `pr-<N>---adventure-website-<hash>.run.app`, so the `pr-<n>---` prefix
       identifies one and nothing else: not adventuremedia.ai, and not the
       production service's own run.app address either, which carries no such tag.

       Why widen it at all: a premium page cannot render until its series has a
       PUBLISHED future date, and publishing is the same switch that puts the date
       on /workshops and opens checkout. Without this, showing the team a finished
       page for review meant first making it buyable — approval backwards. Now the
       PR preview shows the real, clickable page while the live site still reads
       "This event has ended".

       It stays narrow on purpose: an explicit ?previewDates=1 is still required,
       the sessions it fabricates carry no seat numbers, and it only engages when
       the series has no real sessions at all (guard below), so it can never
       shadow a genuine date. */
    const previewHost =
      typeof window !== "undefined" && /^pr-\d+---/.test(window.location.hostname);
    // The private review key (stub series only, see PREVIEW_STUB_SERIES)
    // stands in for both the host check and ?previewDates=1.
    const review = !!fetchedEvent && hasReviewKey(fetchedEvent.seriesSlug ?? fetchedEvent.slug);
    if (!import.meta.env.DEV && !previewHost && !review) return fetchedEvent;
    if (!fetchedEvent) return fetchedEvent;
    if (typeof window === "undefined") return fetchedEvent;
    if (!review && !new URLSearchParams(window.location.search).has("previewDates")) return fetchedEvent;
    if ((fetchedEvent.seriesSessions ?? []).length > 0) return fetchedEvent;

    const base = new Date(fetchedEvent.startDatetime as unknown as string);
    // The event's own length, so a four-hour series previews as four hours.
    const durationMs =
      new Date(fetchedEvent.endDatetime as unknown as string).getTime() - base.getTime() || 3.5 * 36e5;
    const mk = (id: number, daysOut: number): SeriesSessionSummary => {
      const start = new Date(Date.now() + daysOut * 864e5);
      start.setUTCHours(base.getUTCHours(), base.getUTCMinutes(), 0, 0);
      return {
        id,
        slug: `${fetchedEvent.slug}-preview-${id}`,
        status: "active",
        startDatetime: start,
        endDatetime: new Date(start.getTime() + durationMs),
        timezone: fetchedEvent.timezone,
        zoomJoinUrl: null,
        seatsSold: 0,
        maxCapacity: fetchedEvent.maxCapacity,
        ticketTypes: fetchedEvent.ticketTypes,
      };
    };
    /* ── PINNED TO THE REAL DATE ───────────────────────────────────────────
       Esther, 2026-09-01: "can you just do one date for now? tuesday september
       15?" — so this is ONE session, on that Tuesday, not two arbitrary ones.

       Pinned rather than relative because the point of the preview is to review
       the page as it will actually ship, and "21 and 42 days from whenever you
       loaded this" put Sep 22 and Oct 13 on screen — neither of them a date
       anybody had agreed to, which is exactly how the sticky bar came to read
       "TUE, SEP 22 +1 MORE" while the plan said the 15th.

       The relative fallback keeps the seam working after the pinned date passes:
       a hardcoded past date would short-circuit the page to "This event has
       ended" and the next person would be debugging a dead preview instead of
       reading one. Once the real row exists in the DB this whole branch is
       skipped anyway — see the seriesSessions guard above.

       ONE session also gets the hero eyebrow right for free: it reads
       openCount and prints "ONE DATE OPEN FOR ENROLLMENT". */
    /* TWO dates now, matching the two real rows (Esther, 2026-09-07: "can you
       add a second date? we want two about 3/4 weeks apart. one after sukkot").
       Events 124 and 125 exist in the DB but are UNPUBLISHED, so
       `seriesSessions` comes back empty and this seam still runs — keeping the
       pinned list in step with those rows is what makes the preview an honest
       rehearsal rather than a different page.

       Oct 13 is 28 days after Sep 15 and clears Simchat Torah (4 Oct); the
       reasoning is written out in scripts/create-sales-date-sep-2026.ts. */
    /* THE ROW'S OWN DATE WINS (Esther, 2026-10-26, Claude for Beginners: "only
       show one date"). When the URL is a real dated row that is simply not
       published yet, preview exactly that one session, at its real start and
       end, instead of the pinned Sales dates below. */
    const ownStart = new Date(fetchedEvent.startDatetime as unknown as string);
    if (ownStart.getTime() > Date.now()) {
      const own: SeriesSessionSummary = {
        ...mk(-100, 1),
        startDatetime: ownStart,
        endDatetime: new Date(fetchedEvent.endDatetime as unknown as string),
      };
      /* Esther, 2026-10-29: the Cowork dates after Nov 6 that she proposed in
         Slack #workshops become Claude for Beginners dates. Preview only: no
         rows exist for them yet. 10:30 AM ET, as Cowork ran, three hours. */
      const PROPOSED_ET = ["2026-11-24", "2026-12-03", "2026-12-17", "2027-01-07", "2027-01-21"];
      const extra: SeriesSessionSummary[] = PROPOSED_ET.map((d, i) => {
        const start = new Date(`${d}T15:30:00Z`);
        return {
          ...own,
          id: -90 - i,
          slug: `${fetchedEvent.slug}-preview-${d}`,
          startDatetime: start,
          endDatetime: new Date(start.getTime() + 3 * 36e5),
        };
      }).filter((x) => x.startDatetime.getTime() > Date.now());
      // Three cards, as the Cowork page shows: the next three dates.
      return { ...fetchedEvent, status: "active", seriesSessions: [own, ...extra].slice(0, 3) };
    }
    /* AI Video pins its own pair (Dave, 2026-10-27: Tuesdays, he's in the
       office Mondays). The relative fallback had landed on Mon 16 Nov and
       Mon 14 Dec. */
    const PINNED_ET =
      PREVIEW_PINNED_DATES[fetchedEvent.seriesSlug ?? fetchedEvent.slug] ??
      ["2026-09-15", "2026-10-13"];    // both Tuesdays
    const pinned = PINNED_ET
      .map((d, i) => {
        const at = new Date(`${d}T00:00:00Z`);
        const daysOut = Math.ceil((at.getTime() - Date.now()) / 864e5);
        // Relative fallback keeps the seam alive once a pinned date passes; a
        // hardcoded past date short-circuits the page to "This event has ended"
        // and the next person debugs a dead preview instead of reading one.
        return mk(-101 - i, daysOut > 0 ? daysOut : 21 + i * 28);
      })
      .sort((a, b) => a.startDatetime.getTime() - b.startDatetime.getTime());
    return {
      ...fetchedEvent,
      startDatetime: pinned[0].startDatetime,
      endDatetime: pinned[0].endDatetime,
      status: "active",
      seriesSessions: pinned,
    };
  }, [fetchedEvent]);


  /* Hero price for the ?price=1 test.

     Uses the SINGLE-SEAT price, not the cheapest tier. An earlier revision
     took Math.min across every ticket type, which selects the Team tier —
     gated behind minQty 3 in workshop-ticket-block.tsx. On the Code page that
     printed "From $987" when a solo visitor actually pays $1,899, a 48%
     understatement of a price we were about to advertise on paid traffic.

     Tier selection mirrors resolveTickets() exactly (first ticket labelled
     standard/individual/general admission, else the first tier) so the hero
     and the checkout can never name different numbers. Early-bird price wins
     while its deadline is live, same as the checkout.

     No "From" prefix: this is one specific purchasable price, not a range. */
  /* Ad visitors arrive with ?coupon=META30. When the hero shows a price it
     must be the price they will actually pay, so validate the code the same
     way WorkshopTicketBlock does for its reserve bar (same endpoint, same
     seriesSlug scope). Only fetched under ?price=1 — the control arm makes no
     extra request. Display-only; the server recomputes at checkout. */
  // Random arm for the Code page price test — only once the event is known
  // (the slug alone can't tell the adult page from a dated variant) and only
  // for META30 holders, whose hero price matches what checkout charges.
  const [randomPriceArm, setRandomPriceArm] = useState<"A" | "B" | null>(null);
  const priceTestSeries = event?.seriesSlug ?? event?.slug;
  useEffect(() => {
    if (
      forcedPriceArm ||
      priceTestSeries !== "claude-code-for-beginners-workshop" ||
      couponCode.trim().toLowerCase() !== "meta30"
    ) {
      setRandomPriceArm(null);
      return;
    }
    let arm: "A" | "B" | null = null;
    try {
      const stored = localStorage.getItem("code_price_test_arm");
      if (stored === "A" || stored === "B") arm = stored;
    } catch {}
    if (!arm) {
      arm = Math.random() < 0.5 ? "A" : "B";
      try {
        localStorage.setItem("code_price_test_arm", arm);
      } catch {}
    }
    setRandomPriceArm(arm);
  }, [forcedPriceArm, priceTestSeries, couponCode]);
  const showHeroPrice = (forcedPriceArm ?? randomPriceArm) === "B";
  // One GA4 event per view of the page by a randomly assigned visitor (an
  // in-app back/forward re-sends it, like page_view), so read the test by
  // USERS per event, not event count. GTM tag "GA4 - Code price test view"
  // sends it as price_test_view_A / price_test_view_B. GTM loads on first
  // interaction (or after 30s), so a visitor who leaves before either is
  // not counted — check the A/B user split is ~50/50 before reading rates.
  // Forced ?price= visits are QA and are not counted.
  useEffect(() => {
    if (forcedPriceArm || !randomPriceArm) return;
    const w = window as any;
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event: "price_test_view", price_test_arm: randomPriceArm });
    }
  }, [forcedPriceArm, randomPriceArm]);
  // The arm rides to the order on utm_content (→ Stripe metadata → the
  // order's "Referred by"), appended so the ad's own tag survives.
  const checkoutUtm = useMemo(() => {
    // v2 (2026-10-14): hero price moved above the subtitle and arm A's
    // mobile bar dropped its price. Orders tagged price_A / price_B came from
    // the earlier layout, so the new layout carries its own tag.
    const tag = forcedPriceArm
      ? `price_v2_${forcedPriceArm}_forced`
      : randomPriceArm
        ? `price_v2_${randomPriceArm}`
        : null;
    if (!tag) return utmParams;
    return {
      ...utmParams,
      utm_content: utmParams.utm_content ? `${utmParams.utm_content}|${tag}` : tag,
    };
  }, [utmParams, forcedPriceArm, randomPriceArm]);
  // Keyed by the series it was checked for: after a client-side hop to
  // another workshop, the previous page's result must not be read as this
  // page's (it would show the wrong discount until the new check lands).
  const [heroCouponCheck, setHeroCouponCheck] = useState<
    { series: string; status: CouponStatus } | null
  >(null);
  const heroCouponSeries = event?.seriesSlug ?? event?.slug;
  const heroCouponStatus =
    heroCouponCheck && heroCouponCheck.series === heroCouponSeries ? heroCouponCheck.status : null;
  useEffect(() => {
    const code = couponCode?.trim();
    if (!showHeroPrice || !code || !heroCouponSeries) {
      setHeroCouponCheck(null);
      return;
    }
    let cancelled = false;
    // The hero shows no price while the check is pending, so a request that
    // never answers would leave the price arm with no price. After 3s show
    // the plain price, but keep the request open: a late answer still
    // replaces it, so the hero can't disagree with the reserve bar (which
    // waits for the same check with no time limit).
    const controller = new AbortController();
    const timer = setTimeout(() => {
      if (!cancelled) setHeroCouponCheck({ series: heroCouponSeries, status: { valid: false } });
    }, 3000);
    fetch("/api/validate-coupon", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, seriesSlug: heroCouponSeries }),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((status: CouponStatus) => {
        if (!cancelled) setHeroCouponCheck({ series: heroCouponSeries, status });
      })
      .catch(() => {
        if (!cancelled) setHeroCouponCheck({ series: heroCouponSeries, status: { valid: false } });
      })
      .finally(() => clearTimeout(timer));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [showHeroPrice, couponCode, heroCouponSeries]);

  const heroPrice = useMemo<{ line: string; strike?: string } | undefined>(() => {
    if (!showHeroPrice || !event?.ticketTypes?.length) return undefined;
    const isIndividualLabel = (label: string) =>
      ["standard", "individual", "general admission"].includes(label.trim().toLowerCase());
    const std =
      event.ticketTypes.find((t) => isIndividualLabel(t.label)) ?? event.ticketTypes[0];
    if (!std) return undefined;
    const eb =
      !!std.earlyBirdPriceCents &&
      !!std.earlyBirdDeadline &&
      new Date() < new Date(std.earlyBirdDeadline);
    const cents = eb ? std.earlyBirdPriceCents! : std.priceCents;
    // A coupon is in hand but not yet validated: show nothing rather than
    // flash the full price for ~0.5s before the discount lands.
    if (couponCode?.trim() && heroCouponStatus === null) return undefined;
    // Percent codes only, exactly as the reserve bar: fixed-price codes are
    // per-order math, so they keep the plain price.
    const pct =
      heroCouponStatus?.valid && !heroCouponStatus.fixedPriceCents && heroCouponStatus.percentOff
        ? heroCouponStatus.percentOff
        : 0;
    if (pct <= 0) return { line: formatPrice(cents) };
    return {
      line: formatPrice(Math.round(cents * (1 - pct / 100))),
      strike: formatPrice(cents),
    };
  }, [showHeroPrice, event, heroCouponStatus, couponCode]);

  // The drawn primary button needs real SVG strokes to animate in (a CSS
  // background image can't animate stroke-dashoffset). Called HERE, above
  // every early return on this component — see the Rules of Hooks note below.
  const premiumPageClass = event?.seriesSlug
    ? PREMIUM_LAYOUT_BY_SERIES[event.seriesSlug]?.pageClass
    : undefined;
  useDrawnButtons(
    !!premiumPageClass?.includes("ds-cowork"),
    // The palette variant, mirrored onto <html> so the reminder popup — which
    // portals out of the page div — can be reached by it.
    premiumPageClass?.split(" ").find((c) => c.startsWith("ds-") && c !== "ds-cowork"),
  );

  // NOTE: Bespoke per-series page templates (e.g. Claude for Design) are
  // dispatched at the wrapper component below (see EventDetailRouter at the
  // bottom of this file). Doing the dispatch INSIDE this component and AFTER
  // some hooks but BEFORE others violates the Rules of Hooks — once `event`
  // resolves to a Design workshop, the early return skips every hook below
  // it, React detects "rendered fewer hooks than expected", and the page
  // unmounts to a blank screen. The wrapper does the dispatch before any
  // EventDetailPage hooks run, which keeps hook order stable.

  // Series support: which session is selected in the date picker.
  // - Default to the primary event id (which the resolver chose as "soonest
  //   bookable" when the URL was a series slug).
  // - `?date=YYYY-MM-DD` query param can pre-select a specific session.
  // - When the user picks a different date, we reflect that in the URL via
  //   history.replaceState so the URL is shareable and refresh-stable.
  const [selectedSessionId, setSelectedSessionIdState] = useState<number | null>(null);

  const setSelectedSessionId = useCallback((id: number | null) => {
    setSelectedSessionIdState(id);
    // Update the URL `?date=` query param to match. We use replaceState
    // (not pushState) so the browser back button doesn't fill up with
    // intermediate selections — refresh and bookmark just work.
    if (typeof window === "undefined" || !id || !event) return;
    const sessions = event.seriesSessions || [];
    const match = sessions.find(s => s.id === id);
    if (!match) return;
    const isoDate = new Date(match.startDatetime).toISOString().slice(0, 10);
    const url = new URL(window.location.href);
    if (url.searchParams.get("date") === isoDate) return;
    url.searchParams.set("date", isoDate);
    window.history.replaceState(window.history.state, "", url.toString());
  }, [event]);

  // Hero date-stop clicks: select the session AND carry the visitor to the
  // ticketing block, so picking a date flows straight into checkout.
  const selectSessionAndScrollToTickets = useCallback((id: number) => {
    setSelectedSessionId(id);
    // OPENS THE CHECKOUT (sheet on mobile, drawer on desktop); anchor-scroll runs only when nothing is bookable.
    if (requestCheckout()) return;
    document.getElementById("tickets")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [setSelectedSessionId]);

  useEffect(() => {
    if (!event) return;
    const sessions = event.seriesSessions || [];
    if (sessions.length === 0) {
      setSelectedSessionIdState(event.id);
      return;
    }
    // Honor ?date=YYYY-MM-DD if present and matches a session — but skip
    // sold-out matches so the pricing panel never opens on a session you
    // can't actually buy. (The design workshop page mirrors this guard.)
    const sp = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const dateParam = sp?.get("date");
    if (dateParam) {
      const match = sessions.find(s => s.startDatetime.toString().startsWith(dateParam));
      if (match && match.status !== "sold_out") {
        setSelectedSessionIdState(match.id);
        return;
      }
    }
    // Default: prefer a future bookable session over a past or sold-out
    // primary. The resolver returns a primary by exact-slug match — if the
    // user landed on a dated URL whose date is past or whose session is
    // sold out, the date picker and Reserve button should default to the
    // next bookable session so the CTA does something useful.
    const primaryIsPast = new Date(event.startDatetime as unknown as string) < new Date();
    const primaryIsSoldOut = event.status === "sold_out";
    if (primaryIsPast || primaryIsSoldOut) {
      const firstBookable = sessions.find(s => s.status !== "sold_out");
      if (firstBookable) {
        setSelectedSessionIdState(firstBookable.id);
      } else if (sessions.length > 0) {
        // No bookable sibling — fall back to the first sibling at least so
        // the picker has something selected. The page still shows the
        // sold-out treatment for that tile.
        setSelectedSessionIdState(sessions[0].id);
      } else {
        setSelectedSessionIdState(event.id);
      }
    } else {
      setSelectedSessionIdState(event.id);
    }
  }, [event?.id]);

  // selectedSession + the multi-date step-label detection now derive inside
  // WorkshopTicketBlock (they're pure functions of event + selectedSessionId).

  // ─── Workshop scarcity-display layer (v1: Cowork only) ───
  // Compute the 4-tile timeline composition (past + upcoming) and the seat-clock
  // state per upcoming session. Both the hero (WorkshopHero) and the ticketing
  // strip below consume these. When the series is not in SCARCITY_CONFIG, the
  // computations return null/empty and the page falls back to legacy rendering.
  const scarcityConfig = getScarcityConfig(event?.seriesSlug);
  const scarcityUpcoming = useMemo(() => {
    if (!event?.seriesSessions) return [];
    const nowMs = Date.now();
    return event.seriesSessions
      .filter((s) => new Date(s.startDatetime).getTime() >= nowMs)
      .sort((a, b) => new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime())
      .slice(0, 2);
  }, [event?.seriesSessions]);
  // Always call the hook (rules of hooks); pass nulls when no config so it stays a no-op.
  const sessionClocks = useSeatClocks(
    scarcityUpcoming,
    scarcityConfig
      ? {
          startingCount: scarcityConfig.startingCount,
          curve: scarcityConfig.curve,
          dayBefore: scarcityConfig.dayBefore,
        }
      : null,
  );
  const scarcityTimeline = useMemo(() => {
    if (!scarcityConfig || !event) return null;
    return deriveTimelineTiles({
      // A cancelled row is not a sold-out room; keep it off the strip.
      realPast: (event.pastSessions ?? []).filter((s) => s.status !== "cancelled"),
      upcoming: scarcityUpcoming,
      config: scarcityConfig,
      totalSlots: event.seriesSlug ? PREMIUM_LAYOUT_BY_SERIES[event.seriesSlug]?.hero.dateCards : undefined,
    });
  }, [scarcityConfig, event, scarcityUpcoming]);

  // Flatten the derived past/upcoming tiles into the HairlineStop shape that
  // HairlineTimeline expects (chronological left→right, past first). Lives at
  // the page level because both the hero and ticketing strip share the same
  // sessionClocks computation.
  const hairlineStops = useMemo<HairlineStop[] | null>(() => {
    // Premium page WITHOUT synthetic scarcity (e.g. Design): build the hairline
    // from REAL upcoming sessions only, with NO seat clock. The hero renders it
    // with hideMeter, so it shows real dates and zero seat numbers.
    if (!scarcityConfig) {
      if (!event?.seriesSlug || !PREMIUM_LAYOUT_BY_SERIES[event.seriesSlug]) return null;
      const now = Date.now();
      const heroCfg = PREMIUM_LAYOUT_BY_SERIES[event.seriesSlug].hero;
      const capped = heroCfg.soldOutCards !== undefined && heroCfg.dateCards !== undefined;
      // Real past sessions that genuinely sold out → "SOLD OUT" tiles (honest
      // social proof, dates only, no numbers). Most-recent 2, oldest→newest.
      const pastSoldOut: HairlineStop[] = (event.pastSessions ?? [])
        .filter((s) => s.status === "sold_out")
        .sort((a, b) => new Date(a.startDatetime as unknown as string).getTime() - new Date(b.startDatetime as unknown as string).getTime())
        .slice(capped ? -Math.max(heroCfg.soldOutCards!, 1) : -2)
        .map((s) => ({
          kind: "soldout" as const,
          key: `past-${s.id}`,
          startDatetime: new Date(s.startDatetime as unknown as string),
          // Time line on the sold-out card only where the strip is capped
          // (Beginners); other real-dates pages keep their date-only tiles.
          endDatetime: capped ? new Date(s.endDatetime as unknown as string) : undefined,
          timezone: s.timezone,
          where: capped ? event.platform ?? undefined : undefined,
        }));
      const realOpen: HairlineStop[] = (event.seriesSessions ?? [])
        .filter((s) => new Date(s.startDatetime as unknown as string).getTime() >= now && s.status !== "sold_out")
        .sort((a, b) => new Date(a.startDatetime as unknown as string).getTime() - new Date(b.startDatetime as unknown as string).getTime())
        .map((s) => ({
          kind: "open" as const,
          key: `real-${s.id}`,
          sessionId: s.id,
          startDatetime: new Date(s.startDatetime as unknown as string),
          endDatetime: new Date(s.endDatetime as unknown as string),
          timezone: s.timezone,
          where: event.platform ?? undefined,
          startingCount: s.maxCapacity ?? 100, // unused (meter hidden); type requires it
        }));
      if (capped) {
        const sold = heroCfg.soldOutCards === 0 ? [] : pastSoldOut;
        return realOpen.length ? [...sold, ...realOpen.slice(0, heroCfg.dateCards! - sold.length)] : null;
      }
      const stops = [...pastSoldOut, ...realOpen];
      return stops.length ? stops : null;
    }
    if (!scarcityTimeline) return null;
    /* A sold-out card carries the same "10:00 AM to 1:30 PM EDT on Zoom" line
       as an open one (Isaac, 2026-09-09) — but only where the end is real:
         · a REAL past tile has a row in event.pastSessions, matched on the
           start instant the tile was derived from, so its own end is used.
         · a SYNTHETIC tile has no row anywhere, so its end is the tile's start
           plus THIS event's own duration. The synthetic dates exist to say the
           series has been running at this shape; the shape is the event's.
       Either way the string is formatted from dates, never written down, so a
       session that moves cannot leave a wrong time on a card. */
    const eventDurationMs =
      event?.startDatetime && event?.endDatetime
        ? new Date(event.endDatetime as unknown as string).getTime() -
          new Date(event.startDatetime as unknown as string).getTime()
        : null;
    const past: HairlineStop[] = scarcityTimeline.pastTiles.map((t) => {
      const realRow =
        t.kind === "real"
          ? (event?.pastSessions ?? []).find(
              (s) => new Date(s.startDatetime as unknown as string).getTime() === t.startDatetime.getTime(),
            )
          : undefined;
      const endDatetime = realRow
        ? new Date(realRow.endDatetime as unknown as string)
        : eventDurationMs !== null && eventDurationMs > 0
          ? new Date(t.startDatetime.getTime() + eventDurationMs)
          : undefined;
      return {
        kind: "soldout" as const,
        key: t.key,
        startDatetime: t.startDatetime,
        timezone: t.kind === "real" ? t.timezone : "America/New_York",
        ariaLabel: t.kind === "synthetic" ? t.ariaLabel : undefined,
        endDatetime,
        where: event?.platform ?? undefined,
      };
    });
    const upcoming: HairlineStop[] = scarcityTimeline.upcomingTiles.map((t) => ({
      kind: "open" as const,
      key: `upcoming-${t.session.id}`,
      sessionId: t.session.id,
      startDatetime: t.startDatetime,
      timezone: t.session.timezone,
      clock: sessionClocks[t.session.id],
      startingCount: scarcityConfig.startingCount,
      // The time-and-platform line (showSessionMeta) reads these. They were
      // only ever set on the no-clock branch above, so turning the seat clock
      // on for Sales silently dropped "10:00 AM to 1:30 PM EDT on Zoom".
      endDatetime: new Date(t.session.endDatetime as unknown as string),
      where: event?.platform ?? undefined,
    }));
    return [...past, ...upcoming];
  }, [event, scarcityTimeline, scarcityConfig, sessionClocks]);

  // ─── Sticky reserve-bar data ───
  // Surfaces the same conversion facts the ticket block derives — next session
  // date (+ N more), the lowest live price (early-bird respected), seat urgency
  // from the shared seat-clock, and the format — so the bar can never drift
  // from the ticketing panel below. Re-derives from the SAME event/session/clock
  // inputs rather than reading the ticket block's internal state.
  const jumpNavData = useMemo(() => {
    if (!event) return null;
    const now = Date.now();

    // Upcoming OPEN sessions, soonest first (mirrors the ticket block's
    // bookableSessions — sold-out dates are excluded so the bar never
    // advertises a date/price the checkout drawer won't sell). Falls back to
    // the event's own date for single-session workshops with no series
    // siblings.
    const upcoming = (event.seriesSessions ?? [])
      .filter((s) => new Date(s.startDatetime).getTime() >= now && s.status !== "sold_out")
      .sort((a, b) => new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime());
    const nextSession = upcoming[0] ?? null;
    const moreDates = Math.max(0, upcoming.length - 1);

    // Nothing bookable at all: no open upcoming session AND the event itself
    // (the one-off / primary fallback the facts would otherwise render) is
    // sold out. The bar shows a "Sold out" label instead of date/price facts.
    const soldOut = !nextSession && event.status === "sold_out";

    const nextStart = (nextSession?.startDatetime ?? event.startDatetime) as unknown as string;
    const nextTz = nextSession?.timezone ?? event.timezone;
    // Short date — "Mon, Jul 14" — tight enough for a one-line bar.
    const dateLabel = new Date(nextStart).toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric", timeZone: nextTz,
    });
    // Same instant, spelled out — "September 15" — for the name-date variant's
    // badge. No year: the bar only ever shows a date inside the booking window.
    const nextDateLong = new Date(nextStart).toLocaleDateString("en-US", {
      month: "long", day: "numeric", timeZone: nextTz,
    });

    // Lowest live price across the next session's tickets (early-bird wins when
    // its deadline hasn't passed). "From" prefix only when tiers actually differ.
    const tickets =
      nextSession?.ticketTypes && nextSession.ticketTypes.length > 0
        ? nextSession.ticketTypes
        : event.ticketTypes;
    const effectivePrices = tickets.map((t) =>
      t.earlyBirdPriceCents && t.earlyBirdDeadline && new Date() < new Date(t.earlyBirdDeadline)
        ? t.earlyBirdPriceCents
        : t.priceCents,
    );
    const lowestCents = effectivePrices.length > 0 ? Math.min(...effectivePrices) : null;
    const priceLabel = lowestCents !== null ? formatPrice(lowestCents) : null;
    const priceFrom = tickets.length > 1;

    // Seat urgency: prefer the shared deterministic seat-clock (same source as
    // the hero meter) for the next session; otherwise use real remaining
    // capacity, only when it's genuinely tight (< 20).
    let seatsLabel: string | null = null;
    // Premium page WITHOUT synthetic scarcity (Design): show NO seat count in
    // the sticky bar — real dates only, no numbers. (Other series keep their
    // honest real-remaining urgency.)
    const premiumNoScarcity =
      !scarcityConfig && !!(event.seriesSlug && PREMIUM_LAYOUT_BY_SERIES[event.seriesSlug]);
    const clock = nextSession ? sessionClocks[nextSession.id] : undefined;
    if (event.seriesSlug === "claude-for-beginners-workshop") {
      // Esther, 2026-11-02: the handwritten urgency line with no number. It is
      // not tied to real bookings; clear it with Isaac (FTC) before launch, as
      // with the Design page's synthetic scarcity.
      seatsLabel = "Only a few seats left";
    } else if (premiumNoScarcity) {
      seatsLabel = null;
    } else if (scarcityConfig && clock && clock.state !== "sold-out" && typeof clock.seatsLeft === "number") {
      // scarcityConfig guard: with no config useSeatClocks returns seatsLeft 0
      // as a neutral placeholder, which read as "0 seats left" on any series
      // outside SCARCITY_CONFIG.
      // "1 seats left" is reachable here (the day-before clock floors at 3, but
      // a real remaining count of 1 is not floored) and reads as a bug on the
      // one card most likely to be read closely.
      seatsLabel = `${clock.seatsLeft} ${clock.seatsLeft === 1 ? "seat" : "seats"} left`;
    } else {
      const remaining =
        nextSession?.maxCapacity != null
          ? nextSession.maxCapacity - (nextSession.seatsSold ?? 0)
          : event.maxCapacity != null
            ? event.maxCapacity - (event.seatsSold ?? 0)
            : null;
      if (remaining !== null && remaining > 0 && remaining < 20) {
        seatsLabel = `${remaining} ${remaining === 1 ? "seat" : "seats"} left`;
      }
    }

    const formatLabel = event.format === "online" ? "Live on Zoom" : "Live in person";

    return { dateLabel, nextDateLong, title: event.title, moreDates, priceLabel, priceFrom, seatsLabel, formatLabel, soldOut };
  }, [event, sessionClocks, scarcityConfig]);

  const content = getWorkshopContent(event?.seriesSlug ?? null);

  // Per-series SEO copy, shared with the SSR emitter so the hydrated head
  // matches the served one. See WORKSHOP_SERIES_SEO in lib/seo.tsx.
  const seriesSeo = WORKSHOP_SERIES_SEO[event?.seriesSlug ?? slug];

  usePageMeta({
    title: seriesSeo?.title
      ?? (event ? `${event.title} | AdVenture AI Workshops` : "Workshop | AdVenture AI"),
    description: seriesSeo?.description || event?.seoDescription || event?.shortDescription || "Join an AdVenture AI workshop — live training sessions and hands-on cohorts.",
    image: seriesSeo?.image || event?.heroImageUrl || undefined,
    path: `/workshops/${slug}`,
    // Canonical consolidates every dated session URL in a series down to the
    // bare series URL. Falls back to the URL slug for one-off events.
    canonicalPath: `/workshops/${event?.seriesSlug ?? slug}`,
    // The preview/review placeholder (id -1, see previewStubEvent) is never
    // indexed. Every real event has a positive id, so this is false for them.
    noindex: event?.id === -1,
    jsonLd: event ? [
      {
        "@context": "https://schema.org",
        // EducationEvent — keep in sync with the SSR emitters in
        // server/page-meta.ts (buildEventNode + workshop detail).
        "@type": "EducationEvent",
        name: event.title,
        description: event.seoDescription || event.shortDescription || "",
        startDate: event.startDatetime,
        endDate: event.endDatetime,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: event.format === "online"
          ? "https://schema.org/OnlineEventAttendanceMode"
          : event.format === "hybrid"
            ? "https://schema.org/MixedEventAttendanceMode"
            : "https://schema.org/OfflineEventAttendanceMode",
        location: event.format === "online"
          ? { "@type": "VirtualLocation", url: `https://adventuremedia.ai/workshops/${slug}` }
          : { "@type": "Place", name: event.platform || "TBA", address: event.locationAddress || "" },
        organizer: {
          "@type": "Organization",
          name: "AdVenture Media Group",
          url: "https://adventuremedia.ai",
        },
        ...(event.ticketTypes.length > 0 ? {
          offers: {
            "@type": "AggregateOffer",
            url: `https://adventuremedia.ai/workshops/${slug}#tickets`,
            priceCurrency: "USD",
            lowPrice: Math.min(...event.ticketTypes.map(t => t.priceCents)) / 100,
            highPrice: Math.max(...event.ticketTypes.map(t => t.priceCents)) / 100,
            availability: (event.maxCapacity && (event.maxCapacity - (event.seatsSold || 0)) <= 0)
              ? "https://schema.org/SoldOut"
              : "https://schema.org/InStock",
          },
        } : {}),
        ...((content?.instructors?.length ?? 0) > 0 ? {
          performer: (content?.instructors ?? []).map(inst => ({
            "@type": "Person",
            name: inst.name,
            ...(inst.linkedinUrl ? { sameAs: inst.linkedinUrl } : {}),
          })),
        } : {}),
        image: event.heroImageUrl || undefined,
      },
      // Course + hasCourseInstance for the kids camp only — it's a genuine
      // multi-session course, so this makes it eligible for AI course-discovery
      // alongside the Event node. Adult one-day workshops stay Event-only.
      ...(event.seriesSlug === "claude-code-for-kids" || slug.startsWith("claude-code-for-kids")
        ? [{
            "@context": "https://schema.org",
            "@type": "Course",
            name: event.title,
            description: event.seoDescription || event.shortDescription || "",
            provider: {
              "@type": "Organization",
              name: "AdVenture Media Group",
              sameAs: "https://adventuremedia.ai",
            },
            // Mirrors the SSR Course node in server/page-meta.ts (Google needs
            // offers + workload/schedule for Course rich-result eligibility).
            // Price = the single-camper seat, early-bird-aware, same as the
            // Event offers the server emits.
            ...(event.ticketTypes?.length
              ? {
                  offers: {
                    "@type": "Offer",
                    price: (() => {
                      const t = event.ticketTypes[0];
                      const earlyBirdActive =
                        t.earlyBirdPriceCents &&
                        t.earlyBirdDeadline &&
                        new Date() < new Date(t.earlyBirdDeadline);
                      const cents = earlyBirdActive ? t.earlyBirdPriceCents! : t.priceCents;
                      return ((cents ?? 0) / 100).toFixed(2);
                    })(),
                    priceCurrency: "USD",
                    category: "Paid",
                  },
                }
              : {}),
            hasCourseInstance: {
              "@type": "CourseInstance",
              courseMode: "online",
              courseWorkload: KIDS_COURSE_WORKLOAD,
              courseSchedule: KIDS_COURSE_SCHEDULE,
              ...(event.startDatetime ? { startDate: event.startDatetime } : {}),
              ...(event.endDatetime ? { endDate: event.endDatetime } : {}),
              ...((content?.instructors?.length ?? 0) > 0 ? {
                instructor: (content?.instructors ?? []).map(inst => ({
                  "@type": "Person",
                  name: inst.name,
                  ...(inst.linkedinUrl ? { sameAs: inst.linkedinUrl } : {}),
                })),
              } : {}),
            },
          }]
        : []),
      ...((content?.faqItems?.length ?? 0) > 0 ? [faqSchema((content?.faqItems ?? []).map(f => ({ question: f.question, answer: f.answer })))] : []),
      // Reviews as schema.org/Review nodes — Google + LLM crawlers read this
      // even when the visible bento caps at 5 cards. Gated by the same enable
      // sets the bento uses so we only emit reviews where they're "officially"
      // attributed to this workshop.
      ...((event.seriesSlug && REVIEWS_ENABLED_SERIES.has(event.seriesSlug)) || REVIEWS_ENABLED_SLUGS.has(event.slug)
        ? buildReviewsJsonLd({
            itemReviewedName: event.title,
            itemReviewedUrl: `https://adventuremedia.ai/workshops/${event.seriesSlug ?? slug}`,
            seriesSlug: event.seriesSlug,
          })
        : []),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Workshops", url: "/workshops" },
        { name: event.title, url: `/workshops/${slug}` },
      ]),
    ] : undefined,
  });

  // 2026-09-09: the hero IntersectionObserver that used to sit here moved into
  // JumpNav — see the note there. It fired page-level setState on every
  // crossing, which re-rendered all 4,300 lines of this component.

  // Exit-intent + scroll trigger logic moved to EventDetailRouter
  // (mounts ReminderPopup with the useExitIntent hook).

  if (isLoading) {
    return (<div className="min-h-screen bg-cream"><Navbar /><div className="pt-32 container-content"><div className="space-y-6 animate-pulse max-w-[700px]"><div className="h-8 bg-charcoal/5 rounded w-1/4" /><div className="h-16 bg-charcoal/5 rounded w-3/4" /><div className="h-4 bg-charcoal/5 rounded w-1/2" /></div></div></div>);
  }

  if (error || !event) {
    return (<div className="min-h-screen bg-cream"><Navbar /><div className="pt-32 container-content text-center py-20"><h1 className="type-h2 text-charcoal mb-4">Event not found</h1><Link href="/workshops"><Button className="bg-charcoal text-cream rounded-[4px] font-mono text-[14px] uppercase"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Events</Button></Link></div></div>);
  }

  // Past-event state: if the event's end time has passed and no redirect is configured,
  // show an "event has ended" page instead of the live ticket selector.
  const eventHasEnded = new Date(event.endDatetime as unknown as string) < new Date();
  if (eventHasEnded) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="pt-32 container-content text-center py-20 max-w-[640px] mx-auto">
          <span className="font-mono text-[12px] uppercase tracking-[0.15em] text-charcoal/50">Registration closed</span>
          <h1 className="type-h2 text-charcoal mt-4 mb-4">This event has ended</h1>
          <p className="type-body text-charcoal/70 mb-8">
            {event.title} has already taken place. Browse our upcoming workshops and events below.
          </p>
          <Link href="/workshops">
            <Button className="bg-charcoal text-cream rounded-[4px] font-mono text-[14px] uppercase">
              <ArrowLeft className="w-4 h-4 mr-2" /> See Upcoming Events
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const sortedInstructors = [...(content?.instructors ?? [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  // Premium page treatment (photo-blend hero + build accordion + persona
  // carousel + testimonials marquee + attendees-receive), opt-in per series.
  const premium = event.seriesSlug ? PREMIUM_LAYOUT_BY_SERIES[event.seriesSlug] : undefined;
  // The Cowork design system reskin: drawn marks replace the geometric ones.
  const dsCowork = !!premium?.pageClass.includes("ds-cowork");
  /* undefined = not configured, keep the shared label; null = deliberately no
     eyebrow (Sales, Isaac 2026-09-09). */
  const faqEyebrow = premium?.faqEyebrow === undefined ? "▸ Common questions" : premium.faqEyebrow;

  /* Mirror the page's design-system scopes onto <html> as `<scope>-page`.
     Some chrome renders as a direct child of <body> — the mobile sticky reserve
     bar and the reminder popup both do — so a `.ds-sales` selector cannot reach
     it: measured, that bar's ancestry is div.fixed > body, outside both scopes.
     ds-drawn-buttons.ts already adds `ds-cowork-page` for exactly this reason;
     this generalises it so any per-page scope gets the same hook without that
     shared file needing to know which pages exist. */
  useEffect(() => {
    const scopes = (premium?.pageClass ?? "")
      .split(/\s+/)
      .filter((c) => c.startsWith("ds-"))
      .map((c) => `${c}-page`);
    if (!scopes.length) return;
    const root = document.documentElement;
    scopes.forEach((c) => root.classList.add(c));
    return () => scopes.forEach((c) => root.classList.remove(c));
  }, [premium?.pageClass]);
  const mainInstructor = sortedInstructors[0];
  const otherInstructors = sortedInstructors.slice(1);
  // All ticket/pricing derivations (effectiveTicketTypes, effectiveSlug,
  // early-bird/volume pricing, seatsRemaining) moved into WorkshopTicketBlock.

  /* The reviews band, built once and placed by the config: before the agenda on
     every page, after it on Sales (reviewsAfterAgenda). */
  const reviewsBlock = (
    <>
    {/* ─── WORKSHOP REVIEWS ───
      Source of truth: client/src/lib/workshop-reviews.ts.
      Render gate: the page must be in REVIEWS_ENABLED_SERIES (matched
      by event.seriesSlug) OR in REVIEWS_ENABLED_SLUGS (one-off events
      not in a series). Per-review filtering happens inside
      getPublishedWorkshopReviews() based on the review's own
      published flag and optional `series` filter. */}
    {(() => {
      // Premium series: full-bleed scrolling marquee (same component as
      // the Claude-for-design page). Feed it the short real doc quotes
      // plus the longer already-published reviews from workshop-reviews.ts.
      if (premium?.testimonials) {
        // Group by length so card heights stay uniform within each row
        // (minimizes stretch whitespace): short Zoom-chat quotes scroll in
        // rows 1 & 3, the longer real reviews in the middle row.
        const shortQuotes = premium.testimonials.docQuotes;
        const names = premium.testimonials.reviewNames;
        const longReviews = names
          ? names.map((n) => WORKSHOP_REVIEWS.find((r) => r.published && r.name === n)).filter((r): r is WorkshopReview => !!r)
          : getPublishedWorkshopReviews(premium.testimonials.reviewsSeriesSlug);
        if (shortQuotes.length === 0 && longReviews.length === 0) return null;
        let marqueeRows: WorkshopReview[][];
        if (shortQuotes.length > 0) {
          // Two rows on desktop, like every other premium page (Esther,
          // 2026-10-08): the long reviews in one, the short chat quotes in the
          // other, so card heights stay even within each row. Was three rows
          // (short / long / short), which only Cowork showed.
          marqueeRows = [longReviews, shortQuotes];
        } else {
          // No short doc quotes yet (e.g. Code): spread the published
          // reviews across the marquee rows.
          //
          // Each row loops by translating -50%, so its scroll period is half
          // its width (~398px per card). A row with only 2-3 cards is
          // narrower than a desktop viewport and shows a blank stretch
          // mid-cycle. Below 12 reviews we use two fuller rows instead of
          // three thin ones. Threshold is 15 because the three-row branch
          // needs n >= 15 to keep >= 5 cards in every row.
          if (longReviews.length < 15) {
            const half = Math.ceil(longReviews.length / 2);
            marqueeRows = [longReviews.slice(0, half), longReviews.slice(half)];
          } else {
            const per = Math.ceil(longReviews.length / 3);
            marqueeRows = [longReviews.slice(0, per), longReviews.slice(per, per * 2), longReviews.slice(per * 2)];
          }
        }
        return (
          <>
            {/* Desktop / tablet: full-bleed tri-row marquee, full review
                set (short chat quotes + long reviews). Mounted only at
                its own breakpoint — see the reviewsNarrow note above. */}
            {!reviewsNarrow && (
            <div
              className="hidden sm:block"
              style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
            >
              <TestimonialsMarquee
                rows={marqueeRows}
                eyebrow={premium.reviewsEyebrow}
                noBottomBorder
                headline={
                  <>
                    Do we ask our attendees for reviews? <em>You bet we do.</em>
                  </>
                }
              />
            </div>
            )}
            {/* Mobile: the marquee's snap-strip yanked the first card to
                the screen edge and short quotes left a tall dead band
                above the arrows. Use the standard swipe carousel instead:
                first card on the 24px gutter, and MobileReviewsCarousel
                drops the short one-liners on its own. */}
            {reviewsNarrow && (
            <div className="sm:hidden space-y-20">
              <SectionDivider />
              <RevealSection id="reviews">
                <ReviewsAnimatedHeader
                  eyebrow={premium.reviewsEyebrow}
                  headline={
                    <>
                      Do we ask our attendees for reviews?{" "}
                      {/* CRAYON, NOT ITALIC. The desktop marquee headline
                          (.tm-h em in cowork-ds.css) has always rendered
                          this in Crayonize; this mobile header was still
                          Inter italic, so the same sentence had two
                          different treatments depending on viewport.
                          Measured before the fix: desktop Crayonize 52.8px,
                          mobile Inter italic 32px. */}
                      <em style={{ fontFamily: "var(--font-hand)", fontStyle: "normal", color: "var(--terra)" }}>You bet we do.</em>
                    </>
                  }
                />
                <MobileReviewsCarousel reviews={[...longReviews, ...shortQuotes]} longArrows={dsCowork} />
              </RevealSection>
            </div>
            )}
          </>
        );
      }
      const enabled =
        (event.seriesSlug && REVIEWS_ENABLED_SERIES.has(event.seriesSlug)) ||
        REVIEWS_ENABLED_SLUGS.has(event.slug);
      if (!enabled) return null;
      const reviewsToShow = getPublishedWorkshopReviews(event.seriesSlug);
      if (reviewsToShow.length === 0) return null;
      return (
        <>
          <SectionDivider />
          <RevealSection id="reviews">
            {STATIC_REVIEWS_HEADER_SERIES.has(event.seriesSlug || "") ? (
              <ReviewsStaticHeader />
            ) : (
              <ReviewsAnimatedHeader />
            )}
            {/* Desktop: bento grid layout. One breakpoint mounts — see
                the reviewsNarrow note above. */}
            {!reviewsNarrow && (
              <div className="hidden sm:block">
                <WorkshopReviewsBento reviews={reviewsToShow} />
              </div>
            )}
            {/* Mobile: horizontal swipe carousel */}
            {reviewsNarrow && (
              <div className="sm:hidden">
                <MobileReviewsCarousel reviews={reviewsToShow} longArrows={dsCowork} />
              </div>
            )}
          </RevealSection>
        </>
      );
    })()}
    </>
  );

  return (
    <div className={`min-h-screen bg-cream overflow-x-clip${premium ? ` ${premium.pageClass}` : ""}`}>
      <Navbar />
      <JumpNav
        heroRef={heroRef}
        dateLabel={jumpNavData?.dateLabel}
        moreDates={jumpNavData?.moreDates ?? 0}
        priceLabel={jumpNavData?.priceLabel ?? null}
        priceFrom={jumpNavData?.priceFrom ?? false}
        seatsLabel={jumpNavData?.seatsLabel ?? null}
        formatLabel={jumpNavData?.formatLabel}
        soldOut={jumpNavData?.soldOut ?? false}
        variant={premium?.jumpNavVariant}
        title={jumpNavData?.title}
        nextDateLong={jumpNavData?.nextDateLong}
      />

      {/* The page had no main landmark, so assistive tech had no "skip to the
          content" target and every section sat outside any region. Navbar and
          JumpNav stay outside it: they are navigation, not the content. */}
      <main>

      {/* ─── HERO ─── */}
      <div ref={heroRef} data-testid="section-event-hero">
        {(hairlineStops?.some((s) => s.kind === "open") || (event.seriesSessions && event.seriesSessions.length > 1)) ? (() => {
          // When the series is enabled in SCARCITY_CONFIG, render the editorial
          // hairline timeline (2 past sold-out + up to 2 upcoming, with
          // seat-clock capacity meter). This renders even when only ONE
          // upcoming session remains — the sold-out past stops carry the
          // scarcity story (deriveTimelineTiles was designed for that case);
          // collapsing to the generic single-date hero hid it. Falls back to
          // the legacy next-3 display for non-scarcity multi-session series,
          // and to the single-date hero below otherwise.
          if (hairlineStops && hairlineStops.some((s) => s.kind === "open")) {
            // Premium series get the redesigned photo-blend hero (CoworkHero);
            // every other scarcity series keeps the editorial hairline
            // WorkshopHero. Headline/eyebrow/copy come from the per-series
            // PREMIUM_LAYOUT_BY_SERIES config; the DB title/SEO/emails are
            // unaffected (display-only override).
            if (premium) {
              const openCount = hairlineStops.filter((s) => s.kind === "open").length;
              return (
                <CoworkHero
                  eyebrow={premium.hero.eyebrow(openCount)}
                  headline={premium.hero.headline}
                  headlinePlain={premium.hero.headlinePlain}
                  subtitle={premium.hero.subtitle}
                  priceLine={heroPrice?.line}
                  priceStrike={heroPrice?.strike}
                  imageBase={premium.hero.imageBase}
                  imageBaseMobile={premium.hero.imageBaseMobile}
                  heroAvif={premium.hero.heroAvif}
                  blendEdges={premium.hero.blendEdges}
                  hairlineStops={hairlineStops}
                  onSelectSession={selectSessionAndScrollToTickets}
                  primaryCta={premium.hero.primaryCta}
                  secondaryCta={premium.hero.secondaryCta}
                  hideMeter={!scarcityConfig}
                  showSessionMeta={premium.hero.showSessionMeta}
                  dateLine={premium.hero.dateLine}
                  singleEvent={premium.hero.singleEvent}
                  introVideo={premium.hero.introVideo}
                />
              );
            }
            return (
              <WorkshopHero
                eyebrow={event.eventType?.toUpperCase() || "LIVE WORKSHOP"}
                headline={content?.hero?.headline ?? event.title}
                subtitle={content?.hero?.subtitle ?? (event.shortDescription || undefined)}
                kicker={content?.hero?.kicker}
                proofLines={content?.hero?.proofLines}
                priceLine={heroPrice?.line}
                priceStrike={heroPrice?.strike}
                hairlineStops={hairlineStops}
                selectedSessionId={selectedSessionId}
                onSelectSession={selectSessionAndScrollToTickets}
                primaryCta={{ label: "reserve your spot", href: "#tickets" }}
                secondaryCta={content?.agendaItems?.length ? { label: "see full agenda", href: "#agenda" } : undefined}
              />
            );
          }

          // Legacy hero (non-scarcity series): next 3 upcoming, status='sold_out' tiles inline.
          const now = Date.now();
          const next3 = event.seriesSessions
            .filter(s => new Date(s.startDatetime).getTime() >= now)
            .sort((a, b) => new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime())
            .slice(0, 3);
          return (
          <WorkshopHero
            eyebrow={event.eventType?.toUpperCase() || "LIVE WORKSHOP"}
            headline={content?.hero?.headline ?? event.title}
            subtitle={content?.hero?.subtitle ?? (event.shortDescription || undefined)}
            kicker={content?.hero?.kicker}
            proofLines={content?.hero?.proofLines}
            priceLine={heroPrice?.line}
            priceStrike={heroPrice?.strike}
            sessionCards={next3.map(s => ({
              id: s.id,
              startDatetime: s.startDatetime as unknown as string,
              endDatetime: s.endDatetime as unknown as string,
              timezone: s.timezone,
              seatsSold: s.seatsSold,
              maxCapacity: s.maxCapacity,
              soldOut: s.status === "sold_out",
            }))}
            selectedSessionId={selectedSessionId}
            onSelectSession={selectSessionAndScrollToTickets}
            primaryCta={{ label: premium ? "SELECT YOUR DATE" : "reserve your spot", href: "#tickets" }}
            secondaryCta={content?.agendaItems?.length ? { label: "see full agenda", href: "#agenda" } : undefined}
            eyebrowNode={premium ? <CoworkPulsingEyebrow openCount={next3.length} /> : undefined}
            hideSessionCardsMobile={!!premium}
          />
          );
        })() : (
          <WorkshopHero
            eyebrow={event.eventType?.toUpperCase() || "LIVE WORKSHOP"}
            headline={content?.hero?.headline ?? event.title}
            subtitle={content?.hero?.subtitle ?? (event.shortDescription || undefined)}
            kicker={content?.hero?.kicker}
            proofLines={content?.hero?.proofLines}
            priceLine={heroPrice?.line}
            priceStrike={heroPrice?.strike}
            cards={[
              { label: "DATE", value: formatDate(event.startDatetime as unknown as string, event.timezone), icon: "/images/workshop/hero-icon-date.webp?v=4" },
              { label: "TIME", value: formatTime(event.startDatetime as unknown as string, event.endDatetime as unknown as string, event.timezone), icon: "/images/workshop/hero-icon-time.webp?v=4" },
              { label: "FORMAT", value: `${event.format === "online" ? "Virtual" : "In Person"} · ${event.platform}`, icon: "/images/workshop/hero-icon-format.webp?v=4" },
            ]}
            primaryCta={{ label: "reserve your spot", href: "#tickets" }}
            secondaryCta={content?.agendaItems?.length ? { label: "see full agenda", href: "#agenda" } : undefined}
          />
        )}
      </div>

      {/* ─── LOGO BAR — brands that train with us ─── */}
      <div className="border-b border-charcoal/10 bg-cream">
        <div className="container-content">
          <p className="type-eyebrow text-charcoal/75 text-center pt-12 md:pt-16" style={{ fontSize: "16px", letterSpacing: "2px", marginBottom: "2.5rem" }}>
            {content?.logoBarEyebrow ?? "Over 450,000 students and brands upskill with AdVenture"}
          </p>
        </div>
        <div className="hidden md:block pb-10 md:pb-12">
          <Marquee className="[--duration:50s] [--gap:5rem]" repeat={2}>
            {((content?.brandLogos?.length ?? 0) > 0 ? (content?.brandLogos ?? []).map(l => ({ name: l.altText || l.imageUrl, src: l.imageUrl })) : clientLogosFallback).map((logo) => (
              <div key={logo.name} className="flex items-center justify-center px-5">
                <img src={logo.src} alt={logo.name} className="h-10 w-auto object-contain opacity-50" style={{ filter: "grayscale(1)", mixBlendMode: "multiply" }} loading="lazy" width={120} height={40} />
              </div>
            ))}
          </Marquee>
        </div>
        <div className="md:hidden pb-10 px-4">
          <div className="grid grid-cols-3" style={{ borderTop: "var(--border-structural)", borderLeft: "var(--border-structural)" }}>
            {/* WHY THIS USED TO SHOW SIX (Esther, 2026-08-28: "why don't we have a
                9 card logo grid on mobile"). The grid is `grid-cols-3` and was
                built for 3x3. Sales itself showed six because its own
                brandLogos list held exactly six — fixed in workshop-content.ts,
                which is the first branch below and feeds desktop too.

                The fallback had a second, separate version of the same problem:
                a series with NO brandLogos got up to 15 DB logos on desktop and a
                hardcoded six on mobile, from a different list entirely. Now both
                read the DB, sliced to 9 so the grid fills. MOBILE_LOGOS survives
                only for when the clients query is empty or short — better a tidy
                3x2 than a ragged row. */}
            {((content?.brandLogos?.length ?? 0) > 0
              ? (content?.brandLogos ?? []).map(l => ({ name: l.altText || l.imageUrl, src: l.imageUrl }))
              : clientLogosFallback.length >= 9
                ? clientLogosFallback.slice(0, 9)
                : MOBILE_LOGOS
            ).map((logo) => {
              const isCompact = ["T-Mobile", "PayPal", "Sports Illustrated"].includes(logo.name);
              return (
                <div
                  key={logo.name}
                  className="flex items-center justify-center py-5 px-2"
                  style={{ borderRight: "var(--border-structural)", borderBottom: "var(--border-structural)" }}
                >
                  <img
                    src={logo.src}
                    alt={logo.name}
                    className={`${isCompact ? "h-5" : "h-8"} w-auto object-contain opacity-60`}
                    style={{ filter: "grayscale(1)", mixBlendMode: "multiply" }}
                    loading="lazy"
                    width={120}
                    height={40}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── CONTENT ─── */}
      <div className="container-content section-padding-y">
        {/* Mobile tightens the inter-section gap (128px read as dead scroll at
            390px) — desktop rhythm unchanged from sm up. */}
        <div className="space-y-20 sm:space-y-32 md:space-y-44">

          {/* Claude for Beginners runs its own body (BeginnersFlow): the
              claude.com-style flow between the hero and the tickets. Every
              other series keeps the template sections below. */}
          {event.seriesSlug === "claude-for-beginners-workshop" ? (
            <BeginnersFlow reviews={reviewsBlock} />
          ) : (
          <>
          {/* ─── DESIGN-ONLY: samples showcase, ahead of the build accordion ───
              The Design page opens on the work itself, so "Designs like these"
              sits above "Design, build, and ship fast". Only the Design premium
              config sets extraBeforeBuild. */}
          {premium?.extraBeforeBuild}

          {/* ─── WHAT YOU'LL LEARN ─── */}
          <LabelledSection id="learn" labelledBy="learn-heading">
            <TrainingEyebrow label={premium?.learnEyebrow} />
            <SectionHeading
              id="learn-heading"
              title={
                premium ? (
                  premium.learnHeading
                ) : (
                  <>
                    What you'll{" "}
                    <span
                      aria-hidden="true"
                      style={{
                        textDecoration: "line-through",
                        textDecorationColor: "var(--terra)",
                        textDecorationThickness: "0.08em",
                        color: "var(--charcoal)",
                      }}
                    >
                      learn
                    </span>
                    <span className="sr-only">learn</span>{" "}
                    <span
                      className="inline-block align-baseline"
                      style={{
                        backgroundColor: "var(--terra)",
                        color: "var(--cream)",
                        padding: "0 0.22em",
                      }}
                    >
                      do
                    </span>
                    {" "}in {content?.learnHeadlineSuffix ?? event.title}
                  </>
                )
              }
            />
            {premium?.buildScroller ? (
              <StickyBuildScroller items={premium.buildItems} />
            ) : premium ? (
              // Premium: the real builds as a single-open accordion — clay
              // icon + title in each header, two-column expanded panel (copy
              // left, screenshot right). First item open by default.
              <CoworkBuildAccordion
                items={premium.buildItems}
                defaultOpen={0}
                alwaysOpen={premium.buildAlwaysOpen}
              />
            ) : (
            <div className="overflow-hidden" style={{ border: "var(--border-structural)" }}>
              {/* 4-up leaves empty dark cells when the count isn't a multiple
                  of 4 (6 outcomes = a row of 4 + a row of 2). Counts divisible
                  by 3 but not 4 go 3-up instead. */}
              <div className={`grid grid-cols-1 md:grid-cols-2 ${(content?.learningOutcomes?.length ?? 0) % 3 === 0 && (content?.learningOutcomes?.length ?? 0) % 4 !== 0 ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-px`} style={{ backgroundColor: "hsla(60, 3%, 8%, 0.60)" }}>
                {(content?.learningOutcomes ?? []).map((outcome, i) => {
                  // Subheader resolution order: hardcoded LEARN_SUBHEADERS map
                  // (legacy cowork copy), then the DB description. New
                  // workshops just set `description` and skip the map.
                  const subheader = LEARN_SUBHEADERS[outcome.headline] ?? outcome.description ?? null;
                  // Charcoal + indigo thin-line structural SVG icons (GridIcon),
                  // restored in place of the Lucide line-icons.
                  return (
                    <div key={i} className="relative bg-cream p-8 sm:p-10 flex flex-col min-h-0 sm:min-h-[260px]">
                      <EdgeMark position="top-left" />
                      <EdgeMark position="bottom-right" />
                      <div className="flex items-start justify-between mb-8">
                        <span className="text-[42px] font-mono tracking-wide text-indigo/70">0{i + 1}</span>
                        <GridIcon variant={i} className="w-14 h-14" />
                      </div>
                      {/* No mt-auto: headline anchors below the icon row at a
                          fixed offset so headlines align across cards in a row.
                          Subheaders below flow naturally and may end at different
                          y-positions, which is intentional. */}
                      <h3 className="type-h4 text-charcoal">{outcome.headline}</h3>
                      {subheader && (
                        <p className="type-body text-charcoal/70 mt-3 leading-relaxed" style={{ fontSize: "15px" }}>{subheader}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </LabelledSection>

          {/* ─── AFTER THE BUILD ACCORDION ───
              Design puts its Before/After proof here; Sales its artifact
              gallery. Both read as evidence for what the accordion just
              described. */}
          {premium?.extraAfterBuild}

          {/* ─── WHO IS THIS FOR — persona carousel (premium series w/ personas) ───
              Full-bleed out of the content container (standard escape, same as
              the agenda band) so its container-content header aligns with the
              logo and the track bleeds to the right edge. */}
          {premium?.personas && (
            <div style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}>
              {/* All four props: eyebrow/heading let a page override the default
                  "Is this workshop for you?" header (Sales does), and
                  autoOpenFirstOnMobile is main's — the ds-cowork pages opt out of
                  the Founders card hanging open on mobile. Both sides added props
                  to this call; keeping only one set dropped a live behaviour. */}
              <PersonaCarousel
                personas={premium.personas}
                eyebrow={premium.personaEyebrow}
                heading={premium.personaHeading}
                subheading={premium.personaSubheading}
                className={premium.personaClassName}
                autoOpenFirstOnMobile={!dsCowork}
                floatIn={premium.personaFloatIn}
              />
            </div>
          )}

          {/* ─── ONPROMPT STUDIO — everything included with your seat. Partner
              offering, Cowork only (gated by showOnPromptValueStack).
              "In collaboration with OnPrompt": left value copy beside a
              staggered 2×2 grid that bleeds off the right edge. ─── */}
          {premium?.showOnPromptValueStack && <OnPromptStudioValueStack />}
          {/* The Code page runs OnPrompt after the attendee room instead — the
              bonus reads better once the reader knows what the seat itself
              includes. Every other premium page keeps it here. */}
          {!dsCowork && premium?.onPromptSection}

          {/* ─── WHO SHOULD ATTEND (only for series with audience config) ─── */}
          {content?.audience && (
            <RevealSection id="who">
              <SiteEyebrow label="Who Should Attend" />
              <SectionHeading
                title={content.audience.heading ?? "Built for the people who have to lead the change."}
              />
              <div className="overflow-hidden" style={{ border: "var(--border-structural)" }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ backgroundColor: "hsla(60, 3%, 8%, 0.60)" }}>
                  <div className="relative bg-cream p-8 sm:p-10">
                    <EdgeMark position="top-left" />
                    <p className="type-eyebrow text-indigo uppercase mb-6">This is for you if...</p>
                    <ul className="space-y-4">
                      {content.audience.forItems.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className="w-4 h-4 mt-[5px] text-indigo shrink-0" strokeWidth={2} />
                          <span className="type-body text-charcoal/80">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="relative bg-cream p-8 sm:p-10">
                    <EdgeMark position="bottom-right" />
                    <p className="type-eyebrow uppercase mb-6" style={{ color: "var(--terra)" }}>It's not for...</p>
                    <ul className="space-y-4">
                      {content.audience.notForItems.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <X className="w-4 h-4 mt-[5px] shrink-0" style={{ color: "var(--terra)" }} strokeWidth={2} />
                          <span className="type-body text-charcoal/80">{item}</span>
                        </li>
                      ))}
                    </ul>
                    {content.audience.closingNote && (
                      <p className="type-body text-charcoal/60 mt-8">{content.audience.closingNote}</p>
                    )}
                  </div>
                </div>
              </div>
            </RevealSection>
          )}

          {/* ─── WORKSHOP REVIEWS ─── rendered from `reviewsBlock` (defined above the
              return); Sales moves it below the agenda via reviewsAfterAgenda. */}
          {!premium?.reviewsAfterAgenda && reviewsBlock}

          {/* ─── AGENDA (DARK) ─── Skipped when a series has no agenda (AI Video:
              Dave asked for the schedule off, 2026-10-27). */}
          {(content?.agendaItems ?? []).length > 0 && (
          <LabelledSection id="agenda" labelledBy="agenda-heading" style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}>
            <div className="ed-agenda-band relative overflow-hidden section-padding-y px-6 md:px-12 lg:px-20" style={{ backgroundColor: DARK_BG }}>
              {/* Dusk: the dark reviews band resolves into the agenda's paper.
                  Painted here so it sits under the content but over the ground. */}
              <div className="ed-agenda-dusk" aria-hidden="true" />
              <div className="absolute inset-0 bg-dot-matrix-light pointer-events-none opacity-30" />
              <DotMatrixAccent position="top-right" size="lg" dark className="opacity-15" />
              <DotMatrixAccent position="bottom-left" size="md" dark className="opacity-10" />
              <div className="relative z-10 max-w-[1280px] mx-auto">
                <SectionHeading
                  id="agenda-heading"
                  title={
                    premium?.agendaHeading ?? (
                      <>
                        {/* Mixed weight, rule 14: 300 frames it, 500 names it. */}
                        Workshop <strong className="ed-agenda-subject">agenda</strong>
                        <span className="ed-agenda-script">
                          what a day to be <span style={{ textTransform: "none" }}>Ai</span>live
                        </span>
                      </>
                    )
                  }
                  dark
                />
                <p className="type-body text-cream/75 mb-10">
                  All times Eastern (ET).
                  {(content?.agendaItems ?? []).some((it) => `${it.title} ${it.description ?? ""}`.toLowerCase().includes("lunch")) && " Lunch break included."}
                </p>
                <AgendaTimeline items={content?.agendaItems ?? []} />
              </div>
            </div>
          </LabelledSection>
          )}

          {/* Sales (Isaac, 2026-09-17): the agenda comes before the reviews. */}
          {premium?.reviewsAfterAgenda && reviewsBlock}

          {/* ─── THINGS YOU CAN BUILD (only for events with expected outcomes) ─── */}
          {(content?.showThingsYoullBuild ?? false) && (
          <LabelledSection id="build" labelledBy="build-heading">
            <SectionHeading id="build-heading" title="Oh, the things you might build." />
            <p className="type-body text-charcoal/70 -mt-4 mb-10">Just a few examples of what you'll build during this hands-on workshop.</p>
            <ThingsYouBuildScroll groups={THINGS_YOULL_BUILD} />
          </LabelledSection>
          )}

          {/* ─── INSTRUCTOR — simple 3-card row ───
              pb-0 on mobile: with the section gap this read as ~226px of dead
              scroll before the comparison band at 390px. */}
          {premium?.instructorOverride}
          {!premium?.instructorOverride && sortedInstructors.length > 0 && (
            <LabelledSection id="instructor" labelledBy="instructor-heading" className="pb-0 sm:pb-12 md:pb-20">
              {premium?.instructorEyebrow !== null && (
                <SiteEyebrow label={premium?.instructorEyebrow ?? (sortedInstructors.length === 1 ? "Meet your instructor" : "Meet your instructors")} />
              )}
              <SectionHeading id="instructor-heading" title={premium?.instructorHeading ?? (event.seriesSlug === "ai-security-workshop" ? "Taught by someone who has spent 18 years training people on security." : "Hosted by best-selling educators and actual AI practitioners.")} />
              {/* One instructor in the 3-card grid sat in the left third with two
                  empty columns beside it. A lone instructor gets a feature row
                  instead: portrait left, bio and credentials right. */}
              {sortedInstructors.length === 1 && !premium ? (() => {
                const inst = sortedInstructors[0];
                return (
                  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-8 md:gap-14 items-center max-w-5xl mx-auto">
                    {inst.headshotUrl && (
                      <img
                        src={inst.headshotUrl}
                        alt={inst.name}
                        width={800}
                        height={1000}
                        className="w-full max-w-sm mx-auto md:max-w-none vignette-organic"
                        style={{ mixBlendMode: "darken" }}
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                    <div>
                      <h3 className="text-charcoal mb-1" style={{ fontFamily: "var(--font-hand)", fontSize: 40, lineHeight: 1.1 }}>{inst.name}</h3>
                      <p className="type-label text-terra mb-5">{inst.title}</p>
                      {inst.accomplishments && inst.accomplishments.length > 0 && (
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-6 border-y border-charcoal/15 py-5">
                          {inst.accomplishments.map((a) => (
                            <li key={a} className="type-body-sm text-charcoal flex gap-2">
                              <span className="text-terra" aria-hidden="true">▸</span>
                              <span>{a}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="type-body text-charcoal/80">{inst.bio}</p>
                      {inst.linkedinUrl && (
                        <a href={inst.linkedinUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 type-nav text-indigo hover:text-indigo/80 transition-colors mt-4">
                          <LinkedInIcon className="w-3.5 h-3.5" /> LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                );
              })() : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {sortedInstructors.map((inst) => {
                  /* Resolution order, last wins: the shared map, then the
                     ds-cowork map, then this page's own override. */
                  const baseHeadshot = premium?.headshotOverrides?.[inst.name]
                    ? { ...ABOUT_HEADSHOTS[inst.name], ...premium.headshotOverrides[inst.name] }
                    : (dsCowork && ABOUT_HEADSHOTS_DSCOWORK[inst.name])
                    ? { ...ABOUT_HEADSHOTS[inst.name], ...ABOUT_HEADSHOTS_DSCOWORK[inst.name] }
                    : ABOUT_HEADSHOTS[inst.name];
                  const widths = premium?.instructorPhotoWidths;
                  const stem = (baseHeadshot as { variantBase?: string } | undefined)?.variantBase;
                  const aboutHeadshot = widths && stem
                    ? {
                        ...baseHeadshot,
                        photo: `${stem}-${widths[widths.length - 1]}.webp`,
                        srcSet: widths.map((w) => `${stem}-${w}.webp ${w}w`).join(", "),
                        sizes: premium?.instructorPhotoSizes ?? baseHeadshot?.sizes,
                      }
                    : baseHeadshot;
                  const photoSrc = aboutHeadshot?.photo ?? inst.headshotUrl;
                  /* PERF: the masters are 1600x2000 and this slot is 353px wide,
                     so without a srcset every visitor downloaded ~400KB per face
                     to display ~700 device px of it — 1.2MB across the three.
                     Only entries flagged `variants` get one; esti and nechama
                     ship a single file and a srcset naming widths that are not
                     there would 404 three times per face.

                     sizes mirrors the real grid: one card per row under 768,
                     two up to 1023, three above. Measured at 1440 that resolves
                     to 360px, so a 2x screen asks for 720 and gets the 800 —
                     165KB rather than 387KB, with nothing visible lost. */
                  const layers = premium?.instructorLayers?.[inst.name];
                  return (
                  <div key={inst.name}>
                    {layers ? (
                      <div className="ds-inst-layered">
                        <img className="ds-inst-cutout" src={layers.cutout} srcSet={layers.cutoutSrcSet} sizes="(max-width: 767px) 70vw, 360px" alt={inst.name} width={800} height={1060} loading="lazy" decoding="async" />
                      </div>
                    ) : photoSrc && (
                      <img
                        src={photoSrc}
                        srcSet={aboutHeadshot?.srcSet}
                        sizes={aboutHeadshot?.sizes}
                        alt={inst.name}
                        /* The v2 portraits are 4:5. The old 400x400 told the
                           browser to hold a square and then handed it a taller
                           picture, which pushes the bio down as each one lands. */
                        width={aboutHeadshot?.srcSet ? 800 : 400}
                        height={aboutHeadshot?.srcSet ? 1000 : 400}
                        className={aboutHeadshot?.cutout ? "w-full" : "w-full vignette-organic"}
                        style={{ mixBlendMode: aboutHeadshot?.cutout ? "normal" : "darken" }}
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                    <div className="mt-4 px-1">
                      <h3 className="text-charcoal mb-1" style={{ fontFamily: "var(--font-hand)", fontSize: 30, lineHeight: 1.15 }}>{inst.name}</h3>
                      <p className="type-label text-terra mb-3">{inst.title}</p>
                      <p className="type-body-sm text-charcoal/75">{inst.bio}</p>
                      {inst.funFact && (
                        <div className="mt-4">
                          <p className="type-label uppercase text-terra mb-1">Fun fact</p>
                          <p className="type-body-sm text-charcoal/75">{inst.funFact}</p>
                        </div>
                      )}
                      {inst.linkedinUrl && premium?.instructorLinkedin !== false && (
                        <a href={inst.linkedinUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 type-nav text-indigo hover:text-indigo/80 transition-colors mt-3">
                          <LinkedInIcon className="w-3.5 h-3.5" /> LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
              )}
            </LabelledSection>
          )}

          {/* ─── ALL ATTENDEES RECEIVE (cowork only) ─── */}
          {/* ─── ALL ATTENDEES RECEIVE — ambient charcoal video band + value
              card. Variant locked to card-light (Isaac, 2026-07-02); placed
              ABOVE the YouTube comparison (Isaac, 2026-07-03). */}
          {premium?.showAttendeesReceive && (
            <AttendeesReceive
              variant={premium.attendeesVariant ?? (dsCowork ? "card-two-col" : "card-light")}
              art={premium.attendeesArt}
              heading={premium.attendeesHeading}
              items={premium.attendeesItems}
              cta={premium.attendeesCta}
              blendImage={event.seriesSlug === "claude-for-design-workshop"}
              drawn={dsCowork}
            />
          )}

          {/* OnPrompt lands here on the Code page — see the note at its other slot. */}
          {dsCowork && premium?.onPromptSection}

          {/* ─── HONEST COMPARISON ─── */}
          {!dsCowork && !!event.seriesSlug && SHOW_COMPARISON_FOR_SERIES.has(event.seriesSlug) && <SectionDivider />}
          {/* With the table off and nothing after it, there is no section at all
              (Code and Cowork, 2026-10-08), rather than an empty coloured band. */}
          {!!event.seriesSlug && SHOW_COMPARISON_FOR_SERIES.has(event.seriesSlug) && (premium?.comparisonTable !== false || !!premium?.afterComparison) && (
            <LabelledSection id="comparison" labelledBy="comparison-heading">
              {premium?.comparisonTable !== false && premium?.comparisonEyebrow !== null && (
                <div className="mb-3">
                  <span className="type-eyebrow" style={{ color: "var(--terra)" }}>
                    {premium?.comparisonEyebrow ?? "▸ Honest comparison"}
                  </span>
                </div>
              )}
              {premium?.comparisonTable !== false && (
                <>
                  <SectionHeading id="comparison-heading" title="Sure, YouTube is free. But how far has that gotten you?" />
                  <ComparisonTable
                    drawn={dsCowork}
                    rows={(event.seriesSlug && COMPARISON_ROWS_BY_SERIES[event.seriesSlug]) || COMPARISON_ROWS}
                  />
                </>
              )}
              {premium?.afterComparison}
            </LabelledSection>
          )}

          </>
          )}

          {/* ─── TICKETS (DARK) ─── */}
          {mainInstructor && (
            <WorkshopTicketBlock
              event={event}
              selectedSessionId={selectedSessionId}
              onSessionSelect={setSelectedSessionId}
              prefillEmail={prefillEmail}
              utmParams={checkoutUtm}
              sessionClocks={sessionClocks}
              initialCouponCode={couponCode || undefined}
              hideBarPrice={(forcedPriceArm ?? randomPriceArm) === "A"}
            />
          )}

          {/* ─── FAQ ─── */}
          <LabelledSection id="faq" labelledBy="faq-heading">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-10 lg:gap-20 items-start">
              <div className="lg:sticky lg:top-28">
                {faqEyebrow && (
                  <span className="type-eyebrow block mb-6" style={{ color: "var(--terra)" }}>{faqEyebrow}</span>
                )}
                <SectionHeading id="faq-heading" title="Got questions?" />
                <p className="type-body text-charcoal/70 leading-relaxed">
                  Still have a question? Email{" "}
                  <a href="mailto:clara@adventureppc.com" className="text-terra underline decoration-1 underline-offset-4 hover:decoration-2 transition-all">
                    clara@adventureppc.com
                  </a>
                  {/* AI Video (Dave, 2026-10-27): "we", not "a real person". */}
                  {event.seriesSlug === "ai-video-workshop-for-beginners" ? " and we will get back to you within an hour." : " and a real person will get back to you within an hour."}
                </p>
              </div>

              <div className="bg-cream" style={{ border: "var(--border-structural)" }}>
                {(content?.faqItems ?? []).map((faq, i) => (
                  <div key={i} style={i > 0 ? { borderTop: "var(--border-structural)" } : undefined}>
                    {/* The question is a <span>, not an <h3>, on purpose: four
                        rules in cowork-ds.css select the toggle as
                        `#faq > div > div:last-child > div > button`, so an
                        <h3> wrapper (the ARIA accordion pattern) would drop the
                        padding on all four premium workshop pages. The
                        expanded/collapsed state and the question-to-answer
                        association are carried by aria-expanded / aria-controls
                        instead, which cost no CSS. See the handoff note in the
                        perf pass if the heading upgrade is wanted later. */}
                    <button
                      onClick={() => toggleFaq(i)}
                      className={`w-full flex items-center justify-between gap-6 text-left p-8 md:p-10 transition-colors cursor-pointer ${openFaq === i ? "bg-charcoal/[0.025]" : "hover:bg-charcoal/[0.015]"}`}
                      data-testid={`button-faq-${i}`}
                      id={`faq-question-${i}`}
                      aria-expanded={openFaq === i}
                      aria-controls={`faq-answer-${i}`}
                    >
                      <span className="type-h4 text-charcoal ds-faq-q">{faq.question}</span>
                      <span
                        className={`ds-faq-toggle w-[30px] h-[30px] shrink-0 flex items-center justify-center border-[1.5px] transition-colors text-[20px] leading-none font-light ${openFaq === i ? "bg-charcoal text-cream border-charcoal" : "border-charcoal text-charcoal"}`}
                        aria-hidden="true"
                      >
                        {openFaq === i ? "–" : "+"}
                      </span>
                    </button>
                    {/* Answers stay mounted (SSR + crawlers read them) and
                        collapse via CSS — same pattern as faq-section.tsx. */}
                    <div
                      ref={faqPanelRef(i)}
                      id={`faq-answer-${i}`}
                      role="region"
                      aria-labelledby={`faq-question-${i}`}
                      style={{
                        maxHeight: 0,
                        overflow: "hidden",
                        transition: "max-height 500ms cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      <div className="px-8 md:px-10 pt-4 md:pt-5 pb-10 md:pb-12 max-w-[720px]">
                        <p className="type-body-lg text-charcoal/70 leading-relaxed" style={{ fontSize: "18px", whiteSpace: "pre-line" }}>
                          {faq.answerLink && faq.answer.includes(faq.answerLink.text) ? (() => {
                            const [before, after] = faq.answer.split(faq.answerLink.text);
                            return (
                              <>
                                {before}
                                <Link href={faq.answerLink.href} className="text-terra underline underline-offset-2 hover:opacity-70 transition-opacity">{faq.answerLink.text}</Link>
                                {after}
                              </>
                            );
                          })() : faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </LabelledSection>

          {/* The "For companies" team-training bridge band that lived here was
              removed 2026-07-02 (Isaac: distracting from the ticket-purchase
              path). The private-cohort offer still lives at
              /ai-training-for-teams via the nav + footer. */}

        </div>
      </div>

      {/* ─── FINAL CTA ─── */}
      {/* Sales: the ClaudeBot chat sits here, after the FAQ and before the
          closing CTA. Other series leave the slot empty. */}
      {premium?.beforeFinalCta}
      {premium?.finalCtaOverride ? premium.finalCtaOverride : (
      <section
        id="final-cta"
        aria-labelledby="final-cta-heading"
        className="relative overflow-hidden pt-[clamp(120px,13.2vw,190px)] pb-24 sm:pb-[clamp(160px,12.5vw,180px)]"
        style={{ backgroundColor: "var(--indigo)" }}
      >
        {premium?.finalCtaArt && (
          <FinalCtaArt imageBase={premium.finalCtaArt} credit={premium.finalCtaCredit} />
        )}
        {/* ClosingWall (the gold-framed paintings) is built and styled but NOT
            mounted — pulled 2026-08-26 so the closing section is just its own
            ground again. Re-hang it by restoring this line:
              {premium?.pageClass.includes("ds-cowork") && <ClosingWall />}   */}
        <div className="absolute inset-0 bg-dot-matrix-light pointer-events-none opacity-40" aria-hidden="true" />
        <div className="relative container-content text-center">
          <h2
            id="final-cta-heading"
            className="text-cream mx-auto mb-8 max-w-[1000px] serif-section"
            style={{ fontWeight: 500, fontSize: "clamp(44px, 6.5vw, 88px)", lineHeight: 1.2, letterSpacing: "-0.03em", textWrap: "balance" as "balance" }}
          >
            {(event.seriesSlug && FINAL_CTA_BY_SERIES[event.seriesSlug]?.headline) || DEFAULT_FINAL_CTA.headline}
          </h2>
          {/* The Code page's closing line is the headline alone — see
              FINAL_CTA_BY_SERIES, where its subtitle is intentionally empty. */}
          {(event.seriesSlug ? FINAL_CTA_BY_SERIES[event.seriesSlug]?.subtitle ?? DEFAULT_FINAL_CTA.subtitle : DEFAULT_FINAL_CTA.subtitle) && (
            <p className="type-body-lg text-cream/85 mx-auto mb-12 max-w-[620px] leading-relaxed">
              {(event.seriesSlug && FINAL_CTA_BY_SERIES[event.seriesSlug]?.subtitle) || DEFAULT_FINAL_CTA.subtitle}
            </p>
          )}
          <a
            href="#tickets"
            className="btn btn-lg"
            style={{ backgroundColor: "var(--terra)", color: "var(--cream)", transition: "background-color 200ms" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#A83200")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--terra)")}
            onClick={(e) => {
              // OPENS THE CHECKOUT (sheet on mobile, drawer on desktop); anchor-scroll runs only when nothing is bookable.
              if (requestCheckout()) e.preventDefault();
            }}
          >
            {event.seriesSlug === "claude-for-beginners-workshop" ? "Reserve your seat" : "Reserve my seat"}
          </a>
        </div>
      </section>
      )}

      </main>
    </div>
  );
}

/**
 * Top-level dispatcher for /events/:slug.
 *
 * Fetches the event ONCE and decides which page component to mount based on
 * the series. This must happen at a parent component level — not inside
 * EventDetailPage's render body — because each page component owns a
 * different hook tree (different useState/useEffect counts). If we made the
 * decision *after* some EventDetailPage hooks had already run, the early
 * return would skip subsequent hooks and React would tear the subtree down
 * to a blank screen on the render where data first arrives. Hence the
 * wrapper.
 *
 * React Query dedupes the fetch with the child component's identical
 * queryKey + staleTime, so this is one HTTP request despite mounting two
 * components in sequence.
 */
/**
 * Renders the code-split Claude Design Workshop page.
 *
 * On the server, routes-ssr.tsx registers the component eagerly so this
 * renders the full page synchronously inside renderToString. On direct
 * client hits, entry-client.tsx preloads the chunk BEFORE hydrateRoot, so
 * the first client render matches the server tree (no mismatch, no flash).
 * Only on pure client-side navigation does the chunk load on demand — the
 * skeleton below mirrors the isLoading state users just saw, so the
 * transition is seamless. See lib/design-workshop-detail-loader.ts.
 */

function ClaudeKidsDetailGate() {
  const [, setLoadedAt] = useState(0);
  const Comp = getKidsDetail();

  useEffect(() => {
    if (!getKidsDetail()) {
      let cancelled = false;
      preloadKidsDetail().then(() => {
        if (!cancelled) setLoadedAt(Date.now());
      });
      return () => { cancelled = true; };
    }
  }, []);

  if (!Comp) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="pt-32 container-content">
          <div className="space-y-6 animate-pulse max-w-[700px]">
            <div className="h-8 bg-charcoal/5 rounded w-1/4" />
            <div className="h-16 bg-charcoal/5 rounded w-3/4" />
            <div className="h-4 bg-charcoal/5 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return <Comp />;
}

function EventDetailRouter() {
  // Match both /workshops/:slug (canonical) and /events/:slug (legacy alias).
  const [, paramsWorkshops] = useRoute("/workshops/:slug");
  const [, paramsEvents] = useRoute("/events/:slug");
  const slug = paramsWorkshops?.slug || paramsEvents?.slug || "";

  const { data: event, isLoading, error } = useQuery<EventWithSeries>({
    queryKey: ["/api/events", slug],
    queryFn: async () => {
      const res = await fetch(`/api/events/${slug}`);
      if (!res.ok) {
        const stub = previewStubEvent(slug);
        if (stub) return stub;
        throw new Error("Event not found");
      }
      return res.json();
    },
    enabled: !!slug,
    staleTime: 30000,
  });

  // The premium stylesheet (cowork-ds.css) is <link>ed by the server on a direct
  // hit; a client-side navigation lands here without it. No-op when the link is
  // already in <head> or the series doesn't use it — 2026-09-09.
  ensurePremiumCssForSlug(slug, event?.seriesSlug);

  // Reminder popup: mounts on every workshop detail page that has a bookable
  // upcoming session. The session list (all upcoming sessions for the series,
  // or the event itself if it's a one-off) drives the shared date-resolution
  // logic; the popup is suppressed when nothing is bookable.
  const popupSessions = useMemo<ReminderPopupSession[]>(() => {
    if (!event) return [];
    const list = (event.seriesSessions && event.seriesSessions.length > 0)
      ? event.seriesSessions
      : [{
          id: event.id,
          startDatetime: event.startDatetime,
          status: event.status,
          seatsSold: event.seatsSold,
          maxCapacity: event.maxCapacity,
          timezone: event.timezone,
        }];
    return list.map((s) => ({
      id: s.id,
      startDatetime: s.startDatetime as unknown as string,
      status: s.status,
      seatsSold: s.seatsSold ?? null,
      maxCapacity: s.maxCapacity ?? null,
      timezone: s.timezone || "America/New_York",
    }));
  }, [event]);

  const hasReminderTarget = useMemo(
    () => !!resolveReminderTarget(popupSessions, new Date()).reminderTarget,
    [popupSessions],
  );

  // Suppress the popup permanently on this device once the visitor submits
  // their email. Non-submitters are also frequency-capped: useExitIntent
  // writes its 1-day last-shown timestamp when the popup fires, so a
  // returning visitor isn't re-modaled on every pageview.
  const submittedKey = event
    ? `reminderPopup:submitted:${event.seriesSlug ?? event.slug}`
    : null;

  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  useEffect(() => {
    if (!submittedKey) return;
    try {
      if (window.localStorage.getItem(submittedKey)) setAlreadySubmitted(true);
    } catch { /* localStorage unavailable */ }
  }, [submittedKey]);

  const handleSubmitSuccess = useCallback(() => {
    if (submittedKey) {
      try {
        window.localStorage.setItem(submittedKey, "1");
        // Sibling flag read by the global promo popup for cross-suppression:
        // any workshop opt-in (any series) silences the site-wide popup.
        window.localStorage.setItem("reminderPopup:submittedAny", "1");
      } catch { /* ignore */ }
    }
    setAlreadySubmitted(true);
  }, [submittedKey]);

  // QA override: `?popup=force` opens the popup immediately and ignores the
  // per-series "already submitted" suppression, so it can be retested on any
  // machine without clearing localStorage.
  const forcePopup = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("popup") === "force";
  }, []);

  const exitIntent = useExitIntent({
    minTimeMs: 25_000,
    minScrollFraction: 0.65,
    disabled: (alreadySubmitted && !forcePopup) || !hasReminderTarget,
    force: forcePopup,
  });

  if (isLoading || (!event && !error)) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="pt-32 container-content">
          <div className="space-y-6 animate-pulse max-w-[700px]">
            <div className="h-8 bg-charcoal/5 rounded w-1/4" />
            <div className="h-16 bg-charcoal/5 rounded w-3/4" />
            <div className="h-4 bg-charcoal/5 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  // Claude for Design now renders via the premium EventDetailPage treatment
  // (PREMIUM_LAYOUT_BY_SERIES["claude-for-design-workshop"]) — the standalone
  // ClaudeDesignWorkshopDetailGate is retired. Kids keeps its bespoke gate.
  const Page = event?.seriesSlug === "claude-code-for-kids"
    ? <ClaudeKidsDetailGate />
    : <EventDetailPage />;

  return (
    <>
      {Page}
      {/* Kids camp: the generic popup is Copernicus-serif B2B furniture on a
          page with its own design system, and its discount framing conflicts
          with the sibling ladder. Suppressed until a kids variant exists. */}
      {event && hasReminderTarget && event.seriesSlug !== "claude-code-for-kids" && (
        <ReminderPopup
          open={exitIntent.shouldShow}
          onClose={exitIntent.dismiss}
          seriesSlug={event.seriesSlug ?? null}
          seriesTitle={event.title}
          sessions={popupSessions}
          pageSlug={`/workshops/${event.seriesSlug ?? event.slug}`}
          source="exit_popup"
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}
    </>
  );
}

export default EventDetailRouter;
